/* eslint-disable */

import type {
    DATA_FRESHNESS_CHECK_TYPE,
    MODEL_QUALITY_JOB_CHECK_TYPE,
    PRE_MATERIALIZATION_QUALITY_CHECK_TYPE
} from 'app/store/__generated__/types';

export type ModelId = number;
export type TagId = number;

export interface AccessControlDto {
    assetId: EntityId;
    collaborators: Array<string>;
    owner: string;
    visibleToEveryone: boolean;
}

export const ACCESS_CONTROL_DTOS = 'AccessControlDtos';

export interface AccessControlDtos {
    _kind: typeof ACCESS_CONTROL_DTOS;
    accessControlDtos: Array<AccessControlDto>;
}

export const ACCESSIBLE = 'Accessible';

export interface Accessible {
    _kind: typeof ACCESSIBLE;
    accessible: boolean;
    createdAt: Instant;
    database: string;
    failures: Array<object>;
    modifiedAt: Instant;
    lastDDL: Instant;
    rowCount?: number;
    sampleSize?: number;
    schema: string;
    table: string;
    tableType?: TableType;
}

export interface Aggregation {
    columnsToGroup: Array<string>;
    measurements: Array<Measurement>;
}

export interface AggregationError {
    cause: string;
    message: string;
}

export const ALREADY_DEPLOYED_FROM_ANOTHER_TRANSFORMATION =
    'AlreadyDeployedFromAnotherTransformation';

export interface AlreadyDeployedFromAnotherTransformation {
    _kind: typeof ALREADY_DEPLOYED_FROM_ANOTHER_TRANSFORMATION;
    sourceViewName?: string;
    sourceViewSpaceName?: string;
}

export const ALREADY_USED_AS_PROJECT_INPUT = 'AlreadyUsedAsProjectInput';

export interface AlreadyUsedAsProjectInput {
    _kind: typeof ALREADY_USED_AS_PROJECT_INPUT;
    spaceNames: Array<string>;
}

export const AMBIGUOUS_COLUMN_REFERENCE = 'AmbiguousColumnReference';

export interface AmbiguousColumnReference {
    _kind: typeof AMBIGUOUS_COLUMN_REFERENCE;
    column: string;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export interface AmbiguousOutputColumns {
    columnName: string;
    message: string;
}

export const ANALYZE_SCHEMA_USAGE_INITIATED = 'AnalyzeSchemaUsageInitiated';

export interface AnalyzeSchemaUsageInitiated {
    _kind: typeof ANALYZE_SCHEMA_USAGE_INITIATED;
    explorations: Array<CannedExploration>;
    fromTableFunction: boolean;
    projectToImportTo?: string;
    resultTable: SnowflakeTableFqn;
    schemaToAnalyze: SnowflakeSchemaFqn;
}

export const ANOTHER_INSTANCE_IS_RESPONSIBLE = 'AnotherInstanceIsResponsible';

export interface AnotherInstanceIsResponsible {
    _kind: typeof ANOTHER_INSTANCE_IS_RESPONSIBLE;
    instance: string;
    target: SnowflakeTableFqn;
    targetFqn: string;
}

export interface AnyAggregation {
    sourceColumnName?: string;
}

export const API_KEY_CREATED = 'ApiKeyCreated';

export interface ApiKeyCreated {
    _kind: typeof API_KEY_CREATED;
    id: string;
    key: string;
}

export const API_KEY_ENTRIES = 'ApiKeyEntries';

export interface ApiKeyEntries {
    _kind: typeof API_KEY_ENTRIES;
    entries: Array<ApiKeyEntry>;
}

export interface ApiKeyEntry {
    createdAt: Instant;
    createdBy: string;
    description: string;
    expiresAt: Instant;
    id: string;
    name: string;
    revokedAt?: Instant;
}

export interface ApiKeyJobRunTrigger {
    triggerType: JobRunTriggerType.API_KEY;
}

export const API_KEY_OPERATION_NOT_PERMITTED = 'ApiKeyOperationNotPermitted';

export interface ApiKeyOperationNotPermitted {
    _kind: typeof API_KEY_OPERATION_NOT_PERMITTED;
}

export interface ApiTriggerRequest {
    callbackUrl?: string;
    triggerType: ExecutionTriggerType.API;
}

export interface ApiTriggerResponse {
    callbackUrl?: string;
    submitUrl: string;
    triggerType: ExecutionTriggerType.API;
}

export enum ApplicationState {
    HEALTHY = 'HEALTHY',
    STARTING = 'STARTING',
    STOPPED = 'STOPPED',
    UNHEALTHY = 'UNHEALTHY'
}

export interface ApproxCountDistinctAggregation {
    sourceColumnName?: string;
}

export const ARBITRARY_SQL_RESULT = 'ArbitrarySqlResult';

export interface ArbitrarySqlResult {
    _kind: typeof ARBITRARY_SQL_RESULT;
    columns: Array<Column>;
    rowCount: number;
    valuesByColumn: Array<Array<any>>;
}

export const ASSET_DELETION_NOT_PERMITTED = 'AssetDeletionNotPermitted';

export interface AssetDeletionNotPermitted {
    _kind: typeof ASSET_DELETION_NOT_PERMITTED;
    assetId: EntityId;
}

export const ASSET_MODIFICATION_NOT_PERMITTED = 'AssetModificationNotPermitted';

export interface AssetModificationNotPermitted {
    _kind: typeof ASSET_MODIFICATION_NOT_PERMITTED;
    assetId: EntityId;
}

export const ASSET_NOT_DELETABLE = 'AssetNotDeletable';

export interface AssetNotDeletable {
    _kind: typeof ASSET_NOT_DELETABLE;
    assetId: EntityId;
    concerns: Array<EntityModificationConcern>;
    warnings: Array<EntityModificationConcern>;
}

export type AssetPropertyDefinitionPayload =
    | BooleanDefinitionPayload
    | LongTextDefinitionPayload
    | SelectionDefinitionPayload
    | ShortTextDefinitionPayload;

export interface AssetPropertyDto {
    appearance?: SelectOptionAppearance;
    name: string;
    prominent: boolean;
    type: AssetPropertyType;
    uid: string;
    value: string;
    valueIsDefault: boolean;
}

export type AssetPropertyPayload =
    BooleanPayload | LongTextPayload | SelectionPayload | ShortTextPayload;

export enum AssetPropertyTarget {
    COLUMN = 'COLUMN',
    SOURCE_LOCATION = 'SOURCE_LOCATION',
    DATASET = 'DATASET',
    SPACE = 'SPACE',
    SYSTEM = 'SYSTEM'
}

export enum AssetPropertyType {
    BOOLEAN = 'BOOLEAN',
    LONG_TEXT = 'LONG_TEXT',
    SELECT = 'SELECT',
    SHORT_TEXT = 'SHORT_TEXT'
}

export interface AssetPropertyValueDto {
    assetId: EntityId;
    definitionUid: string;
    entityId: EntityId;
    isDefault: boolean;
    payload: AssetPropertyPayload;
}

export const AUDIT_JOB_RUN = 'AuditJobRun';

export interface AuditJobRun {
    _kind: typeof AUDIT_JOB_RUN;
    environmentUid: string;
    jobRunUid: string;
}

export type AuditJobRunSuccess = JobRunAuditResult;

export type AuditJobRunFailure =
    | Canceled
    | Timeout
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type AuditJobRunMessage = AuditJobRunSuccess | AuditJobRunFailure;

export interface AuditResult {
    executionMetadata: object;
    modifiedTables: Array<ModifiedTableEntity>;
    queries: Array<AuditedQuery>;
    storedProceduresCalled: Array<StoredProcedureEntity>;
    usedTables: Array<UsedTableEntity>;
}

export type AuditedEntity = ModifiedTableEntity | StoredProcedureEntity | UsedTableEntity;

export interface AuditedQuery {
    duration: Duration;
    executionStatus: string;
    parseError?: string;
    queryId: string;
    querySql: string;
    queryType: string;
    sessionId: string;
    startTime: Instant;
    warehouseName: string;
    warehouseSize?: string;
    warehouseType: string;
}

export const AUTHENTICATE = 'Authenticate';

export interface Authenticate {
    _kind: typeof AUTHENTICATE;
    jwt: string;
}

export type AuthenticateSuccess = Authenticated;
export type AuthenticateFailure = TokenExpired | NotAuthenticated | UnexpectedFailure;
export type AuthenticateMessage = AuthenticateSuccess | AuthenticateFailure;

export const AUTHENTICATED = 'Authenticated';

export interface Authenticated {
    _kind: typeof AUTHENTICATED;
    user: UserDto;
    username: string;
}

export interface AvgAggregation {
    sourceColumnName?: string;
}

export interface JobDefinitionEntry {
    checks: Array<JobCheck>;
    createdAt: Instant;
    createdBy: string;
    description?: string;
    environmentRef: JobEnvironmentRef;
    lastModifiedAt: Instant;
    lastModifiedBy: string;
    modelRef?: ModelRef;
    name: string;
    operation: Operation;
    projectRef?: ProjectRef;
    scheduledTrigger?: JobTrigger;
    uid: string;
}

export interface BasicColumnsDiff {
    added: Array<string>;
    removed: Array<string>;
    typeChanged: Record<string, TypeChange>;
}

export interface Blend {
    additionalSourceViews: Array<string>;
    blendType: BlendType;
    blendView: string;
    criteria: Array<BlendCriteria>;
    dedupeJoinKeys: boolean;
}

export interface BlendCriteria {
    leftColumn: string;
    rightColumn: string;
}

export enum BlendType {
    INNER = 'INNER',
    LEFT_OUTER = 'LEFT_OUTER',
    RIGHT_OUTER = 'RIGHT_OUTER',
    FULL_OUTER = 'FULL_OUTER'
}

export interface BooleanDefinitionPayload {
    defaultValue?: string;
}

export interface BooleanPayload {
    value: boolean;
}

export const CALL_SNOWFLAKE_STORED_PROCEDURE = 'CallSnowflakeStoredProcedure';

export interface CallSnowflakeStoredProcedure {
    _kind: typeof CALL_SNOWFLAKE_STORED_PROCEDURE;
    arguments: Array<SnowflakeStoredProcedureArgument>;
    fqn: SnowflakeStoredProcedureFqn;
    uid: string;
}

export type CallSnowflakeStoredProcedureSuccess = JobRunResult;
export type CallSnowflakeStoredProcedureFailure = UnexpectedFailure;
export type CallSnowflakeStoredProcedureMessage =
    CallSnowflakeStoredProcedureSuccess | CallSnowflakeStoredProcedureFailure;

export const CANCEL_ACCEPTED = 'CancelAccepted';

export interface CancelAccepted {
    _kind: typeof CANCEL_ACCEPTED;
}

export const CANCEL_JOB_RUN = 'CancelJobRun';

export interface CancelJobRun {
    _kind: typeof CANCEL_JOB_RUN;
    jobRunUid: string;
}

export type CancelJobRunSuccess = JobRunCancelRequested;

export type CancelJobRunFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | JobRunNotCancelable
    | JobRunNotFound
    | Canceled
    | UnexpectedFailure;

export type CancelJobRunMessage = CancelJobRunSuccess | CancelJobRunFailure;

export const CANCEL_REQUEST = 'CancelRequest';

export interface CancelRequest {
    _kind: typeof CANCEL_REQUEST;
    requestId: string;
}

export type CancelRequestSuccess = Succeeded;
export type CancelRequestFailure = UnexpectedFailure;
export type CancelRequestMessage = CancelRequestSuccess | CancelRequestFailure;

export const CANCEL_SNOWFLAKE_QUERY = 'CancelSnowflakeQuery';

export interface CancelSnowflakeQuery {
    _kind: typeof CANCEL_SNOWFLAKE_QUERY;
    id: string;
}

export type CancelSnowflakeQuerySuccess = CancelAccepted;
export type CancelSnowflakeQueryFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type CancelSnowflakeQueryMessage = CancelSnowflakeQuerySuccess | CancelSnowflakeQueryFailure;

export const CANCELED = 'Canceled';

export interface Canceled {
    _kind: typeof CANCELED;
}

export interface CannedExploration {
    id: string;
    model: object;
    sql: string;
}

export const CANNOT_CAST_VALUE_OF_TYPE = 'CannotCastValueOfType';

export interface CannotCastValueOfType {
    _kind: typeof CANNOT_CAST_VALUE_OF_TYPE;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sourceColumnType: string;
    sql: string;
    sqlState?: string;
    targetColumnType: string;
    vendorCode?: number;
}

export const CHAT_GPT_ERROR = 'ChatGptError';

export interface ChatGptError {
    _kind: typeof CHAT_GPT_ERROR;
    code?: string;
    message?: string;
}

export const CHAT_GPT_INFO = 'ChatGptInfo';

export interface ChatGptInfo {
    _kind: typeof CHAT_GPT_INFO;
    maskedApiKey: string;
    maskedOrganizationKey?: string;
    model: string;
}

export const CHAT_GPT_NOT_CONFIGURED = 'ChatGptNotConfigured';

export interface ChatGptNotConfigured {
    _kind: typeof CHAT_GPT_NOT_CONFIGURED;
}

export const CHAT_GPT_RESPONSE = 'ChatGptResponse';

export interface ChatGptResponse {
    _kind: typeof CHAT_GPT_RESPONSE;
    responseJson: object;
}

export const CIRCULAR_REFERENCE = 'CircularReference';

export interface CircularReference {
    _kind: typeof CIRCULAR_REFERENCE;
}

export const CLIENT_CONNECTION_ERROR = 'ClientConnectionError';

export interface ClientConnectionError {
    _kind: typeof CLIENT_CONNECTION_ERROR;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const COLLECT_DBT_MODELS = 'CollectDbtModels';

export interface CollectDbtModels {
    _kind: typeof COLLECT_DBT_MODELS;
    githubOwner: string;
    githubRef?: string;
    githubRepositoryName: string;
    targetDatabase: string;
    targetSchema: string;
}

export type CollectDbtModelsSuccess = JobRunResult;
export type CollectDbtModelsFailure = SecretNotFound | Canceled | Timeout | UnexpectedFailure;
export type CollectDbtModelsMessage = CollectDbtModelsSuccess | CollectDbtModelsFailure;

export const COLLECT_ROI_TABLE_METRICS = 'CollectRoiTableMetrics';

export interface CollectRoiTableMetrics {
    _kind: typeof COLLECT_ROI_TABLE_METRICS;
    source?: string;
    targetSchema: SnowflakeSchemaFqn;
}

export type CollectRoiTableMetricsSuccess = JobRunResult;
export type CollectRoiTableMetricsFailure = UnexpectedFailure;
export type CollectRoiTableMetricsMessage =
    CollectRoiTableMetricsSuccess | CollectRoiTableMetricsFailure;

export const COLLECT_SNOWFLAKE_EVENTS = 'CollectSnowflakeEvents';

export interface CollectSnowflakeEvents {
    _kind: typeof COLLECT_SNOWFLAKE_EVENTS;
    eventTable: SnowflakeTableFqn;
    queryHistory: SnowflakeTableFqn;
}

export type CollectSnowflakeEventsSuccess = Succeeded;
export type CollectSnowflakeEventsFailure =
    | AnotherInstanceIsResponsible
    | Canceled
    | Timeout
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type CollectSnowflakeEventsMessage =
    CollectSnowflakeEventsSuccess | CollectSnowflakeEventsFailure;

export const COLLECT_WAREHOUSE_USAGE = 'CollectWarehouseUsage';

export interface CollectWarehouseUsage {
    _kind: typeof COLLECT_WAREHOUSE_USAGE;
    queryHistorySource: string;
    targetSchema: SnowflakeSchemaFqn;
    warehouseMeteringHistorySource: string;
}

export type CollectWarehouseUsageSuccess = JobRunResult;
export type CollectWarehouseUsageFailure = Canceled | UnexpectedFailure;
export type CollectWarehouseUsageMessage =
    CollectWarehouseUsageSuccess | CollectWarehouseUsageFailure;

export interface Column {
    name: string;
    typeFamily: TypeFamily;
}

export interface ColumnAlias {
    alias: string;
    column: string;
}

export interface ColumnDescriptorDto {
    columnUuid: ColumnUuid;
    description?: string;
    name: string;
    typeFamily: TypeFamily;
}

export type ColumnInput = object;

export const COLUMN_METADATA = 'ColumnMetadata';

export interface ColumnMetadata {
    _kind: typeof COLUMN_METADATA;
    columnProperties: Array<ColumnProperty>;
    descriptionByColumnName: Record<string, string>;
}

export interface ColumnProperty {
    columnName: string;
    columnUuid: ColumnUuid;
    definitionUid: string;
    isDefault: boolean;
    type: AssetPropertyType;
    value: string;
}

export interface ColumnReference {
    column: string;
    view?: string;
}

export type ColumnUuid = SnowflakeColumnFqn | SpaceViewColumn | TransformationColumn;

export interface Columns {
    columns: Array<Column>;
}

export interface Combine {
    inputDatasets: Array<InputDataset>;
    outputColumns: Array<SqlColumn>;
    setOperator: string;
}

export const COMPILATION_FAILED_WITH_DATASET_ERROR = 'CompilationFailedWithDatasetError';

export interface CompilationFailedWithDatasetError {
    _kind: typeof COMPILATION_FAILED_WITH_DATASET_ERROR;
    error: DatasetError;
}

export const COMPILATION_FAILED_WITH_TRANSFORMATION_ERRORS =
    'CompilationFailedWithTransformationErrors';

export interface CompilationFailedWithTransformationErrors {
    _kind: typeof COMPILATION_FAILED_WITH_TRANSFORMATION_ERRORS;
    errors: Array<TransformationError>;
}

export const COMPLETE_JOB_RUN = 'CompleteJobRun';

export interface CompleteJobRun {
    _kind: typeof COMPLETE_JOB_RUN;
    completedAt: Instant;
    message: Message;
    operationId: string;
    runUid: string;
}

export type CompleteJobRunSuccess = JobRunCompleted;
export type CompleteJobRunFailure = UnexpectedFailure;
export type CompleteJobRunMessage = CompleteJobRunSuccess | CompleteJobRunFailure;

export const CONFIG_PROPERTIES = 'ConfigProperties';

export interface ConfigProperties {
    _kind: typeof CONFIG_PROPERTIES;
    properties: Record<string, string>;
}

export const CONNECTION_DETAILS = 'ConnectionDetails';

export interface ConnectionDetails {
    _kind: typeof CONNECTION_DETAILS;
    account: string;
    database: string;
    jdbcUrl: string;
    schema: string;
    table: string;
}

export const CONNECTION_NOT_CONFIGURED = 'ConnectionNotConfigured';

export interface ConnectionNotConfigured {
    _kind: typeof CONNECTION_NOT_CONFIGURED;
}

export interface Constant extends ColumnInput {
    value?: string;
}

export const COPY_SPACE = 'CopySpace';

export interface CopySpace {
    _kind: typeof COPY_SPACE;
    from: string;
    name: string;
}

export type CopySpaceSuccess = SpaceCopied;

export type CopySpaceFailure =
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | DerivedAssetMustNotBeModified
    | UnexpectedFailure;

export type CopySpaceMessage = CopySpaceSuccess | CopySpaceFailure;

export interface CountAggregation {}

export interface CountDistinctAggregation {
    sourceColumnName?: string;
}

export const CREATE_API_KEY = 'CreateApiKey';

export interface CreateApiKey {
    _kind: typeof CREATE_API_KEY;
    description: string;
    name: string;
    permittedOperations: Array<string>;
}

export type CreateApiKeySuccess = ApiKeyCreated;
export type CreateApiKeyFailure = ApiKeyOperationNotPermitted | UnexpectedFailure;
export type CreateApiKeyMessage = CreateApiKeySuccess | CreateApiKeyFailure;

export const CREATE_DAP_MIGRATION = 'CreateDapMigration';

export interface CreateDapMigration {
    _kind: typeof CREATE_DAP_MIGRATION;
    description?: string;
    feMetadata?: object;
    name: string;
}

export type CreateDapMigrationSuccess = DapMigrationCreated;
export type CreateDapMigrationFailure = UnexpectedFailure;
export type CreateDapMigrationMessage = CreateDapMigrationSuccess | CreateDapMigrationFailure;

export const CREATE_DATAMEER_CHANNEL = 'CreateDatameerChannel';

export interface CreateDatameerChannel {
    _kind: typeof CREATE_DATAMEER_CHANNEL;
    name: string;
    payload: DatameerChannelPayload;
}

export type CreateDatameerChannelSuccess = DatameerChannelCreated;
export type CreateDatameerChannelFailure = UnexpectedFailure;
export type CreateDatameerChannelMessage =
    CreateDatameerChannelSuccess | CreateDatameerChannelFailure;

export const CREATE_EVENT_SUBSCRIPTION = 'CreateEventSubscription';

export interface CreateEventSubscription {
    _kind: typeof CREATE_EVENT_SUBSCRIPTION;
    deliveryTarget: EventDeliveryTarget;
    eventFilters: Record<string, string>;
    eventId: string;
    name: string;
}

export type CreateEventSubscriptionSuccess = EventSubscriptionCreated;

export type CreateEventSubscriptionFailure =
    EventIdNotFound | NameAlreadyExists | NameCannotBeBlank | UnexpectedFailure;

export type CreateEventSubscriptionMessage =
    CreateEventSubscriptionSuccess | CreateEventSubscriptionFailure;

export const CREATE_EXPLORATION = 'CreateExploration';

export interface CreateExploration {
    _kind: typeof CREATE_EXPLORATION;
    id: string;
    model: object;
    sql: string;
    tableFqn?: SnowflakeTableFqn;
    transformationId?: string;
}

export type CreateExplorationSuccess = ExplorationCreated;
export type CreateExplorationFailure = UnexpectedFailure;
export type CreateExplorationMessage = CreateExplorationSuccess | CreateExplorationFailure;

export const CREATE_GROUP = 'CreateGroup';

export interface CreateGroup {
    _kind: typeof CREATE_GROUP;
    children: Array<string>;
    spaceUid: string;
}

export type CreateGroupSuccess = GroupCreated;
export type CreateGroupFailure = NodeAlreadyInGroup | UnexpectedFailure;
export type CreateGroupMessage = CreateGroupSuccess | CreateGroupFailure;

export const CREATE_SLACK_CHANNEL = 'CreateSlackChannel';

export interface CreateSlackChannel {
    _kind: typeof CREATE_SLACK_CHANNEL;
    name: string;
}

export type CreateSlackChannelSuccess = SlackChannelCreated;
export type CreateSlackChannelFailure =
    SecretNotFound | InvalidSlackChannelName | SlackApiCallFailed | UnexpectedFailure;
export type CreateSlackChannelMessage = CreateSlackChannelSuccess | CreateSlackChannelFailure;

export const CREATE_SPACE_WITH_VIEW = 'CreateSpaceWithView';

export interface CreateSpaceWithView {
    _kind: typeof CREATE_SPACE_WITH_VIEW;
    addressToImport?: SnowflakeTableFqn;
    envUid?: string;
    name: string;
}

export type CreateSpaceWithViewSuccess = SpaceCreatedWithView;
export type CreateSpaceWithViewFailure =
    | DerivedAssetMustNotBeModified
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | ViewAlreadyExists
    | UnexpectedFailure;
export type CreateSpaceWithViewMessage = CreateSpaceWithViewSuccess | CreateSpaceWithViewFailure;

export const CREATE_TRANSFORMATION_NODE = 'CreateTransformationNode';

export interface CreateTransformationNode {
    _kind: typeof CREATE_TRANSFORMATION_NODE;
    name: string;
    payload: TransformationPayload;
    spaceUid: string;
}

export type CreateTransformationNodeSuccess = TransformationNodeCreated;
export type CreateTransformationNodeFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | DuplicateColumnNames
    | OutputColumnsWithInvisibleControlCharacters
    | OutputColumnsWithLineSeparator
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | CompilationFailedWithTransformationErrors
    | UnexpectedFailure;

export type CreateTransformationNodeMessage =
    CreateTransformationNodeSuccess | CreateTransformationNodeFailure;

export const CREATE_USER = 'CreateUser';

export interface CreateUser {
    _kind: typeof CREATE_USER;
    email: string;
    firstName: string;
    group: string;
    lastName: string;
    otpEnabled: boolean;
    username: string;
}

export type CreateUserSuccess = UserCreated;
export type CreateUserFailure =
    | NotPermitted
    | TooManyEnabledUsers
    | EmailDomainNotAuthorized
    | EmailAddressAlreadyTaken
    | UsernameAlreadyTaken
    | UsernameInvalid
    | UnexpectedFailure;
export type CreateUserMessage = CreateUserSuccess | CreateUserFailure;

export interface CustomTransformationErrors {
    errors: Array<TransformationError>;
    index?: number;
    origins: Array<DatasetUid>;
}

export type DapExportFileImported = object;

export const DAP_MIGRATION_CREATED = 'DapMigrationCreated';

export interface DapMigrationCreated {
    _kind: typeof DAP_MIGRATION_CREATED;
    uid: string;
}

export interface DapMigrationEntry {
    description: string;
    feMetadata: object;
    name: string;
    uid: string;
}

export type DapMigrationFileContents = object;

export const DAP_MIGRATION_UPDATED = 'DapMigrationUpdated';

export interface DapMigrationUpdated {
    _kind: typeof DAP_MIGRATION_UPDATED;
    uid: string;
}

export const DAP_MIGRATIONS = 'DapMigrations';

export interface DapMigrations {
    _kind: typeof DAP_MIGRATIONS;
    migrations: Array<DapMigrationEntry>;
}

export interface DataBrowserTableDetails {
    database: string;
    isCreatedByDatameer: boolean;
    isManagedByDatameer: boolean;
    isManagedByDbt: boolean;
    isScheduled: boolean;
    isUsedByDatameer: boolean;
    schema: string;
    table: string;
}

export interface DataDrivenJobRunTrigger {
    triggerSourceDefinitionRef: JobDefinitionRef;
    triggerSourceRunUid: string;
    triggerType: JobRunTriggerType.DATA_DRIVEN;
}

export interface DataDrivenJobTrigger {
    enabled: boolean;
    triggerSourceDefinitionRefs: Array<JobDefinitionRef>;
    triggerType: JobTriggerType.DATA_DRIVEN;
}

export const DATA_DRIVEN_TRIGGER_CANDIDATES = 'DataDrivenTriggerCandidates';

export interface DataDrivenTriggerCandidates {
    _kind: typeof DATA_DRIVEN_TRIGGER_CANDIDATES;
    entries: Array<TriggerCandidate>;
}

export interface DataDrivenTriggerRequest {
    enabled: boolean;
    triggerSourceDeployedNodeUuid: string;
    triggerType: ExecutionTriggerType.DATA_DRIVEN;
}

export interface DataDrivenTriggerResponse {
    enabled: boolean;
    triggerSourceDeployedNodeUuid: string;
    triggerSourceFqn: SnowflakeTableFqn;
    triggerType: ExecutionTriggerType.DATA_DRIVEN;
}

export interface DataFreshnessCheck {
    _type: typeof DATA_FRESHNESS_CHECK_TYPE;
    maxAcceptableDurationUnit: DurationUnit;
    maxAcceptableDurationValue: number;
    tableFqn: SnowflakeTableFqn;
}

export interface ModelQualityJobCheck {
    _type: typeof MODEL_QUALITY_JOB_CHECK_TYPE;
    tableFqn: SnowflakeTableFqn;
}

export interface PreMaterializationQualityCheck {
    _type: typeof PRE_MATERIALIZATION_QUALITY_CHECK_TYPE;
}

export type DataFreshnessCheckNotMet = object;

export const DATA_LOADING_FAILED = 'DataLoadingFailed';

export interface DataLoadingFailed {
    _kind: typeof DATA_LOADING_FAILED;
    message: string;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export enum DataQualityCheckGroup {
    FRESHNESS = 'FRESHNESS',
    VOLUME = 'VOLUME'
}

export const DATA_SAMPLE_SQL = 'DataSampleSql';

export interface DataSampleSql {
    _kind: typeof DATA_SAMPLE_SQL;
    sql: string;
}

export const DATA_SOURCE_IMPORTED = 'DataSourceImported';

export interface DataSourceImported {
    _kind: typeof DATA_SOURCE_IMPORTED;
    database: string;
    importedAt: Instant;
    schema: string;
    table: string;
    viewRef: ViewRef;
}

export const DATABASE_NOT_EXISTS_OR_NOT_AUTHORIZED = 'DatabaseNotExistsOrNotAuthorized';

export interface DatabaseNotExistsOrNotAuthorized {
    _kind: typeof DATABASE_NOT_EXISTS_OR_NOT_AUTHORIZED;
    database: string;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const DATAMEER_CHANNEL_CREATED = 'DatameerChannelCreated';

export interface DatameerChannelCreated {
    _kind: typeof DATAMEER_CHANNEL_CREATED;
    channel: DatameerChannelEntry;
}

export interface DatameerChannelEntry {
    datameerChannelUid: string;
    name: string;
    payload: DatameerChannelPayload;
}

export const DATAMEER_CHANNEL_NOT_FOUND = 'DatameerChannelNotFound';

export interface DatameerChannelNotFound {
    _kind: typeof DATAMEER_CHANNEL_NOT_FOUND;
    channelId: string;
}

export type DatameerChannelPayload = DatameerSlackChannelPayload | DatameerTestChannelPayload;

export const DATAMEER_CHANNEL_USAGES = 'DatameerChannelUsages';

export interface DatameerChannelUsages {
    _kind: typeof DATAMEER_CHANNEL_USAGES;
    tableCheckNameById: Record<number, string>;
}

export const DATAMEER_CHANNELS = 'DatameerChannels';

export interface DatameerChannels {
    _kind: typeof DATAMEER_CHANNELS;
    entries: Array<DatameerChannelEntry>;
}

export interface DatameerSlackChannelPayload {
    channelId: string;
}

export interface DatameerTestChannelPayload {}

export interface DatameerWarehouse {
    name?: string;
    type: DatameerWarehouseType;
}

export enum DatameerWarehouseType {
    CUSTOM = 'CUSTOM',
    DEFAULT = 'DEFAULT',
    NOT_CONFIGURED = 'NOT_CONFIGURED'
}

export type DatasetError =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | CustomTransformationErrors
    | DatabaseNotExistsOrNotAuthorized
    | DuplicateColumnNames
    | ErrorInReference
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | InvalidSqlSyntax
    | MissingColumn
    | MissingTable
    | NotExistsOrNotAuthorized
    | SchemaNotExistsOrNotAuthorized
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | ValueNotRecognized;

export type DatasetUid = OperationUid | SnowflakeTableFqn | ViewUid;
export type DayOfWeek = string;

export const DBT_MODEL_INFO = 'DbtModelInfo';

export interface DbtModelInfo {
    _kind: typeof DBT_MODEL_INFO;
    database: string;
    dbtProject: string;
    dbtProjectVersion: string;
    description?: string;
    githubModelPath: string;
    githubOwner: string;
    githubRef: string;
    githubRepository: string;
    schema: string;
    table: string;
    usedBy: Array<ModelReference>;
    uses: Array<ModelReference>;
}

export const DELETE_DATAMEER_CHANNEL = 'DeleteDatameerChannel';

export interface DeleteDatameerChannel {
    _kind: typeof DELETE_DATAMEER_CHANNEL;
    uid: string;
}

export type DeleteDatameerChannelSuccess = Succeeded;

export type DeleteDatameerChannelFailure = UnexpectedFailure;

export type DeleteDatameerChannelMessage =
    DeleteDatameerChannelSuccess | DeleteDatameerChannelFailure;

export const DELETE_EVENT_SUBSCRIPTION = 'DeleteEventSubscription';

export interface DeleteEventSubscription {
    _kind: typeof DELETE_EVENT_SUBSCRIPTION;
    name: string;
}

export type DeleteEventSubscriptionSuccess = Succeeded;

export type DeleteEventSubscriptionFailure = UnexpectedFailure;

export type DeleteEventSubscriptionMessage =
    DeleteEventSubscriptionSuccess | DeleteEventSubscriptionFailure;

export const DELETE_EXPLORATION = 'DeleteExploration';

export interface DeleteExploration {
    _kind: typeof DELETE_EXPLORATION;
    id: string;
}

export type DeleteExplorationSuccess = ExplorationDeleted;

export type DeleteExplorationFailure = ExplorationNotFound | UnexpectedFailure;

export type DeleteExplorationMessage = DeleteExplorationSuccess | DeleteExplorationFailure;

export const DELETE_GITHUB_AUTHENTICATION = 'DeleteGithubAuthentication';

export interface DeleteGithubAuthentication {
    _kind: typeof DELETE_GITHUB_AUTHENTICATION;
}

export type DeleteGithubAuthenticationSuccess = Succeeded;

export type DeleteGithubAuthenticationFailure = UnexpectedFailure;

export type DeleteGithubAuthenticationMessage =
    DeleteGithubAuthenticationSuccess | DeleteGithubAuthenticationFailure;

export const DELETE_GROUP = 'DeleteGroup';

export interface DeleteGroup {
    _kind: typeof DELETE_GROUP;
    groupUid: string;
    spaceUid: string;
}

export type DeleteGroupSuccess = GroupDeleted;

export type DeleteGroupFailure = UnexpectedFailure;

export type DeleteGroupMessage = DeleteGroupSuccess | DeleteGroupFailure;

export const DELETE_JOB_DEFINITION = 'DeleteJobDefinition';

export interface DeleteJobDefinition {
    _kind: typeof DELETE_JOB_DEFINITION;
    uid: string;
}

export type DeleteJobDefinitionSuccess = Succeeded;

export type DeleteJobDefinitionFailure = UnexpectedFailure;

export type DeleteJobDefinitionMessage = DeleteJobDefinitionSuccess | DeleteJobDefinitionFailure;

export const DELETE_JOB_ENVIRONMENT = 'DeleteJobEnvironment';

export interface DeleteJobEnvironment {
    _kind: typeof DELETE_JOB_ENVIRONMENT;
    uid: string;
}

export type DeleteJobEnvironmentSuccess = Succeeded;

export type DeleteJobEnvironmentFailure = UnexpectedFailure;

export type DeleteJobEnvironmentMessage = DeleteJobEnvironmentSuccess | DeleteJobEnvironmentFailure;

export const DELETE_SHARE = 'DeleteShare';

export interface DeleteShare {
    _kind: typeof DELETE_SHARE;
    id: number;
}

export type DeleteShareSuccess = Succeeded;

export type DeleteShareFailure = UnexpectedFailure;

export type DeleteShareMessage = DeleteShareSuccess | DeleteShareFailure;

export const DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER = 'DeleteSnowflakeServiceAccountUser';

export interface DeleteSnowflakeServiceAccountUser {
    _kind: typeof DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER;
    uid: string;
}

export type DeleteSnowflakeServiceAccountUserSuccess = SnowflakeServiceAccountUserDeleted;

export type DeleteSnowflakeServiceAccountUserFailure = Canceled | Timeout | UnexpectedFailure;

export type DeleteSnowflakeServiceAccountUserMessage =
    DeleteSnowflakeServiceAccountUserSuccess | DeleteSnowflakeServiceAccountUserFailure;

export const DELETE_SNOWFLAKE_TABLE = 'DeleteSnowflakeTable';

export interface DeleteSnowflakeTable {
    _kind: typeof DELETE_SNOWFLAKE_TABLE;
    database: string;
    schema: string;
    table: string;
    tableType: TableType;
}

export type DeleteSnowflakeTableSuccess = SnowflakeTableDeleted;

export type DeleteSnowflakeTableProgress = SnowflakeTableDeleted;

export type DeleteSnowflakeTableFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type DeleteSnowflakeTableMessage =
    DeleteSnowflakeTableSuccess | DeleteSnowflakeTableProgress | DeleteSnowflakeTableFailure;

export const DELETE_SNOWFLAKE_USER_CREDENTIALS = 'DeleteSnowflakeUserCredentials';

export interface DeleteSnowflakeUserCredentials {
    _kind: typeof DELETE_SNOWFLAKE_USER_CREDENTIALS;
}

export type DeleteSnowflakeUserCredentialsSuccess = Succeeded;

export type DeleteSnowflakeUserCredentialsFailure = UnexpectedFailure;

export type DeleteSnowflakeUserCredentialsMessage =
    DeleteSnowflakeUserCredentialsSuccess | DeleteSnowflakeUserCredentialsFailure;

export const DELETE_TEMPORARY_TABLE = 'DeleteTemporaryTable';

export interface DeleteTemporaryTable {
    _kind: typeof DELETE_TEMPORARY_TABLE;
    database: string;
    schema: string;
    table: string;
}

export type DeleteTemporaryTableSuccess = Succeeded;

export type DeleteTemporaryTableFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | SnowflakeObjectNotFoundOrNotAccessible
    | UnexpectedFailure;

export type DeleteTemporaryTableMessage = DeleteTemporaryTableSuccess | DeleteTemporaryTableFailure;

export const DELETE_UNUSED_DATASETS_FROM_PROJECT = 'DeleteUnusedDatasetsFromProject';

export interface DeleteUnusedDatasetsFromProject {
    _kind: typeof DELETE_UNUSED_DATASETS_FROM_PROJECT;
    datasets: Array<string>;
}

export type DeleteUnusedDatasetsFromProjectSuccess = DeleteUnusedDatasetsFromProjectRequested;

export type DeleteUnusedDatasetsFromProjectProgress = ViewDeleted;

export type DeleteUnusedDatasetsFromProjectFailure = UnexpectedFailure;

export type DeleteUnusedDatasetsFromProjectMessage =
    | DeleteUnusedDatasetsFromProjectSuccess
    | DeleteUnusedDatasetsFromProjectProgress
    | DeleteUnusedDatasetsFromProjectFailure;

export const DELETE_UNUSED_DATASETS_FROM_PROJECT_REQUESTED =
    'DeleteUnusedDatasetsFromProjectRequested';

export interface DeleteUnusedDatasetsFromProjectRequested {
    _kind: typeof DELETE_UNUSED_DATASETS_FROM_PROJECT_REQUESTED;
    datasets: Array<string>;
}

export const DELETE_USER = 'DeleteUser';

export interface DeleteUser {
    _kind: typeof DELETE_USER;
    username: string;
}

export type DeleteUserSuccess = UserDeleted;

export type DeleteUserFailure = NotPermitted | UserNotFound | UnexpectedFailure;

export type DeleteUserMessage = DeleteUserSuccess | DeleteUserFailure;

export const DELETE_VIEW = 'DeleteView';

export interface DeleteView {
    _kind: typeof DELETE_VIEW;
    assetId: string;
    ignoreWarnings: boolean;
}

export type DeleteViewSuccess = ViewDeleted;

export type DeleteViewFailure =
    | EntityNotFoundOrNotAccessible
    | AssetDeletionNotPermitted
    | AssetNotDeletable
    | UnexpectedFailure;

export type DeleteViewMessage = DeleteViewSuccess | DeleteViewFailure;

export const DEPLOY_MODEL = 'DeployModel';

export interface DeployModel {
    _kind: typeof DEPLOY_MODEL;
    operationNodeUuid: string;
    targetTable: SnowflakeTableFqn;
}

export type DeployModelSuccess = ModelDeployed;

export type DeployModelFailure = UnexpectedFailure;

export type DeployModelMessage = DeployModelSuccess | DeployModelFailure;

export const DEPLOY_TRANSFORMATION_AS_SNOWFLAKE_TABLE = 'DeployTransformationAsSnowflakeTable';

export interface DeployTransformationAsSnowflakeTable {
    _kind: typeof DEPLOY_TRANSFORMATION_AS_SNOWFLAKE_TABLE;
    database: string;
    deploymentMessage?: string;
    redeploy: boolean;
    role?: string;
    schema: string;
    table: string;
    transformationId: string;
    warehouse?: string;
}

export type DeployTransformationAsSnowflakeTableSuccess =
    DataSourceImported | SnowflakeTableAlreadyImported;

export type DeployTransformationAsSnowflakeTableProgress =
    DataSourceImported | ViewPublishingInitiated | FailedTableMaterializationCompleted;

export type DeployTransformationAsSnowflakeTableFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | FailureDuringSubmission
    | SecretNotFound
    | TargetViewOrTableNotAllowedToOverwrite
    | ViewWithSameNameAlreadyExists
    | NoDeploymentHistory
    | CompilationFailedWithDatasetError
    | EntityNotFoundOrNotAccessible
    | AlreadyDeployedFromAnotherTransformation
    | AlreadyUsedAsProjectInput
    | RequirementNotMet
    | TransformationNotOperational
    | TableAlreadyExists
    | UnexpectedFailure;

export type DeployTransformationAsSnowflakeTableMessage =
    | DeployTransformationAsSnowflakeTableSuccess
    | DeployTransformationAsSnowflakeTableProgress
    | DeployTransformationAsSnowflakeTableFailure;

export const DEPLOY_TRANSFORMATION_AS_SNOWFLAKE_VIEW = 'DeployTransformationAsSnowflakeView';

export interface DeployTransformationAsSnowflakeView {
    _kind: typeof DEPLOY_TRANSFORMATION_AS_SNOWFLAKE_VIEW;
    database: string;
    deploymentMessage?: string;
    redeploy: boolean;
    schema: string;
    table: string;
    transformationId: string;
}

export type DeployTransformationAsSnowflakeViewSuccess = ViewDeployed;

export type DeployTransformationAsSnowflakeViewFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | ViewAlreadyExists
    | CompilationFailedWithDatasetError
    | AlreadyDeployedFromAnotherTransformation
    | AlreadyUsedAsProjectInput
    | RequirementNotMet
    | TransformationNotOperational
    | TableAlreadyExists
    | TargetViewOrTableNotAllowedToOverwrite
    | UnexpectedFailure;

export type DeployTransformationAsSnowflakeViewMessage =
    DeployTransformationAsSnowflakeViewSuccess | DeployTransformationAsSnowflakeViewFailure;

export interface DeployedNodeIsDataDrivenTrigger {
    message: string;
    tableRefs: Array<ProjectTableRef>;
}

export const DEPLOYMENT_DETAILS = 'DeploymentDetails';

export interface DeploymentDetails {
    _kind: typeof DEPLOYMENT_DETAILS;
    accessible: boolean;
    columnsDiff?: BasicColumnsDiff;
    createdAt?: Instant;
    database: string;
    deploymentImportedBy: Array<ProjectReference>;
    deploymentOutOfSync: Array<DeploymentOutOfSyncProperty>;
    failures: Array<object>;
    lastRun?: Instant;
    lastStatus?: ExecutionStatus;
    modifiedAt?: Instant;
    lastDDL: Instant;
    nextRun?: Instant;
    role?: string;
    rowCount?: number;
    running: boolean;
    sampleSize?: number;
    schema: string;
    table: string;
    tableType?: TableType;
    trigger: SnowflakeOutputTriggerResponse;
    warehouse?: string;
}

export const DEPLOYMENT_HISTORY_ENTRIES = 'DeploymentHistoryEntries';

export interface DeploymentHistoryEntries {
    _kind: typeof DEPLOYMENT_HISTORY_ENTRIES;
    entries: Array<DeploymentHistoryEntry>;
}

export interface DeploymentHistoryEntry {
    columns: Array<SqlColumn>;
    completeSql: string;
    database: string;
    deployedAt: Instant;
    message: string;
    restoredVersion?: number;
    schema: string;
    table: string;
    transformationId: string;
    transformationSql: string;
    triggerSourceFqn?: SnowflakeTableFqn;
    type: TableType;
    user: string;
    version: number;
}

export enum DeploymentOutOfSyncProperty {
    COLUMNS_ADDED_UPSTREAM = 'COLUMNS_ADDED_UPSTREAM',
    COLUMNS_REMOVED_UPSTREAM = 'COLUMNS_REMOVED_UPSTREAM',
    COLUMNS_TYPE_CHANGED_UPSTREAM = 'COLUMNS_TYPE_CHANGED_UPSTREAM',
    TRANSFORMATION_SQL = 'TRANSFORMATION_SQL'
}

export const DEPLOYMENT_OVERVIEW = 'DeploymentOverview';

export interface DeploymentOverview {
    _kind: typeof DEPLOYMENT_OVERVIEW;
    entries: Array<DeploymentOverviewEntry>;
}

export interface DeploymentOverviewEntry {
    database: string;
    deployedAt: Instant;
    projectDatasetName?: string;
    projectDatasetSlug?: string;
    projectName?: string;
    projectSlug?: string;
    schema: string;
    table: string;
    type: TableType;
    user: string;
    version: number;
}

export const DERIVED_ASSET_MUST_NOT_BE_MODIFIED = 'DerivedAssetMustNotBeModified';

export interface DerivedAssetMustNotBeModified {
    _kind: typeof DERIVED_ASSET_MUST_NOT_BE_MODIFIED;
    assetId: EntityId;
}

export const DISCOVERY_FAILURE = 'DiscoveryFailure';

export interface DiscoveryFailure {
    _kind: typeof DISCOVERY_FAILURE;
    error: string;
}

export const DISTINCT_VALUES = 'DistinctValues';

export interface DistinctValues {
    _kind: typeof DISTINCT_VALUES;
    columnUuid: ColumnUuid;
    values: Array<string>;
}

export const DOWNLOAD_PREPARED = 'DownloadPrepared';

export interface DownloadPrepared {
    _kind: typeof DOWNLOAD_PREPARED;
    preparedDownloadDeletionDelay: Duration;
    preparedDownloadFilename: string;
    viewName: string;
    viewUuid: ViewUuid;
}

export const DOWNSTREAM_REFERENCES = 'DownstreamReferences';

export interface DownstreamReferences {
    _kind: typeof DOWNSTREAM_REFERENCES;
    references: Array<ViewRef>;
}

export interface DuplicateColumnName {
    columnName: string;
    message: string;
}

export const DUPLICATE_COLUMN_NAMES = 'DuplicateColumnNames';

export interface DuplicateColumnNames {
    _kind: typeof DUPLICATE_COLUMN_NAMES;
    columnNames: Array<string>;
    index?: number;
    origins: Array<DatasetUid>;
}
export type Duration = string;

export enum DurationUnit {
    HOURS = 'HOURS',
    MINUTES = 'MINUTES',
    DAYS = 'DAYS'
}

export const EMAIL_ADDRESS_ALREADY_TAKEN = 'EmailAddressAlreadyTaken';

export interface EmailAddressAlreadyTaken {
    _kind: typeof EMAIL_ADDRESS_ALREADY_TAKEN;
    email: string;
}

export const EMAIL_DOMAIN_NOT_AUTHORIZED = 'EmailDomainNotAuthorized';

export interface EmailDomainNotAuthorized {
    _kind: typeof EMAIL_DOMAIN_NOT_AUTHORIZED;
    emailDomain: string;
}

export interface EmailTarget {
    recipients: Array<string>;
}

export const ENABLE_USER = 'EnableUser';

export interface EnableUser {
    _kind: typeof ENABLE_USER;
    username: string;
}

export type EnableUserSuccess = UserEnabled;

export type EnableUserFailure =
    NotPermitted | TooManyEnabledUsers | UserAlreadyEnabled | UnexpectedFailure;

export type EnableUserMessage = EnableUserSuccess | EnableUserFailure;

export type EntityId =
    | SnowflakeColumnFqn
    | SnowflakeTableFqn
    | SpaceUid
    | SpaceViewColumn
    | SpaceViewUuid
    | TransformationColumn
    | TransformationUuid;

export interface EntityIsReferenced {
    message: string;
    references: Array<RefDto>;
}

export interface EntityIsReferencedByTransformationStack {
    message: string;
    references: Array<string>;
}

export type EntityModificationConcern =
    | DeployedNodeIsDataDrivenTrigger
    | EntityIsReferenced
    | EntityIsReferencedByTransformationStack
    | ModelDeletionConcern
    | ModificationIsNotSupported
    | OwnerOrCollaboratorshipRequired
    | OwnershipRequired;

export const ENTITY_NOT_FOUND_OR_NOT_ACCESSIBLE = 'EntityNotFoundOrNotAccessible';

export interface EntityNotFoundOrNotAccessible {
    _kind: typeof ENTITY_NOT_FOUND_OR_NOT_ACCESSIBLE;
    entityId: EntityId;
}

export interface ErrorInReference {
    index?: number;
    origins: Array<DatasetUid>;
    views: Array<string>;
}

export interface ErrorPosition {
    line: number;
    startPosition: number;
}

export type EventDeliveryTarget = SlackChannel | Webhook;

export const EVENT_ID_NOT_FOUND = 'EventIdNotFound';

export interface EventIdNotFound {
    _kind: typeof EVENT_ID_NOT_FOUND;
}

export const EVENT_SUBSCRIPTION_CREATED = 'EventSubscriptionCreated';

export interface EventSubscriptionCreated {
    _kind: typeof EVENT_SUBSCRIPTION_CREATED;
    id: number;
}

export const EVENT_SUBSCRIPTION_NOT_FOUND = 'EventSubscriptionNotFound';

export interface EventSubscriptionNotFound {
    _kind: typeof EVENT_SUBSCRIPTION_NOT_FOUND;
}

export const EVENT_SUBSCRIPTIONS = 'EventSubscriptions';

export interface EventSubscriptions {
    _kind: typeof EVENT_SUBSCRIPTIONS;
    entries: Array<ListEventSubscriptionResponse>;
}

export const EXECUTE_ARBITRARY_SQL = 'ExecuteArbitrarySql';

export interface ExecuteArbitrarySql {
    _kind: typeof EXECUTE_ARBITRARY_SQL;
    overwriteTimeout?: Duration;
    sql: string;
}

export type ExecuteArbitrarySqlSuccess = ArbitrarySqlResult;

export type ExecuteArbitrarySqlFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ExecuteArbitrarySqlMessage = ExecuteArbitrarySqlSuccess | ExecuteArbitrarySqlFailure;

export const EXECUTE_QUERY_BY_ENVIRONMENT = 'ExecuteQueryByEnvironment';

export interface ExecuteQueryByEnvironment {
    _kind: typeof EXECUTE_QUERY_BY_ENVIRONMENT;
    environmentUid: string;
    sql: string;
}

export type ExecuteQueryByEnvironmentSuccess = ResultRecords;

export type ExecuteQueryByEnvironmentFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ExecuteQueryByEnvironmentMessage =
    ExecuteQueryByEnvironmentSuccess | ExecuteQueryByEnvironmentFailure;

export const EXECUTE_QUERY_BY_SERVICE_ACCOUNT = 'ExecuteQueryByServiceAccount';

export interface ExecuteQueryByServiceAccount {
    _kind: typeof EXECUTE_QUERY_BY_SERVICE_ACCOUNT;
    credentials?: Record<string, string>;
    role?: string;
    serviceAccountUid?: string;
    sql: string;
    warehouse?: string;
}

export type ExecuteQueryByServiceAccountSuccess = ResultRecords;

export type ExecuteQueryByServiceAccountFailure = SecretNotFound | UnexpectedFailure;

export type ExecuteQueryByServiceAccountMessage =
    ExecuteQueryByServiceAccountSuccess | ExecuteQueryByServiceAccountFailure;

export const EXECUTION_HISTORY = 'ExecutionHistory';

export interface ExecutionHistory {
    _kind: typeof EXECUTION_HISTORY;
    history: Array<ExecutionHistoryEntry>;
}

export interface ExecutionHistoryEntry {
    byteCount?: number;
    deploymentVersion?: number;
    duration?: Duration;
    endTime?: Instant;
    error?: string;
    payload?: Record<string, unknown>;
    queryId?: string;
    recordCount?: number;
    role?: string;
    sessionId?: string;
    snowflakeUser?: string;
    startTime?: Instant;
    status: string;
    triggerType: ExecutionTriggerType;
    username: string;
    warehouse?: string;
}

export enum ExecutionStatus {
    FAILED_WITH_ERROR = 'FAILED_WITH_ERROR',
    RUNNING = 'RUNNING',
    SUCCESS = 'SUCCESS'
}

export enum ExecutionTriggerType {
    API = 'API',
    DATA_DRIVEN = 'DATA_DRIVEN',
    EVENT_DRIVEN = 'EVENT_DRIVEN',
    SCHEDULE_DRIVEN = 'SCHEDULE_DRIVEN',
    MANUAL = 'MANUAL'
}

export const EXISTS = 'Exists';

export interface Exists {
    _kind: typeof EXISTS;
    slug: string;
}

export const EXPLORATION = 'Exploration';

export interface Exploration {
    _kind: typeof EXPLORATION;
    model: object;
    sql: string;
    tableFqn?: SnowflakeTableFqn;
    transformationId?: string;
}

export const EXPLORATION_CREATED = 'ExplorationCreated';

export interface ExplorationCreated {
    _kind: typeof EXPLORATION_CREATED;
}

export const EXPLORATION_DELETED = 'ExplorationDeleted';

export interface ExplorationDeleted {
    _kind: typeof EXPLORATION_DELETED;
}

export const EXPLORATION_DOWNLOAD_INITIATED = 'ExplorationDownloadInitiated';

export interface ExplorationDownloadInitiated {
    _kind: typeof EXPLORATION_DOWNLOAD_INITIATED;
    fqn?: SnowflakeTableFqn;
    referencedViews: Array<ViewUuid>;
    spaceUid?: string;
    sql: string;
}

export const EXPLORATION_DOWNLOAD_PREPARED = 'ExplorationDownloadPrepared';

export interface ExplorationDownloadPrepared {
    _kind: typeof EXPLORATION_DOWNLOAD_PREPARED;
    preparedDownloadDeletionDelay: Duration;
    preparedDownloadFilename: string;
}

export const EXPLORATION_EMAIL_REQUESTED = 'ExplorationEmailRequested';

export interface ExplorationEmailRequested {
    _kind: typeof EXPLORATION_EMAIL_REQUESTED;
    explorationId: string;
    message: string;
    recipients: Array<string>;
    requestedBy: string;
    shareId?: number;
    subject: string;
}

export interface ExplorationEntry {
    id: string;
    model: object;
    sql: string;
}

export type ExplorationId = string;

export const EXPLORATION_NOT_FOUND = 'ExplorationNotFound';

export interface ExplorationNotFound {
    _kind: typeof EXPLORATION_NOT_FOUND;
}

export const EXPLORATION_PROMOTED = 'ExplorationPromoted';

export interface ExplorationPromoted {
    _kind: typeof EXPLORATION_PROMOTED;
    viewRef: ViewRef;
}

export const EXPLORATION_UPDATED = 'ExplorationUpdated';

export interface ExplorationUpdated {
    _kind: typeof EXPLORATION_UPDATED;
}

export const EXPLORATIONS = 'Explorations';

export interface Explorations {
    _kind: typeof EXPLORATIONS;
    entries: Array<ExplorationEntry>;
}

export interface Facet {
    count: number;
    value: string;
}

export interface Facets {
    facets: Array<Facet>;
    name: string;
    type: string;
}

export const FAILED_TABLE_MATERIALIZATION_COMPLETED = 'FailedTableMaterializationCompleted';

export interface FailedTableMaterializationCompleted {
    _kind: typeof FAILED_TABLE_MATERIALIZATION_COMPLETED;
    database: string;
    errorCode?: number;
    errorMessage: string;
    outputNodeUrl: string;
    projectUid: string;
    queryId: string;
    queryStatus?: string;
    schema: string;
    table: string;
}

export const FAILED_TEMPORARY_TABLE_MATERIALIZATION_COMPLETED =
    'FailedTemporaryTableMaterializationCompleted';

export interface FailedTemporaryTableMaterializationCompleted {
    _kind: typeof FAILED_TEMPORARY_TABLE_MATERIALIZATION_COMPLETED;
}

export const FAILURE_DURING_SUBMISSION = 'FailureDuringSubmission';

export interface FailureDuringSubmission {
    _kind: typeof FAILURE_DURING_SUBMISSION;
    errorCode?: number;
    errorMessage: string;
    sqlState?: string;
}

export interface FailureResponsePayload {
    failures: Array<object>;
}

export const FETCH_DAP_MIGRATION_FILES_STARTED = 'FetchDapMigrationFilesStarted';

export interface FetchDapMigrationFilesStarted {
    _kind: typeof FETCH_DAP_MIGRATION_FILES_STARTED;
    dapMigrationFiles: Array<string>;
}

export const FETCH_SNOWFLAKE_TABLE_COLUMNS = 'FetchSnowflakeTableColumns';

export interface FetchSnowflakeTableColumns {
    _kind: typeof FETCH_SNOWFLAKE_TABLE_COLUMNS;
    database: string;
    schema: string;
    table: string;
}

export type FetchSnowflakeTableColumnsSuccess = SnowflakeTableColumns;

export type FetchSnowflakeTableColumnsFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type FetchSnowflakeTableColumnsMessage =
    FetchSnowflakeTableColumnsSuccess | FetchSnowflakeTableColumnsFailure;

export const FILE_FORMAT_CHANGED = 'FileFormatChanged';

export interface FileFormatChanged {
    _kind: typeof FILE_FORMAT_CHANGED;
    changes: Array<string>;
}

export const FILE_FORMAT_NOT_SUPPORTED = 'FileFormatNotSupported';

export interface FileFormatNotSupported {
    _kind: typeof FILE_FORMAT_NOT_SUPPORTED;
    fileFormatType: string;
}

export interface FileFormatProperties {
    properties: Record<string, string>;
}

export type FileFormatProperties_ = object;

export interface Filter {
    conditions: Array<FilterCondition>;
}

export interface FilterCondition {
    column: string;
    operator: Operator;
    values: Array<string>;
}

export interface Formula {
    columns: Array<FormulaColumn>;
}

export interface FormulaCircularReference {
    message: string;
}

export interface FormulaColumn {
    expression: string;
    isVisible: boolean;
    name: string;
}

export interface GenericTransform {
    model: object;
    sqlTemplate: string;
    suggestedViewName: string;
    type: string;
}

export const GET_ACCESS_CONTROLS = 'GetAccessControls';

export interface GetAccessControls {
    _kind: typeof GET_ACCESS_CONTROLS;
    assetIds: Array<EntityId>;
}

export type GetAccessControlsSuccess = AccessControlDtos;

export type GetAccessControlsFailure = UnexpectedFailure;

export type GetAccessControlsMessage = GetAccessControlsSuccess | GetAccessControlsFailure;

export const GET_CHAT_GPT_INFO = 'GetChatGptInfo';

export interface GetChatGptInfo {
    _kind: typeof GET_CHAT_GPT_INFO;
}

export type GetChatGptInfoSuccess = ChatGptInfo;

export type GetChatGptInfoFailure = SecretNotFound | UnexpectedFailure;

export type GetChatGptInfoMessage = GetChatGptInfoSuccess | GetChatGptInfoFailure;

export const GET_COLUMN_METADATA = 'GetColumnMetadata';

export interface GetColumnMetadata {
    _kind: typeof GET_COLUMN_METADATA;
    columnNames: Array<string>;
    viewUuid: ViewUuid;
}

export type GetColumnMetadataSuccess = ColumnMetadata;

export type GetColumnMetadataFailure = UnexpectedFailure;

export type GetColumnMetadataMessage = GetColumnMetadataSuccess | GetColumnMetadataFailure;

export const GET_DATA_DRIVEN_TRIGGER_CANDIDATES = 'GetDataDrivenTriggerCandidates';

export interface GetDataDrivenTriggerCandidates {
    _kind: typeof GET_DATA_DRIVEN_TRIGGER_CANDIDATES;
    outputNode: string;
}

export type GetDataDrivenTriggerCandidatesSuccess = DataDrivenTriggerCandidates;

export type GetDataDrivenTriggerCandidatesFailure = UnexpectedFailure;

export type GetDataDrivenTriggerCandidatesMessage =
    GetDataDrivenTriggerCandidatesSuccess | GetDataDrivenTriggerCandidatesFailure;

export const GET_DATAMEER_CHANNEL_USAGES = 'GetDatameerChannelUsages';

export interface GetDatameerChannelUsages {
    _kind: typeof GET_DATAMEER_CHANNEL_USAGES;
    datameerChannelUid: string;
}

export type GetDatameerChannelUsagesSuccess = DatameerChannelUsages;

export type GetDatameerChannelUsagesFailure = UnexpectedFailure;

export type GetDatameerChannelUsagesMessage =
    GetDatameerChannelUsagesSuccess | GetDatameerChannelUsagesFailure;

export const GET_DBT_MODEL_INFO = 'GetDbtModelInfo';

export interface GetDbtModelInfo {
    _kind: typeof GET_DBT_MODEL_INFO;
    database: string;
    schema: string;
    table: string;
}

export type GetDbtModelInfoSuccess = DbtModelInfo;

export type GetDbtModelInfoFailure = NoDbtModelFound | UnexpectedFailure;

export type GetDbtModelInfoMessage = GetDbtModelInfoSuccess | GetDbtModelInfoFailure;

export const GET_DEPLOYMENT_HISTORY = 'GetDeploymentHistory';

export interface GetDeploymentHistory {
    _kind: typeof GET_DEPLOYMENT_HISTORY;
    database: string;
    limit: number;
    offset: number;
    schema: string;
    table: string;
}

export type GetDeploymentHistorySuccess = DeploymentHistoryEntries;

export type GetDeploymentHistoryFailure = UnexpectedFailure;

export type GetDeploymentHistoryMessage = GetDeploymentHistorySuccess | GetDeploymentHistoryFailure;

export const GET_DEPLOYMENT_OVERVIEW = 'GetDeploymentOverview';

export interface GetDeploymentOverview {
    _kind: typeof GET_DEPLOYMENT_OVERVIEW;
}

export type GetDeploymentOverviewSuccess = DeploymentOverview;

export type GetDeploymentOverviewFailure = UnexpectedFailure;

export type GetDeploymentOverviewMessage =
    GetDeploymentOverviewSuccess | GetDeploymentOverviewFailure;

export const GET_DISTINCT_COLUMN_VALUES = 'GetDistinctColumnValues';

export interface GetDistinctColumnValues {
    _kind: typeof GET_DISTINCT_COLUMN_VALUES;
    columnUuid: ColumnUuid;
    limit?: number;
    timeout?: Duration;
}

export type GetDistinctColumnValuesSuccess = DistinctValues;

export type GetDistinctColumnValuesFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | Canceled
    | CompilationFailedWithDatasetError
    | EntityNotFoundOrNotAccessible
    | UnexpectedFailure;

export type GetDistinctColumnValuesMessage =
    GetDistinctColumnValuesSuccess | GetDistinctColumnValuesFailure;

export const GET_DOWNSTREAM_REFERENCES = 'GetDownstreamReferences';

export interface GetDownstreamReferences {
    _kind: typeof GET_DOWNSTREAM_REFERENCES;
    database: string;
    schema: string;
    table: string;
}

export type GetDownstreamReferencesSuccess = DownstreamReferences;

export type GetDownstreamReferencesFailure = UnexpectedFailure;

export type GetDownstreamReferencesMessage =
    GetDownstreamReferencesSuccess | GetDownstreamReferencesFailure;

export const GET_EVENT_SUBSCRIPTION = 'GetEventSubscription';

export interface GetEventSubscription {
    _kind: typeof GET_EVENT_SUBSCRIPTION;
    name: string;
}

export type GetEventSubscriptionSuccess = GetEventSubscriptionResponse;

export type GetEventSubscriptionFailure = EventSubscriptionNotFound | UnexpectedFailure;

export type GetEventSubscriptionMessage = GetEventSubscriptionSuccess | GetEventSubscriptionFailure;

export const GET_EVENT_SUBSCRIPTION_RESPONSE = 'GetEventSubscriptionResponse';

export interface GetEventSubscriptionResponse {
    _kind: typeof GET_EVENT_SUBSCRIPTION_RESPONSE;
    deliveryTarget: EventDeliveryTarget;
    eventFilters: Record<string, string>;
    eventId: string;
    id: number;
    name: string;
}

export const GET_EXECUTION_HISTORY = 'GetExecutionHistory';

export interface GetExecutionHistory {
    _kind: typeof GET_EXECUTION_HISTORY;
    maximumNumberOfRecords: number;
    snowflakeTableFqn: SnowflakeTableFqn;
}

export type GetExecutionHistorySuccess = ExecutionHistory;

export type GetExecutionHistoryFailure = UnexpectedFailure;

export type GetExecutionHistoryMessage = GetExecutionHistorySuccess | GetExecutionHistoryFailure;

export const GET_EXPLORATION = 'GetExploration';

export interface GetExploration {
    _kind: typeof GET_EXPLORATION;
    id: string;
}

export type GetExplorationSuccess = Exploration;

export type GetExplorationFailure = ExplorationNotFound | UnexpectedFailure;

export type GetExplorationMessage = GetExplorationSuccess | GetExplorationFailure;

export const GET_GITHUB_AUTHENTICATION_INFO = 'GetGithubAuthenticationInfo';

export interface GetGithubAuthenticationInfo {
    _kind: typeof GET_GITHUB_AUTHENTICATION_INFO;
}

export type GetGithubAuthenticationInfoSuccess = GithubAuthenticationInfo;

export type GetGithubAuthenticationInfoFailure = SecretNotFound | UnexpectedFailure;

export type GetGithubAuthenticationInfoMessage =
    GetGithubAuthenticationInfoSuccess | GetGithubAuthenticationInfoFailure;

export const GET_GOOGLE_CREDENTIALS_INFO = 'GetGoogleCredentialsInfo';

export interface GetGoogleCredentialsInfo {
    _kind: typeof GET_GOOGLE_CREDENTIALS_INFO;
}

export type GetGoogleCredentialsInfoSuccess = GoogleCredentialsInfo;

export type GetGoogleCredentialsInfoFailure = SecretNotFound | UnexpectedFailure;

export type GetGoogleCredentialsInfoMessage =
    GetGoogleCredentialsInfoSuccess | GetGoogleCredentialsInfoFailure;

export const GET_GOOGLE_SHEETS_SHARE = 'GetGoogleSheetsShare';

export interface GetGoogleSheetsShare {
    _kind: typeof GET_GOOGLE_SHEETS_SHARE;
    transformationId: string;
}

export type GetGoogleSheetsShareSuccess = ShareNotFound | GoogleSheetsShare;

export type GetGoogleSheetsShareFailure = UnexpectedFailure;

export type GetGoogleSheetsShareMessage = GetGoogleSheetsShareSuccess | GetGoogleSheetsShareFailure;

export const GET_JOB_ACTIVITY_METRICS = 'GetJobActivityMetrics';

export interface GetJobActivityMetrics {
    _kind: typeof GET_JOB_ACTIVITY_METRICS;
    definitionUids: Array<string>;
    environmentUids: Array<string>;
    period: Period;
    projectUids: Array<string>;
    roles: Array<string>;
    statuses: Array<JobRunStatus>;
    warehouses: Array<string>;
}

export type GetJobActivityMetricsSuccess = JobActivityMetrics;

export type GetJobActivityMetricsFailure = UnexpectedFailure;

export type GetJobActivityMetricsMessage =
    GetJobActivityMetricsSuccess | GetJobActivityMetricsFailure;

export const GET_JOB_DEFINITION = 'GetJobDefinition';

export interface GetJobDefinition {
    _kind: typeof GET_JOB_DEFINITION;
    definitionUid: string;
}

export type GetJobDefinitionSuccess = JobDefinitionDetailedEntry;

export type GetJobDefinitionFailure = UnexpectedFailure;

export type GetJobDefinitionMessage = GetJobDefinitionSuccess | GetJobDefinitionFailure;

export const GET_JOB_OPERATION_COUNTS = 'GetJobOperationCounts';

export interface GetJobOperationCounts {
    _kind: typeof GET_JOB_OPERATION_COUNTS;
}

export type GetJobOperationCountsSuccess = JobOperationCounts;

export type GetJobOperationCountsFailure = UnexpectedFailure;

export type GetJobOperationCountsMessage =
    GetJobOperationCountsSuccess | GetJobOperationCountsFailure;

export const GET_JOB_RUN = 'GetJobRun';

export interface GetJobRun {
    _kind: typeof GET_JOB_RUN;
    jobRunUid: string;
}

export type GetJobRunSuccess = JobRunEntry;

export type GetJobRunFailure = JobRunNotFound | UnexpectedFailure;

export type GetJobRunMessage = GetJobRunSuccess | GetJobRunFailure;

export const GET_JOB_RUN_FILTER_OPTIONS = 'GetJobRunFilterOptions';

export interface GetJobRunFilterOptions {
    _kind: typeof GET_JOB_RUN_FILTER_OPTIONS;
    definitionUids: Array<string>;
    environmentUids: Array<string>;
    period: Period;
    projectUids: Array<string>;
    roles: Array<string>;
    statuses: Array<JobRunStatus>;
    warehouses: Array<string>;
}

export type GetJobRunFilterOptionsSuccess = JobRunFilterOptions;

export type GetJobRunFilterOptionsFailure = UnexpectedFailure;

export type GetJobRunFilterOptionsMessage =
    GetJobRunFilterOptionsSuccess | GetJobRunFilterOptionsFailure;

export const GET_JOB_RUN_TIMELINE = 'GetJobRunTimeline';

export interface GetJobRunTimeline {
    _kind: typeof GET_JOB_RUN_TIMELINE;
    definitionUids: Array<string>;
    environmentUids: Array<string>;
    period: Period;
    projectUids: Array<string>;
    roles: Array<string>;
    statuses: Array<JobRunStatus>;
    warehouses: Array<string>;
}

export type GetJobRunTimelineSuccess = JobRunTimeline;

export type GetJobRunTimelineFailure = UnexpectedFailure;

export type GetJobRunTimelineMessage = GetJobRunTimelineSuccess | GetJobRunTimelineFailure;

interface DeploymentTargetJob {
    _kind: string;
    nodeUid: string;
}
interface ModelTargetJob {
    _kind: string;
    tagId: number;
    sourceTableMappings: Array<SourceTableMapping>;
}
interface StoredProcedureTargetJob {
    _kind: string;
    fqn: SnowflakeTableFqn;
}

export type TargetJob = DeploymentTargetJob | ModelTargetJob | StoredProcedureTargetJob;

export interface JobTriggerCandidateByRole {
    definitionRef: JobDefinitionRef;
    description?: string;
    lastRunAt?: Instant;
    managedTable: SnowflakeTableFqn;
    operationType: string;
    trigger?: JobTrigger;
    environmentRef: JobEnvironmentRef;
}
export const GET_JOB_TRIGGER_CANDIDATES_BY_ROLE = 'GetJobTriggerCandidatesByRole';

export type GetJobTriggerCandidatesByRoleSuccess = JobTriggerCandidates;

export type GetJobTriggerCandidatesByRoleFailure = UnexpectedFailure;

export interface GetJobTriggerCandidatesByRole {
    _kind: typeof GET_JOB_TRIGGER_CANDIDATES_BY_ROLE;
    targetJob: TargetJob;
}

export interface JobTriggerCandidateByRole {
    definitionRef: JobDefinitionRef;
    description?: string;
    lastRunAt?: Instant;
    managedTable: SnowflakeTableFqn;
    operationType: string;
    trigger?: JobTrigger;
}

export interface JobTriggerCandidatesByRole {
    _kind: typeof JOB_TRIGGER_CANDIDATES;
    entries?: Array<JobTriggerCandidateByRole>;
}

export const GET_LAST_JOB_RUN = 'GetLastJobRun';

export interface GetLastJobRun {
    _kind: typeof GET_LAST_JOB_RUN;
    definitionUid: string;
}

export type GetLastJobRunFailure = LastJobRunNotFound | UnexpectedFailure;

export type GetLastJobRunSuccess = LastJobRun;

export type GetLastJobRunMessage = GetLastJobRunSuccess | GetLastJobRunFailure;

export const GET_LOAD_JOB_TARGET_TABLE_COLUMNS = 'GetLoadJobTargetTableColumns';

export interface GetLoadJobTargetTableColumns {
    _kind: typeof GET_LOAD_JOB_TARGET_TABLE_COLUMNS;
    environmentUid: string;
    targetTableFqn: SnowflakeTableFqn;
}

export type GetLoadJobTargetTableColumnsSuccess = TargetTableColumns;

export type GetLoadJobTargetTableColumnsFailure =
    | TargetTableNotExists
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type GetLoadJobTargetTableColumnsMessage =
    GetLoadJobTargetTableColumnsSuccess | GetLoadJobTargetTableColumnsFailure;

export const GET_MODEL_TAG_CHECK_CANDIDATES = 'GetModelTagCheckCandidates';

export interface GetModelTagCheckCandidates {
    _kind: typeof GET_MODEL_TAG_CHECK_CANDIDATES;
    environmentUid: string;
    sourceTableMappings: Array<SourceTableMapping>;
    tagId: number;
}

export type GetModelTagCheckCandidatesSuccess = ModelTagCheckCandidates;

export type GetModelTagCheckCandidatesFailure = UnexpectedFailure;

export type GetModelTagCheckCandidatesMessage =
    GetModelTagCheckCandidatesSuccess | GetModelTagCheckCandidatesFailure;

export const GET_MODEL_TAG_OUTPUT_COLUMNS = 'GetModelTagOutputColumns';

export interface GetModelTagOutputColumns {
    _kind: typeof GET_MODEL_TAG_OUTPUT_COLUMNS;
    tagId: number;
}

export type GetModelTagOutputColumnsSuccess = ModelTagOutputColumns;

export type GetModelTagOutputColumnsFailure = UnexpectedFailure;

export type GetModelTagOutputColumnsMessage =
    GetModelTagOutputColumnsSuccess | GetModelTagOutputColumnsFailure;

export const GET_MODEL_TAG_SOURCE_TABLES = 'GetModelTagSourceTables';

export interface GetModelTagSourceTables {
    _kind: typeof GET_MODEL_TAG_SOURCE_TABLES;
    tagId: number;
}

export type GetModelTagSourceTablesSuccess = ModelTagSourceTables;

export type GetModelTagSourceTablesFailure = UnexpectedFailure;

export type GetModelTagSourceTablesMessage =
    GetModelTagSourceTablesSuccess | GetModelTagSourceTablesFailure;

export const GET_OUTPUT_COLUMNS = 'GetOutputColumns';

export interface GetOutputColumns {
    _kind: typeof GET_OUTPUT_COLUMNS;
    viewUuid: string;
}

export type GetOutputColumnsSuccess = OutputColumns;

export type GetOutputColumnsFailure = EntityNotFoundOrNotAccessible | UnexpectedFailure;

export type GetOutputColumnsMessage = GetOutputColumnsSuccess | GetOutputColumnsFailure;

export const GET_OVERRIDE_CONFIG = 'GetOverrideConfig';

export interface GetOverrideConfig {
    _kind: typeof GET_OVERRIDE_CONFIG;
}

export type GetOverrideConfigSuccess = ConfigProperties;

export type GetOverrideConfigFailure = UnexpectedFailure;

export type GetOverrideConfigMessage = GetOverrideConfigSuccess | GetOverrideConfigFailure;

export const GET_PROJECT_DEPENDENCIES = 'GetProjectDependencies';

export interface GetProjectDependencies {
    _kind: typeof GET_PROJECT_DEPENDENCIES;
    spaceUid: string;
}

export type GetProjectDependenciesSuccess = ProjectDependencies;

export type GetProjectDependenciesFailure = UnexpectedFailure;

export type GetProjectDependenciesMessage =
    GetProjectDependenciesSuccess | GetProjectDependenciesFailure;

export const GET_PROJECT_DEPLOYMENTS = 'GetProjectDeployments';

export interface GetProjectDeployments {
    _kind: typeof GET_PROJECT_DEPLOYMENTS;
    spaceUid: string;
}

export type GetProjectDeploymentsSuccess = ProjectDeployments;

export type GetProjectDeploymentsFailure = UnexpectedFailure;

export type GetProjectDeploymentsMessage =
    GetProjectDeploymentsSuccess | GetProjectDeploymentsFailure;

export const GET_PROJECT_INPUTS = 'GetProjectInputs';

export interface GetProjectInputs {
    _kind: typeof GET_PROJECT_INPUTS;
    spaceUid: string;
}

export type GetProjectInputsSuccess = ProjectInputs;

export type GetProjectInputsFailure = UnexpectedFailure;

export type GetProjectInputsMessage = GetProjectInputsSuccess | GetProjectInputsFailure;

export const GET_PROJECT_REFERENCES = 'GetProjectReferences';

export interface GetProjectReferences {
    _kind: typeof GET_PROJECT_REFERENCES;
    database: string;
    schema: string;
    table: string;
}

export type GetProjectReferencesSuccess = ProjectReferences;

export type GetProjectReferencesFailure = UnexpectedFailure;

export type GetProjectReferencesMessage = GetProjectReferencesSuccess | GetProjectReferencesFailure;

export const GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS = 'GetProjectsContainingTableDeployments';

export interface GetProjectsContainingTableDeployments {
    _kind: typeof GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS;
}

export type GetProjectsContainingTableDeploymentsSuccess = ProjectsContainingTableDeployments;

export type GetProjectsContainingTableDeploymentsFailure = UnexpectedFailure;

export type GetProjectsContainingTableDeploymentsMessage =
    GetProjectsContainingTableDeploymentsSuccess | GetProjectsContainingTableDeploymentsFailure;

export const GET_SLACK_AUTHORIZATION_URL = 'GetSlackAuthorizationUrl';

export interface GetSlackAuthorizationUrl {
    _kind: typeof GET_SLACK_AUTHORIZATION_URL;
    refererPath: string;
}

export type GetSlackAuthorizationUrlSuccess = SlackAuthorizationUrl;

export type GetSlackAuthorizationUrlFailure =
    SecretNotFound | SlackNotConfigured | UnexpectedFailure;

export type GetSlackAuthorizationUrlMessage =
    GetSlackAuthorizationUrlSuccess | GetSlackAuthorizationUrlFailure;

export const GET_SLACK_CONFIGURATION = 'GetSlackConfiguration';

export interface GetSlackConfiguration {
    _kind: typeof GET_SLACK_CONFIGURATION;
}

export type GetSlackConfigurationSuccess =
    NotAuthorizedToSlack | SlackConfiguration | SlackNotConfigured;

export type GetSlackConfigurationFailure = SecretNotFound | SlackApiCallFailed | UnexpectedFailure;

export type GetSlackConfigurationMessage =
    GetSlackConfigurationSuccess | GetSlackConfigurationFailure;

export const GET_SNOWFLAKE_AUTHORIZATION_URL = 'GetSnowflakeAuthorizationUrl';

export interface GetSnowflakeAuthorizationUrl {
    _kind: typeof GET_SNOWFLAKE_AUTHORIZATION_URL;
    referer: string;
}

export type GetSnowflakeAuthorizationUrlSuccess = SnowflakeAuthorizationUrl;

export type GetSnowflakeAuthorizationUrlFailure =
    SecretNotFound | ConnectionNotConfigured | DiscoveryFailure | UnexpectedFailure;

export type GetSnowflakeAuthorizationUrlMessage =
    GetSnowflakeAuthorizationUrlSuccess | GetSnowflakeAuthorizationUrlFailure;

export const GET_SNOWFLAKE_CONFIGURATION = 'GetSnowflakeConfiguration';

export interface GetSnowflakeConfiguration {
    _kind: typeof GET_SNOWFLAKE_CONFIGURATION;
}

export type GetSnowflakeConfigurationSuccess =
    SnowflakeConnection | SnowflakeMultiUserConfiguration | SnowflakeOAuth2Connection;

export type GetSnowflakeConfigurationFailure =
    SecretNotFound | ConnectionNotConfigured | UnexpectedFailure;

export type GetSnowflakeConfigurationMessage =
    GetSnowflakeConfigurationSuccess | GetSnowflakeConfigurationFailure;

export const GET_SNOWFLAKE_CONNECTION_DETAILS = 'GetSnowflakeConnectionDetails';

export interface GetSnowflakeConnectionDetails {
    _kind: typeof GET_SNOWFLAKE_CONNECTION_DETAILS;
}

export type GetSnowflakeConnectionDetailsSuccess = SnowflakeConnectionDetails;

export type GetSnowflakeConnectionDetailsFailure =
    | SnowflakeNotConfigured
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | Canceled
    | UnexpectedFailure;

export type GetSnowflakeConnectionDetailsMessage =
    GetSnowflakeConnectionDetailsSuccess | GetSnowflakeConnectionDetailsFailure;

export const GET_SNOWFLAKE_QUERY_HISTORY = 'GetSnowflakeQueryHistory';

export interface GetSnowflakeQueryHistory {
    _kind: typeof GET_SNOWFLAKE_QUERY_HISTORY;
}

export type GetSnowflakeQueryHistorySuccess = SnowflakeResult;

export type GetSnowflakeQueryHistoryFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type GetSnowflakeQueryHistoryMessage =
    GetSnowflakeQueryHistorySuccess | GetSnowflakeQueryHistoryFailure;

export const GET_SNOWFLAKE_QUERY_STATUS = 'GetSnowflakeQueryStatus';

export interface GetSnowflakeQueryStatus {
    _kind: typeof GET_SNOWFLAKE_QUERY_STATUS;
    id: string;
}

export type GetSnowflakeQueryStatusSuccess = SnowflakeQueryStatus;

export type GetSnowflakeQueryStatusFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type GetSnowflakeQueryStatusMessage =
    GetSnowflakeQueryStatusSuccess | GetSnowflakeQueryStatusFailure;

export const GET_SNOWFLAKE_RESULT = 'GetSnowflakeResult';

export interface GetSnowflakeResult {
    _kind: typeof GET_SNOWFLAKE_RESULT;
    id: string;
}

export type GetSnowflakeResultSuccess = SnowflakeResult;

export type GetSnowflakeResultFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type GetSnowflakeResultMessage = GetSnowflakeResultSuccess | GetSnowflakeResultFailure;

export const GET_SNOWFLAKE_SQL_BY_PROJECT_SQL = 'GetSnowflakeSqlByProjectSql';

export interface GetSnowflakeSqlByProjectSql {
    _kind: typeof GET_SNOWFLAKE_SQL_BY_PROJECT_SQL;
    limit?: number;
    sampleInputs: boolean;
    spaceUid?: string;
    sql: string;
}

export type GetSnowflakeSqlByProjectSqlSuccess = SnowflakeSql;

export type GetSnowflakeSqlByProjectSqlFailure =
    | InvalidSqlSyntax
    | EntityNotFoundOrNotAccessible
    | MissingTable
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | UnexpectedFailure;

export type GetSnowflakeSqlByProjectSqlMessage =
    GetSnowflakeSqlByProjectSqlSuccess | GetSnowflakeSqlByProjectSqlFailure;

export const GET_SNOWFLAKE_SQL_BY_TRANSFORMATION = 'GetSnowflakeSqlByTransformation';

export interface GetSnowflakeSqlByTransformation {
    _kind: typeof GET_SNOWFLAKE_SQL_BY_TRANSFORMATION;
    limit?: number;
    sampleInputs: boolean;
    transformation: Transformation;
    transformationId: ViewUuid;
}

export type GetSnowflakeSqlByTransformationSuccess = SnowflakeSql;

export type GetSnowflakeSqlByTransformationFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | EntityNotFoundOrNotAccessible
    | UnexpectedFailure;

export type GetSnowflakeSqlByTransformationMessage =
    GetSnowflakeSqlByTransformationSuccess | GetSnowflakeSqlByTransformationFailure;

export const GET_SNOWFLAKE_SQL_BY_TRANSFORMATION_ID = 'GetSnowflakeSqlByTransformationId';

export interface GetSnowflakeSqlByTransformationId {
    _kind: typeof GET_SNOWFLAKE_SQL_BY_TRANSFORMATION_ID;
    limit?: number;
    sampleInputs: boolean;
    transformationId: ViewUuid;
}

export type GetSnowflakeSqlByTransformationIdSuccess = SnowflakeSql;

export type GetSnowflakeSqlByTransformationIdFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | EntityNotFoundOrNotAccessible
    | UnexpectedFailure;

export type GetSnowflakeSqlByTransformationIdMessage =
    GetSnowflakeSqlByTransformationIdSuccess | GetSnowflakeSqlByTransformationIdFailure;

export const GET_SNOWFLAKE_SQL_FOR_OUTPUT_MAPPING = 'GetSnowflakeSqlForOutputMapping';

export interface GetSnowflakeSqlForOutputMapping {
    _kind: typeof GET_SNOWFLAKE_SQL_FOR_OUTPUT_MAPPING;
    limit?: number;
    mappings: Array<MappedOutputColumn>;
    uuid: string;
}

export type GetSnowflakeSqlForOutputMappingSuccess = DataSampleSql;

export type GetSnowflakeSqlForOutputMappingFailure =
    | CompilationFailedWithDatasetError
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | EntityNotFoundOrNotAccessible
    | UnexpectedFailure;

export type GetSnowflakeSqlForOutputMappingMessage =
    GetSnowflakeSqlForOutputMappingSuccess | GetSnowflakeSqlForOutputMappingFailure;

export const GET_SNOWFLAKE_STAGE_DETAILS = 'GetSnowflakeStageDetails';

export interface GetSnowflakeStageDetails {
    _kind: typeof GET_SNOWFLAKE_STAGE_DETAILS;
    environmentUid: string;
    stage: SnowflakeStageFqn;
}

export type GetSnowflakeStageDetailsSuccess = StageDetails;

export type GetSnowflakeStageDetailsFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type GetSnowflakeStageDetailsMessage =
    GetSnowflakeStageDetailsSuccess | GetSnowflakeStageDetailsFailure;

export const GET_SNOWFLAKE_STAGE_FILE_INFO = 'GetSnowflakeStageFileInfo';

export interface GetSnowflakeStageFileInfo {
    _kind: typeof GET_SNOWFLAKE_STAGE_FILE_INFO;
    environmentUid: string;
    fileFormat: SnowflakeFileFormatFqn;
    fileFormatParameters?: string;
    pattern?: string;
    rawDataSize: number;
    stage: SnowflakeStageFqn;
    targetSchema: SnowflakeSchemaFqn;
}

export type GetSnowflakeStageFileInfoSuccess = SnowflakeStageFileInfo;

export type GetSnowflakeStageFileInfoFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | NoSampleFilesFoundByPattern
    | FileFormatNotSupported
    | UnexpectedFailure;

export type GetSnowflakeStageFileInfoMessage =
    GetSnowflakeStageFileInfoSuccess | GetSnowflakeStageFileInfoFailure;

export const GET_SNOWFLAKE_TABLE_ACCESS = 'GetSnowflakeTableAccess';

export interface GetSnowflakeTableAccess {
    _kind: typeof GET_SNOWFLAKE_TABLE_ACCESS;
    database: string;
    schema: string;
    table: string;
}

export type GetSnowflakeTableAccessSuccess = Accessible | DeploymentDetails | NotAccessible;

export type GetSnowflakeTableAccessFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | SnowflakeObjectNotFoundOrNotAccessible
    | NoDeploymentHistory
    | CompilationFailedWithDatasetError
    | EntityNotFoundOrNotAccessible
    | UnexpectedFailure;

export type GetSnowflakeTableAccessMessage =
    GetSnowflakeTableAccessSuccess | GetSnowflakeTableAccessFailure;

export const GET_SNOWFLAKE_TABLE_CONNECTION_DETAILS = 'GetSnowflakeTableConnectionDetails';

export interface GetSnowflakeTableConnectionDetails {
    _kind: typeof GET_SNOWFLAKE_TABLE_CONNECTION_DETAILS;
    database: string;
    schema: string;
    table: string;
}

export type GetSnowflakeTableConnectionDetailsSuccess = ConnectionDetails;

export type GetSnowflakeTableConnectionDetailsFailure =
    SecretNotFound | NotAuthenticatedToSnowflake | SnowflakeNotConfigured | UnexpectedFailure;

export type GetSnowflakeTableConnectionDetailsMessage =
    GetSnowflakeTableConnectionDetailsSuccess | GetSnowflakeTableConnectionDetailsFailure;

export const GET_TABLE_DETAILS = 'GetTableDetails';

export interface GetTableDetails {
    _kind: typeof GET_TABLE_DETAILS;
    database: string;
    schema: string;
    table: string;
}

export type GetTableDetailsSuccess = TableDetails;

export type GetTableDetailsFailure = NoDeploymentHistory | UnexpectedFailure;

export type GetTableDetailsMessage = GetTableDetailsSuccess | GetTableDetailsFailure;

export const GET_TABLE_DETAILS_FOR_DATA_BROWSER = 'GetTableDetailsForDataBrowser';

export interface GetTableDetailsForDataBrowser {
    _kind: typeof GET_TABLE_DETAILS_FOR_DATA_BROWSER;
    tables: Array<SnowflakeTableFqn>;
}

export type GetTableDetailsForDataBrowserSuccess = TableDetailsForDataBrowser;

export type GetTableDetailsForDataBrowserFailure = UnexpectedFailure;

export type GetTableDetailsForDataBrowserMessage =
    GetTableDetailsForDataBrowserSuccess | GetTableDetailsForDataBrowserFailure;

export const GET_TRANSFORMATION_EMAIL_SCHEDULE = 'GetTransformationEmailSchedule';

export interface GetTransformationEmailSchedule {
    _kind: typeof GET_TRANSFORMATION_EMAIL_SCHEDULE;
    transformationId: string;
}

export type GetTransformationEmailScheduleSuccess = ShareNotFound | TransformationEmailSchedule;

export type GetTransformationEmailScheduleFailure = UnexpectedFailure;

export type GetTransformationEmailScheduleMessage =
    GetTransformationEmailScheduleSuccess | GetTransformationEmailScheduleFailure;

export type GithubAuth = GithubAuthPersonalToken;

export enum GithubAuthMethod {
    ANONYMOUS = 'ANONYMOUS',
    PERSONAL_TOKEN = 'PERSONAL_TOKEN'
}

export interface GithubAuthPersonalToken {
    token: string;
}

export const GITHUB_AUTHENTICATION_ERROR = 'GithubAuthenticationError';

export interface GithubAuthenticationError {
    _kind: typeof GITHUB_AUTHENTICATION_ERROR;
    message: string;
}

export const GITHUB_AUTHENTICATION_INFO = 'GithubAuthenticationInfo';

export interface GithubAuthenticationInfo {
    _kind: typeof GITHUB_AUTHENTICATION_INFO;
    authMethod: GithubAuthMethod;
}

export const GOOGLE_AUTHENTICATION_FAILED = 'GoogleAuthenticationFailed';

export interface GoogleAuthenticationFailed {
    _kind: typeof GOOGLE_AUTHENTICATION_FAILED;
    errorCode?: string;
    message: string;
}

export const GOOGLE_CREDENTIALS_INFO = 'GoogleCredentialsInfo';

export interface GoogleCredentialsInfo {
    _kind: typeof GOOGLE_CREDENTIALS_INFO;
    clientEmail: string;
    clientId: string;
    privateKeyId: string;
    projectId: string;
}

export const GOOGLE_CREDENTIALS_NOT_CONFIGURED = 'GoogleCredentialsNotConfigured';

export interface GoogleCredentialsNotConfigured {
    _kind: typeof GOOGLE_CREDENTIALS_NOT_CONFIGURED;
}

export const GOOGLE_SHEET_ALREADY_IN_USE = 'GoogleSheetAlreadyInUse';

export interface GoogleSheetAlreadyInUse {
    _kind: typeof GOOGLE_SHEET_ALREADY_IN_USE;
    name: string;
}

export const GOOGLE_SHEET_DOES_NOT_EXIST = 'GoogleSheetDoesNotExist';

export interface GoogleSheetDoesNotExist {
    _kind: typeof GOOGLE_SHEET_DOES_NOT_EXIST;
    sheetId: number;
    spreadsheetId: string;
}

export const GOOGLE_SHEET_WRITTEN = 'GoogleSheetWritten';

export interface GoogleSheetWritten {
    _kind: typeof GOOGLE_SHEET_WRITTEN;
    columns: Array<string>;
    rowCount: number;
}

export const GOOGLE_SHEETS_SHARE = 'GoogleSheetsShare';

export interface GoogleSheetsShare {
    _kind: typeof GOOGLE_SHEETS_SHARE;
    id: number;
    schedule: Scheduling;
    sheetId: number;
    spreadsheetId: string;
}

export const GOOGLE_SHEETS_SYNC_REQUESTED = 'GoogleSheetsSyncRequested';

export interface GoogleSheetsSyncRequested {
    _kind: typeof GOOGLE_SHEETS_SYNC_REQUESTED;
    shareId: number;
    sheetId: number;
    spreadsheetId: string;
    transformationId: string;
}

export interface GoogleSheetsTarget {
    sheetId: number;
    spreadsheetId: string;
}

export const GOOGLE_SPREADSHEET_NOT_ACCESSIBLE = 'GoogleSpreadsheetNotAccessible';

export interface GoogleSpreadsheetNotAccessible {
    _kind: typeof GOOGLE_SPREADSHEET_NOT_ACCESSIBLE;
    clientEmail: string;
    spreadsheetId: string;
}

export const GOOGLE_SPREADSHEET_NOT_FOUND = 'GoogleSpreadsheetNotFound';

export interface GoogleSpreadsheetNotFound {
    _kind: typeof GOOGLE_SPREADSHEET_NOT_FOUND;
    spreadsheetId: string;
}

export const GOOGLE_SPREADSHEET_NOT_WRITABLE = 'GoogleSpreadsheetNotWritable';

export interface GoogleSpreadsheetNotWritable {
    _kind: typeof GOOGLE_SPREADSHEET_NOT_WRITABLE;
    message: string;
    spreadsheetId: string;
}

export interface Group {
    children: Array<string>;
    description: string;
    id: string;
    name: string;
    slug: string;
}

export const GROUP_CREATED = 'GroupCreated';

export interface GroupCreated {
    _kind: typeof GROUP_CREATED;
    group: Group;
    spaceUid: string;
}

export const GROUP_DELETED = 'GroupDeleted';

export interface GroupDeleted {
    _kind: typeof GROUP_DELETED;
    children: Array<string>;
    groupUid: string;
    spaceUid: string;
}

export const GROUP_DESCRIPTION_UPDATED = 'GroupDescriptionUpdated';

export interface GroupDescriptionUpdated {
    _kind: typeof GROUP_DESCRIPTION_UPDATED;
    groupUid: string;
    newDescription: string;
    spaceUid: string;
}

export const GROUP_NAME_UPDATED = 'GroupNameUpdated';

export interface GroupNameUpdated {
    _kind: typeof GROUP_NAME_UPDATED;
    groupUid: string;
    newName: string;
    newSlug: string;
    previousName: string;
    spaceUid: string;
}

export const GROUP_NOT_FOUND = 'GroupNotFound';

export interface GroupNotFound {
    _kind: typeof GROUP_NOT_FOUND;
    groupUid: string;
    spaceUid: string;
}

export interface Heartbeat {}

export interface HighestFailureRateRanking {
    dataType: JobMetricsDataType.PERCENT;
    entries: Array<JobRanking>;
    name: JobMetricsName.HighestFailureRateRanking;
    type: JobMetricsType.TOP_N;
}

export type ISO8601Duration = string;

export interface IllegalAlias {
    columnName: string;
    message: string;
}

export const IMPORT_DATASETS = 'ImportDatasets';

export interface ImportDatasets {
    _kind: typeof IMPORT_DATASETS;
    objectsToImport: Array<SnowflakeTableFqn>;
    spaceUid: string;
}

export type ImportDatasetsSuccess = ImportOfDatasetsStarted;

export type ImportDatasetsProgress = DataSourceImported;

export type ImportDatasetsFailure = UnexpectedFailure;

export type ImportDatasetsMessage =
    ImportDatasetsSuccess | ImportDatasetsProgress | ImportDatasetsFailure;

export const IMPORT_OF_DATASETS_STARTED = 'ImportOfDatasetsStarted';

export interface ImportOfDatasetsStarted {
    _kind: typeof IMPORT_OF_DATASETS_STARTED;
    identifiers: Array<SnowflakeTableFqn>;
    spaceUid: string;
}

export const IMPORT_TABLE_TO_NEW_PROJECT = 'ImportTableToNewProject';

export interface ImportTableToNewProject {
    _kind: typeof IMPORT_TABLE_TO_NEW_PROJECT;
    addressToImport: SnowflakeTableFqn;
    explorationsToCopy: Array<string>;
    name: string;
}

export type ImportTableToNewProjectSuccess = TableImportedToNewProject;

export type ImportTableToNewProjectFailure =
    | DerivedAssetMustNotBeModified
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | ViewAlreadyExists
    | UnexpectedFailure;

export type ImportTableToNewProjectMessage =
    ImportTableToNewProjectSuccess | ImportTableToNewProjectFailure;

export const IMPORT_TABLE_TO_PROJECT = 'ImportTableToProject';

export interface ImportTableToProject {
    _kind: typeof IMPORT_TABLE_TO_PROJECT;
    addressToImport: SnowflakeTableFqn;
    explorationsToCopy: Array<string>;
    targetSpaceUid: string;
}

export type ImportTableToProjectSuccess = TableImportedToProject;

export type ImportTableToProjectFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | ViewAlreadyExists
    | UnexpectedFailure;

export type ImportTableToProjectMessage = ImportTableToProjectSuccess | ImportTableToProjectFailure;

export const INITIATE_ANALYZE_SCHEMA_USAGE = 'InitiateAnalyzeSchemaUsage';

export interface InitiateAnalyzeSchemaUsage {
    _kind: typeof INITIATE_ANALYZE_SCHEMA_USAGE;
    explorations: Array<CannedExploration>;
    fromTableFunction: boolean;
    projectToImportTo?: string;
    resultTable: SnowflakeTableFqn;
    schemaToAnalyze: SnowflakeSchemaFqn;
}

export type InitiateAnalyzeSchemaUsageSuccess = UsagesTableCreated | NoUsagesFound;

export type InitiateAnalyzeSchemaUsageProgress = AnalyzeSchemaUsageInitiated;

export type InitiateAnalyzeSchemaUsageFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type InitiateAnalyzeSchemaUsageMessage =
    | InitiateAnalyzeSchemaUsageSuccess
    | InitiateAnalyzeSchemaUsageProgress
    | InitiateAnalyzeSchemaUsageFailure;

export const INITIATE_EXPLORATION_DOWNLOAD = 'InitiateExplorationDownload';

export interface InitiateExplorationDownload {
    _kind: typeof INITIATE_EXPLORATION_DOWNLOAD;
    fqn?: SnowflakeTableFqn;
    spaceUid?: string;
    sql: string;
}

export type InitiateExplorationDownloadSuccess = ExplorationDownloadPrepared;

export type InitiateExplorationDownloadProgress = ExplorationDownloadInitiated;

export type InitiateExplorationDownloadFailure =
    EntityNotFoundOrNotAccessible | InvalidSqlSyntax | MissingTable | UnexpectedFailure;

export type InitiateExplorationDownloadMessage =
    | InitiateExplorationDownloadSuccess
    | InitiateExplorationDownloadProgress
    | InitiateExplorationDownloadFailure;

export const INITIATE_PREPARE_DOWNLOAD = 'InitiatePrepareDownload';

export interface InitiatePrepareDownload {
    _kind: typeof INITIATE_PREPARE_DOWNLOAD;
    viewUuid: ViewUuid;
}

export type InitiatePrepareDownloadSuccess = DownloadPrepared;

export type InitiatePrepareDownloadProgress = PreparedDownloadInitiated;

export type InitiatePrepareDownloadFailure = EntityNotFoundOrNotAccessible | UnexpectedFailure;

export type InitiatePrepareDownloadMessage =
    | InitiatePrepareDownloadSuccess
    | InitiatePrepareDownloadProgress
    | InitiatePrepareDownloadFailure;

export interface InputColumn extends ColumnInput {
    name: string;
    type: string;
}

export interface InputDataset {
    columns: Array<ColumnInput>;
    viewUid: string;
}
export type Instant = string;

export const INSUFFICIENT_PRIVILEGES = 'InsufficientPrivileges';

export interface InsufficientPrivileges {
    _kind: typeof INSUFFICIENT_PRIVILEGES;
    message: string;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const INSUFFICIENT_TASK_PRIVILEGES = 'InsufficientTaskPrivileges';

export interface InsufficientTaskPrivileges {
    _kind: typeof INSUFFICIENT_TASK_PRIVILEGES;
    message: string;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const INVALID_ACCESS_TOKEN = 'InvalidAccessToken';

export interface InvalidAccessToken {
    _kind: typeof INVALID_ACCESS_TOKEN;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const INVALID_ACCOUNT_NAME = 'InvalidAccountName';

export interface InvalidAccountName {
    _kind: typeof INVALID_ACCOUNT_NAME;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export interface InvalidExpression {
    cause: string;
    message: string;
}

export const INVALID_IDENTIFIER = 'InvalidIdentifier';

export interface InvalidIdentifier {
    _kind: typeof INVALID_IDENTIFIER;
    column: string;
    errorPosition: ErrorPosition;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const INVALID_PARAMETER_TYPE_FOR_FUNCTION = 'InvalidParameterTypeForFunction';

export interface InvalidParameterTypeForFunction {
    _kind: typeof INVALID_PARAMETER_TYPE_FOR_FUNCTION;
    function: string;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    parameterValue: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const INVALID_SLACK_CHANNEL_NAME = 'InvalidSlackChannelName';

export interface InvalidSlackChannelName {
    _kind: typeof INVALID_SLACK_CHANNEL_NAME;
}

export const INVALID_SQL_SYNTAX = 'InvalidSqlSyntax';

export interface InvalidSqlSyntax {
    _kind: typeof INVALID_SQL_SYNTAX;
    errorPosition: ErrorPosition;
    index?: number;
    message: string;
    origins: Array<DatasetUid>;
    sql: string;
}

export const JOB_ACTIVITY_METRICS = 'JobActivityMetrics';

export interface JobActivityMetrics {
    _kind: typeof JOB_ACTIVITY_METRICS;
    entries: Record<JobMetricsName, JobMetrics>;
}

export type JobCheck = DataFreshnessCheck | ModelQualityJobCheck | PreMaterializationQualityCheck;

export interface JobCheckNotMet {}

export const JOB_DEFINITION_CREATED = 'JobDefinitionCreated';

export interface JobDefinitionCreated {
    _kind: typeof JOB_DEFINITION_CREATED;
    uid: string;
    isJobRunRequired: boolean;
}

export enum JobEventTriggerType {
    JOB_RUN_COMPLETED = 'europa.job.management.JobRunCompletedTrigger',
    TABLE_MATERIALIZATION = 'europa.job.management.TableMaterializedTrigger',
    JOB_RUN_COMPLETED_ENTRY = 'europa.job.management.JobRunCompletedTriggerEntry',
    TABLE_MATERIALIZATION_ENTRY = 'europa.job.management.TableMaterializedTriggerEntry',
    JOB_RUN_COMPLETED_TABLE_MATERIALIZATION_TRIGGER = 'europa.project.output.JobRunCompletedTableMaterializationTrigger',
    TABLE_MATERIALIZED_TABLE_MATERIALIZATION_TRIGGER = 'europa.project.output.TableMaterializedTableMaterializationTrigger'
}

interface TableMaterializationTrigger {
    _type:
        JobEventTriggerType.TABLE_MATERIALIZATION_ENTRY | JobEventTriggerType.TABLE_MATERIALIZATION;
    name: string;
    table: SnowflakeTableFqn;
    viewUid: string;
}

interface JobRunCompletedTrigger {
    _type: JobEventTriggerType.JOB_RUN_COMPLETED_ENTRY | JobEventTriggerType.JOB_RUN_COMPLETED;
    name: string;
    uid: string;
}

export type EventDrivenTrigger = TableMaterializationTrigger | JobRunCompletedTrigger;

export interface JobOperationDetails {
    _type: string;
    targetTable?: SnowflakeTableFqn | string;
    tagId?: number;
    columns?: Array<string>;
    sourceTableMappings?: Array<SourceTableMapping>;
    targetType?: typeof TargetType;
}

export const JOB_DEFINITION_DETAILED_ENTRY = 'JobDefinitionDetailedEntry';

export interface JobDefinitionDetailedEntry {
    _kind: typeof JOB_DEFINITION_DETAILED_ENTRY;
    checks: Array<JobCheck>;
    createdAt: Instant;
    createdBy: string;
    description?: string;
    environmentRef: JobEnvironmentRef;
    lastModifiedAt: Instant;
    lastModifiedBy: string;
    lastRunId?: number;
    lastRunUid?: string;
    modelRef?: ModelRef;
    name: string;
    operation: JobOperationDetails;
    projectRef?: ProjectRef;
    targetTable?: SnowflakeTableFqn;
    targetTableFqn?: string;
    scheduledTrigger?: JobTrigger;
    uid: string;
    viewRef?: ViewRef;
    eventDrivenTriggers: Array<EventDrivenTrigger>;
}

export interface JobDefinitionRef {
    isDeleted: boolean;
    name: string;
    uid: string;
}

export const JOB_DEFINITION_UPDATED = 'JobDefinitionUpdated';

export interface JobDefinitionUpdated {
    _kind: typeof JOB_DEFINITION_UPDATED;
    isJobRunRequired: boolean;
}

export const JOB_DEFINITIONS = 'JobDefinitions';

export interface JobDefinitions {
    _kind: typeof JOB_DEFINITIONS;
    entries: Array<JobDefinitionEntry>;
}

export const JOB_ENVIRONMENT_CREATED = 'JobEnvironmentCreated';

export interface JobEnvironmentCreated {
    _kind: typeof JOB_ENVIRONMENT_CREATED;
    uid: string;
}

export interface JobEnvironmentEntry {
    createdAt: Instant;
    createdBy: string;
    database?: string;
    description?: string;
    lastModifiedAt: Instant;
    lastModifiedBy: string;
    name: string;
    role: string;
    schema?: string;
    slackChannelId?: string;
    snowflakeServiceAccountName?: string;
    snowflakeServiceAccountUid?: string;
    uid: string;
    username: string;
    warehouse: string;
}

export interface JobEnvironmentRef {
    isDeleted: boolean;
    name: string;
    uid: string;
}

export const JOB_ENVIRONMENT_UPDATED = 'JobEnvironmentUpdated';

export interface JobEnvironmentUpdated {
    _kind: typeof JOB_ENVIRONMENT_UPDATED;
}

export const JOB_ENVIRONMENTS = 'JobEnvironments';

export interface JobEnvironments {
    _kind: typeof JOB_ENVIRONMENTS;
    entries: Array<JobEnvironmentEntry>;
}

export interface JobInfoRef {
    jobDefRef: JobDefinitionRef;
    jobEnvRef: JobEnvironmentRef;
    scheduling?: Scheduling;
}

export type JobMetrics =
    | HighestFailureRateRanking
    | JobRunCount
    | JobRunFailureCount
    | LongestRuntimeRanking
    | MedianDurationMillis
    | MostJobRunsRanking
    | MostRecordsProducedRanking
    | RecordsProducedSum;

export enum JobMetricsDataType {
    MILLISECONDS = 'MILLISECONDS',
    NUMBER = 'NUMBER',
    PERCENT = 'PERCENT'
}

export enum JobMetricsName {
    HighestFailureRateRanking = 'HighestFailureRateRanking',
    JobRunCount = 'JobRunCount',
    JobRunFailureCount = 'JobRunFailureCount',
    LongestRuntimeRanking = 'LongestRuntimeRanking',
    MedianDurationMillis = 'MedianDurationMillis',
    MostJobRunsRanking = 'MostJobRunsRanking',
    MostRecordsProducedRanking = 'MostRecordsProducedRanking',
    RecordsProducedSum = 'RecordsProducedSum'
}

export enum JobMetricsType {
    AGGREGATE = 'AGGREGATE',
    TOP_N = 'TOP_N'
}

export const JOB_OPERATION_COUNTS = 'JobOperationCounts';

export interface JobOperationCounts {
    _kind: typeof JOB_OPERATION_COUNTS;
    counts: Record<string, number>;
}

export interface JobRanking {
    currentValue?: Number;
    definitionRef: JobDefinitionRef;
    previousValue?: Number;
}

export const JOB_RUN_AUDIT_RESULT = 'JobRunAuditResult';

export interface JobRunAuditResult extends AuditResult {
    _kind: typeof JOB_RUN_AUDIT_RESULT;
    executionMetadata: object;
    modifiedTables: Array<ModifiedTableEntity>;
    queries: Array<AuditedQuery>;
    storedProceduresCalled: Array<StoredProcedureEntity>;
    usedTables: Array<UsedTableEntity>;
}

export const JOB_RUN_CANCEL_REQUESTED = 'JobRunCancelRequested';

export interface JobRunCancelRequested {
    _kind: typeof JOB_RUN_CANCEL_REQUESTED;
    statusUrl: string;
}

export const JOB_RUN_CHECK_FAILED = 'JobRunCheckFailed';

export interface JobRunCheckFailed extends JobStateChangeEvent {
    _kind: typeof JOB_RUN_CHECK_FAILED;
    completedAt?: Instant;
    definitionUid: string;
    environmentUid: string;
    executionMetadata: object;
    internalError: object;
    modelRef?: ModelRef;
    name: string;
    operationId?: number;
    projectRef?: ProjectRef;
    role: string;
    runStatus: JobRunStatus;
    runUid: string;
    submittedAt: Instant;
    submittedBy: string;
    targetTable?: SnowflakeTableFqn;
    trigger: JobRunTrigger;
    urlPrefix: string;
    warehouse: string;
    warnings: Array<JobRunWarning>;
}

export const JOB_RUN_COMPLETED = 'JobRunCompleted';

export interface JobRunCompleted extends JobStateChangeEvent {
    _kind: typeof JOB_RUN_COMPLETED;
    completedAt?: Instant;
    definitionUid: string;
    environmentUid: string;
    executionMetadata: object;
    internalError: object;
    modelRef?: ModelRef;
    name: string;
    operationId?: number;
    projectRef?: ProjectRef;
    role: string;
    runStatus: JobRunStatus;
    runUid: string;
    submittedAt: Instant;
    submittedBy: string;
    targetTable?: SnowflakeTableFqn;
    trigger: JobRunTrigger;
    urlPrefix: string;
    warehouse: string;
    warnings: Array<JobRunWarning>;
}

export interface JobRunCount {
    currentValue?: Number;
    dataType: JobMetricsDataType.NUMBER;
    name: JobMetricsName.JobRunCount;
    previousValue?: Number;
    type: JobMetricsType.AGGREGATE;
}

export const JOB_RUN_ENTRY = 'JobRunEntry';

export interface JobRunEntry {
    _kind: typeof JOB_RUN_ENTRY;
    completedAt?: Instant;
    completionExpectedAt?: Instant;
    definitionRef: JobDefinitionRef;
    deployedNodeRef?: ViewRef;
    duration?: Duration;
    environmentRef: JobEnvironmentRef;
    executionMetadata?: Record<string, unknown>;
    internalError?: Record<string, unknown>;
    jobRunStatus: JobRunStatus;
    modelRef?: ModelRef;
    operationName: string;
    projectRef?: ProjectRef;
    role: string;
    snowflakeUsername: string;
    submittedAt: Instant;
    submittedBy: string;
    trigger: JobRunTrigger;
    uid: string;
    warehouse: string;
    warnings: Array<JobRunWarning>;
}

export interface JobRunFailureCount {
    currentValue?: Number;
    dataType: JobMetricsDataType.NUMBER;
    name: JobMetricsName.JobRunFailureCount;
    previousValue?: Number;
    type: JobMetricsType.AGGREGATE;
}

export const JOB_RUN_FILTER_OPTIONS = 'JobRunFilterOptions';

export interface JobRunFilterOptions {
    _kind: typeof JOB_RUN_FILTER_OPTIONS;
    environments: Record<string, string>;
    jobs: Record<string, string>;
    projects: Record<string, string>;
    roles: Array<string>;
    statuses: Array<JobRunStatus>;
    warehouses: Array<string>;
}

export const JOB_RUN_NOT_CANCELABLE = 'JobRunNotCancelable';

export interface JobRunNotCancelable {
    _kind: typeof JOB_RUN_NOT_CANCELABLE;
    runStatus: JobRunStatus;
}

export const JOB_RUN_NOT_FOUND = 'JobRunNotFound';

export interface JobRunNotFound {
    _kind: typeof JOB_RUN_NOT_FOUND;
}

export interface JobRunRequest {
    definitionId: number;
    skipChecks: boolean;
}

export const JOB_RUN_RESULT = 'JobRunResult';

export interface JobRunResult {
    _kind: typeof JOB_RUN_RESULT;
    executionMetadata: Record<string, unknown>;
    warnings: Array<JobRunWarning>;
}

export const JOB_RUN_SKIPPED = 'JobRunSkipped';

export interface JobRunSkipped extends JobStateChangeEvent {
    _kind: typeof JOB_RUN_SKIPPED;
    completedAt?: Instant;
    definitionUid: string;
    environmentUid: string;
    executionMetadata: object;
    internalError: object;
    modelRef?: ModelRef;
    name: string;
    operationId?: number;
    projectRef?: ProjectRef;
    role: string;
    runStatus: JobRunStatus;
    runUid: string;
    submittedAt: Instant;
    submittedBy: string;
    targetTable?: SnowflakeTableFqn;
    trigger: JobRunTrigger;
    urlPrefix: string;
    warehouse: string;
    warnings: Array<JobRunWarning>;
}

export enum JobRunStatus {
    CANCELED = 'CANCELED',
    CANCELING = 'CANCELING',
    FAILED = 'FAILED',
    RUNNING = 'RUNNING',
    SCHEDULED = 'SCHEDULED',
    SKIPPED = 'SKIPPED',
    SUCCEEDED = 'SUCCEEDED',
    SUCCEEDED_WITH_WARNINGS = 'SUCCEEDED_WITH_WARNINGS'
}

export interface JobRunSummaryEntry {
    definitionName: string;
    definitionUid: string;
    duration?: Duration;
    environmentName: string;
    jobRunStatus: JobRunStatus;
    modelRef?: ModelRef;
    projectRef?: ProjectRef;
    submittedAt: Instant;
    uid: string;
}

export const JOB_RUN_TIMELINE = 'JobRunTimeline';

export interface JobRunTimeline {
    _kind: typeof JOB_RUN_TIMELINE;
    entries: Array<JobRunTimelineEntry>;
}

export interface JobRunTimelineEntry {
    completedAt?: Instant;
    completionExpectedAt?: Instant;
    definitionUid: string;
    durationIsUnknown: boolean;
    entryId: string;
    name: string;
    path: Array<string>;
    runUid?: string;
    status?: JobRunStatus;
    submissionExpectedAt?: Instant;
    submittedAt?: Instant;
}

export type JobRunTrigger =
    ApiKeyJobRunTrigger | DataDrivenJobRunTrigger | ManualJobRunTrigger | ScheduledJobRunTrigger;

export enum JobRunTriggerType {
    API_KEY = 'API_KEY',
    DATA_DRIVEN = 'DATA_DRIVEN',
    EVENT_DRIVEN = 'EVENT_DRIVEN',
    MANUAL = 'MANUAL',
    SCHEDULED = 'SCHEDULED'
}

export const JOB_RUN_TRIGGERED = 'JobRunTriggered';

export interface JobRunTriggered extends JobStateChangeEvent {
    _kind: typeof JOB_RUN_TRIGGERED;
    completedAt?: Instant;
    definitionUid: string;
    environmentUid: string;
    executionMetadata: object;
    internalError: object;
    modelRef?: ModelRef;
    name: string;
    operationId?: number;
    projectRef?: ProjectRef;
    role: string;
    runStatus: JobRunStatus;
    runUid: string;
    submittedAt: Instant;
    submittedBy: string;
    targetTable?: SnowflakeTableFqn;
    trigger: JobRunTrigger;
    urlPrefix: string;
    warehouse: string;
    warnings: Array<JobRunWarning>;
}

export type JobRunWarning = NoRowsProduced;

export const JOB_RUNS = 'JobRuns';

export interface JobRuns {
    _kind: typeof JOB_RUNS;
    entries: Array<JobRunSummaryEntry>;
    totalCount: number;
}

export const JOB_RUNS_TRIGGERED = 'JobRunsTriggered';

export interface JobRunsTriggered {
    _kind: typeof JOB_RUNS_TRIGGERED;
    failed: Array<number>;
    skipped: Record<number, number>;
    submitted: Record<number, number>;
}

export type JobStateChangeEvent = object;

export type JobTrigger = ScheduleDrivenJobTrigger;

export interface JobTriggerCandidate {
    definitionRef: JobDefinitionRef;
    description?: string;
    lastRunAt?: Instant;
    managedTables: Array<SnowflakeTableFqn>;
    trigger?: JobTrigger;
}

export const JOB_TRIGGER_CANDIDATES = 'JobTriggerCandidates';

export interface JobTriggerCandidates {
    _kind: typeof JOB_TRIGGER_CANDIDATES;
    candidates: Array<JobTriggerCandidate>;
    unmanagedTables: Array<ProjectTableRef>;
}

export enum JobTriggerType {
    DATA_DRIVEN = 'DATA_DRIVEN',
    SCHEDULED = 'SCHEDULED'
}

export enum JobValidationMessage {
    FRESHNESS_CHECK_TABLE_MISSING = 'FRESHNESS_CHECK_TABLE_MISSING',
    INCREMENTAL_REFRESH_KEY_COLUMN_MISSING = 'INCREMENTAL_REFRESH_KEY_COLUMN_MISSING',
    SOURCE_TABLE_MAPPING_TABLE_MISSING = 'SOURCE_TABLE_MAPPING_TABLE_MISSING',
    TRIGGER_SOURCE_JOB_DELETED = 'TRIGGER_SOURCE_JOB_DELETED'
}

export enum JobValidationStatus {
    BROKEN = 'BROKEN',
    GOOD = 'GOOD',
    POTENTIALLY_BROKEN = 'POTENTIALLY_BROKEN',
    STALE = 'STALE'
}

export interface Join {
    criteria: Array<JoinCriterium>;
    dedupeJoinKeys: boolean;
    joinType: JoinType;
    leftSourceView: string;
    rightSourceView: string;
}

export interface JoinCriterium {
    leftColumn: string;
    rightColumn: string;
}

export enum JoinType {
    INNER = 'INNER',
    LEFT_OUTER = 'LEFT_OUTER',
    RIGHT_OUTER = 'RIGHT_OUTER',
    FULL_OUTER = 'FULL_OUTER'
}

export interface KurtosisAggregation {
    sourceColumnName?: string;
}

export enum LastExecutionStatus {
    ERROR = 'ERROR',
    SUCCESSFUL = 'SUCCESSFUL'
}

export interface LastJobRun {
    _kind: 'LastJobRun';
    completedAt?: Instant;
    duration?: Duration;
    runStatus: JobRunStatus;
    runId: number;
    runUid: string;
    submittedAt: Instant;
}

export interface LastJobRunNotFound {
    _kind: 'LastJobRunNotFound';
}

export interface LatestTag {
    id: number;
    message?: string;
    name: string;
    projectVersion: number;
}

export const LIST_API_KEYS = 'ListApiKeys';

export interface ListApiKeys {
    _kind: typeof LIST_API_KEYS;
}

export type ListApiKeysSuccess = ApiKeyEntries;

export type ListApiKeysFailure = UnexpectedFailure;

export type ListApiKeysMessage = ListApiKeysSuccess | ListApiKeysFailure;

export const LIST_DAP_MIGRATIONS = 'ListDapMigrations';

export interface ListDapMigrations {
    _kind: typeof LIST_DAP_MIGRATIONS;
}

export type ListDapMigrationsSuccess = DapMigrations;

export type ListDapMigrationsFailure = UnexpectedFailure;

export type ListDapMigrationsMessage = ListDapMigrationsSuccess | ListDapMigrationsFailure;

export const LIST_DATAMEER_CHANNELS = 'ListDatameerChannels';

export interface ListDatameerChannels {
    _kind: typeof LIST_DATAMEER_CHANNELS;
}

export type ListDatameerChannelsSuccess = DatameerChannels;

export type ListDatameerChannelsFailure = UnexpectedFailure;

export type ListDatameerChannelsMessage = ListDatameerChannelsSuccess | ListDatameerChannelsFailure;

export interface ListEventSubscriptionResponse {
    deliveryTarget: EventDeliveryTarget;
    eventFilters: Record<string, string>;
    eventId: string;
    id: number;
    name: string;
}

export const LIST_EVENT_SUBSCRIPTIONS = 'ListEventSubscriptions';

export interface ListEventSubscriptions {
    _kind: typeof LIST_EVENT_SUBSCRIPTIONS;
}

export type ListEventSubscriptionsSuccess = EventSubscriptions;

export type ListEventSubscriptionsFailure = UnexpectedFailure;

export type ListEventSubscriptionsMessage =
    ListEventSubscriptionsSuccess | ListEventSubscriptionsFailure;

export const LIST_EXPLORATIONS = 'ListExplorations';

export interface ListExplorations {
    _kind: typeof LIST_EXPLORATIONS;
    tableFqn?: SnowflakeTableFqn;
    transformationId?: string;
}

export type ListExplorationsSuccess = Explorations;

export type ListExplorationsFailure = UnexpectedFailure;

export type ListExplorationsMessage = ListExplorationsSuccess | ListExplorationsFailure;

export const LIST_JOB_DEFINITIONS = 'ListJobDefinitions';

export interface ListJobDefinitions {
    _kind: typeof LIST_JOB_DEFINITIONS;
}

export type ListJobDefinitionsSuccess = JobDefinitions;

export type ListJobDefinitionsFailure = UnexpectedFailure;

export type ListJobDefinitionsMessage = ListJobDefinitionsSuccess | ListJobDefinitionsFailure;

export const LIST_JOB_ENVIRONMENTS = 'ListJobEnvironments';

export interface ListJobEnvironments {
    _kind: typeof LIST_JOB_ENVIRONMENTS;
}

export type ListJobEnvironmentsSuccess = JobEnvironments;

export type ListJobEnvironmentsFailure = UnexpectedFailure;

export type ListJobEnvironmentsMessage = ListJobEnvironmentsSuccess | ListJobEnvironmentsFailure;

export const LIST_JOB_RUNS = 'ListJobRuns';

export interface ListJobRuns {
    _kind: typeof LIST_JOB_RUNS;
    definitionUids: Array<string>;
    environmentUids: Array<string>;
    limit: number;
    offset: number;
    period: Period;
    projectUids: Array<string>;
    roles: Array<string>;
    sortingColumn: string;
    sortingOrder: SortingOrder;
    statuses: Array<JobRunStatus>;
    warehouses: Array<string>;
}

export type ListJobRunsSuccess = JobRuns;

export type ListJobRunsFailure = EntityNotFoundOrNotAccessible | UnexpectedFailure;

export type ListJobRunsMessage = ListJobRunsSuccess | ListJobRunsFailure;

export const LIST_MODEL_TAGS = 'ListModelTags';

export interface ListModelTags {
    _kind: typeof LIST_MODEL_TAGS;
    modelId?: number;
    projectId?: number;
}

export type ListModelTagsSuccess = ModelTags;

export type ListModelTagsFailure = UnexpectedFailure;

export type ListModelTagsMessage = ListModelTagsSuccess | ListModelTagsFailure;

export const LIST_MODELS = 'ListModels';

export interface ListModels {
    _kind: typeof LIST_MODELS;
    modelId?: number;
    projectId?: number;
}

export type ListModelsSuccess = Models;

export type ListModelsFailure = UnexpectedFailure;

export type ListModelsMessage = ListModelsSuccess | ListModelsFailure;

export const LIST_ROLES_AND_WAREHOUSES = 'ListRolesAndWarehouses';

export interface ListRolesAndWarehouses {
    _kind: typeof LIST_ROLES_AND_WAREHOUSES;
    credentials: Record<string, string>;
    role?: string;
}

export type ListRolesAndWarehousesSuccess = RolesAndWarehouses;

export type ListRolesAndWarehousesFailure =
    | SecretNotFound
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | UnexpectedFailure;

export type ListRolesAndWarehousesMessage =
    ListRolesAndWarehousesSuccess | ListRolesAndWarehousesFailure;

export const LIST_SHARED_OUTPUTS = 'ListSharedOutputs';

export interface ListSharedOutputs {
    _kind: typeof LIST_SHARED_OUTPUTS;
    spaceUid: string;
}

export type ListSharedOutputsSuccess = SharedOutputs;

export type ListSharedOutputsFailure = UnexpectedFailure;

export type ListSharedOutputsMessage = ListSharedOutputsSuccess | ListSharedOutputsFailure;

export const LIST_SLACK_CHANNELS = 'ListSlackChannels';

export interface ListSlackChannels {
    _kind: typeof LIST_SLACK_CHANNELS;
}

export type ListSlackChannelsSuccess = SlackChannels;

export type ListSlackChannelsFailure = SecretNotFound | SlackApiCallFailed | UnexpectedFailure;

export type ListSlackChannelsMessage = ListSlackChannelsSuccess | ListSlackChannelsFailure;

export const LIST_SNOWFLAKE_FILE_FORMATS = 'ListSnowflakeFileFormats';

export interface ListSnowflakeFileFormats {
    _kind: typeof LIST_SNOWFLAKE_FILE_FORMATS;
    environmentUid: string;
    supportedForUnloading: boolean;
}

export type ListSnowflakeFileFormatsSuccess = SnowflakeFileFormats;

export type ListSnowflakeFileFormatsFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ListSnowflakeFileFormatsMessage =
    ListSnowflakeFileFormatsSuccess | ListSnowflakeFileFormatsFailure;

export const LIST_SNOWFLAKE_QUERIES = 'ListSnowflakeQueries';

export interface ListSnowflakeQueries {
    _kind: typeof LIST_SNOWFLAKE_QUERIES;
}

export type ListSnowflakeQueriesSuccess = SnowflakeQueries;

export type ListSnowflakeQueriesFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ListSnowflakeQueriesMessage = ListSnowflakeQueriesSuccess | ListSnowflakeQueriesFailure;

export const LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS = 'ListSnowflakeServiceAccountUsers';

export interface ListSnowflakeServiceAccountUsers {
    _kind: typeof LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS;
}

export type ListSnowflakeServiceAccountUsersSuccess = SnowflakeServiceAccountUsers;

export type ListSnowflakeServiceAccountUsersFailure = SecretNotFound | UnexpectedFailure;

export type ListSnowflakeServiceAccountUsersMessage =
    ListSnowflakeServiceAccountUsersSuccess | ListSnowflakeServiceAccountUsersFailure;

export const LIST_SNOWFLAKE_STAGE_FILES = 'ListSnowflakeStageFiles';

export interface ListSnowflakeStageFiles {
    _kind: typeof LIST_SNOWFLAKE_STAGE_FILES;
    environmentUid: string;
    pattern?: string;
    stage: SnowflakeStageFqn;
}

export type ListSnowflakeStageFilesSuccess = StageFiles;

export type ListSnowflakeStageFilesFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ListSnowflakeStageFilesMessage =
    ListSnowflakeStageFilesSuccess | ListSnowflakeStageFilesFailure;

export const LIST_SNOWFLAKE_STAGES = 'ListSnowflakeStages';

export interface ListSnowflakeStages {
    _kind: typeof LIST_SNOWFLAKE_STAGES;
    environmentUid: string;
}

export type ListSnowflakeStagesSuccess = SnowflakeStages;

export type ListSnowflakeStagesFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ListSnowflakeStagesMessage = ListSnowflakeStagesSuccess | ListSnowflakeStagesFailure;

export const LIST_SNOWFLAKE_STORED_PROCEDURES = 'ListSnowflakeStoredProcedures';

export interface ListSnowflakeStoredProcedures {
    _kind: typeof LIST_SNOWFLAKE_STORED_PROCEDURES;
    database: string;
    environmentUid: string;
}

export type ListSnowflakeStoredProceduresSuccess = SnowflakeStoredProcedures;

export type ListSnowflakeStoredProceduresFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ListSnowflakeStoredProceduresMessage =
    ListSnowflakeStoredProceduresSuccess | ListSnowflakeStoredProceduresFailure;

export const LIST_SNOWFLAKE_WAREHOUSES = 'ListSnowflakeWarehouses';

export interface ListSnowflakeWarehouses {
    _kind: typeof LIST_SNOWFLAKE_WAREHOUSES;
    role?: string;
}

export type ListSnowflakeWarehousesSuccess = WarehouseList;

export type ListSnowflakeWarehousesFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type ListSnowflakeWarehousesMessage =
    ListSnowflakeWarehousesSuccess | ListSnowflakeWarehousesFailure;

export interface LoadJobTargetColumnMapping extends MappedOutputColumn {
    alias?: string;
    excluded: boolean;
    name: string;
    notNull: boolean;
    position?: number;
    sourceOrdinalIndex: number;
    typeFamily?: TypeFamily;
}

export const LOAD_SNOWFLAKE_TABLE = 'LoadSnowflakeTable';

export interface LoadSnowflakeTable {
    _kind: typeof LOAD_SNOWFLAKE_TABLE;
    abortJobOnFileFormatChanges: boolean;
    copyOptions: string;
    fileFormat: SnowflakeFileFormatFqn;
    fileFormatParameters: string;
    fileFormatPropertiesHistory: FileFormatProperties;
    mappings: Array<LoadJobTargetColumnMapping>;
    pattern?: string;
    replaceIfExists: boolean;
    saveSkippedRecords: boolean;
    stage: SnowflakeStageFqn;
    targetTableFqn: SnowflakeTableFqn;
}

export type LoadSnowflakeTableSuccess = LoadSnowflakeTableResult;

export type LoadSnowflakeTableFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | TargetTableNotExists
    | Canceled
    | FileFormatChanged
    | UnexpectedFailure;

export type LoadSnowflakeTableMessage = LoadSnowflakeTableSuccess | LoadSnowflakeTableFailure;

export const LOAD_SNOWFLAKE_TABLE_RESULT = 'LoadSnowflakeTableResult';

export interface LoadSnowflakeTableResult {
    _kind: typeof LOAD_SNOWFLAKE_TABLE_RESULT;
    executionMetadata: Record<string, unknown>;
    filesLoaded: Array<StageFileCopyIntoResult>;
    skippedRecordsTableFqn?: SnowflakeTableFqn;
    warnings: Array<JobRunWarning>;
}
export type LocalTime = string;

export const LOG_ACTION = 'LogAction';

export interface LogAction {
    _kind: typeof LOG_ACTION;
}

export type LogActionSuccess = Succeeded;

export type LogActionFailure = UnexpectedFailure;

export type LogActionMessage = LogActionSuccess | LogActionFailure;

export interface LongTextDefinitionPayload {
    defaultValue?: string;
}

export interface LongTextPayload {
    value: string;
}

export interface LongestRuntimeRanking {
    dataType: JobMetricsDataType.MILLISECONDS;
    entries: Array<JobRanking>;
    name: JobMetricsName.LongestRuntimeRanking;
    type: JobMetricsType.TOP_N;
}

export interface ManualJobRunTrigger {
    requestId?: string;
    triggerType: JobRunTriggerType.MANUAL;
}

export interface ManualTriggerRequest {
    triggerType: ExecutionTriggerType.MANUAL;
}

export interface ManualTriggerResponse {
    triggerType: ExecutionTriggerType.MANUAL;
}

export interface MappedOutputColumn {
    alias?: string;
    excluded: boolean;
    name: string;
    position?: number;
    typeFamily?: TypeFamily;
}

export type MappedOutputColumn_ = object;

export const MATERIALIZE_TABLE = 'MaterializeTable';

export interface MaterializeTable {
    _kind: typeof MATERIALIZE_TABLE;
    database: string;
    role?: string;
    schema: string;
    table: string;
    triggerType: ExecutionTriggerType;
    warehouse?: string;
}

export type MaterializeTableSuccess =
    TableMaterializationCompleted | FailedTableMaterializationCompleted;

export type MaterializeTableProgress = TableMaterializationInitiated;

export type MaterializeTableFailure =
    | NoDeploymentHistory
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | FailureDuringSubmission
    | SecretNotFound
    | TargetViewOrTableNotAllowedToOverwrite
    | ViewWithSameNameAlreadyExists
    | UnexpectedFailure;

export type MaterializeTableMessage =
    MaterializeTableSuccess | MaterializeTableProgress | MaterializeTableFailure;

export const MATERIALIZE_TRANSFORMATION_AS_TEMPORARY_TABLE =
    'MaterializeTransformationAsTemporaryTable';

export interface MaterializeTransformationAsTemporaryTable {
    _kind: typeof MATERIALIZE_TRANSFORMATION_AS_TEMPORARY_TABLE;
    transformationId: string;
}

export type MaterializeTransformationAsTemporaryTableSuccess =
    TemporaryTableMaterializationCompleted;

export type MaterializeTransformationAsTemporaryTableProgress =
    TemporaryTableMaterializationInitiated | FailedTemporaryTableMaterializationCompleted;

export type MaterializeTransformationAsTemporaryTableFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | NoWritableSchemaFound
    | FailureDuringSubmission
    | CompilationFailedWithDatasetError
    | EntityNotFoundOrNotAccessible
    | UnexpectedFailure;

export type MaterializeTransformationAsTemporaryTableMessage =
    | MaterializeTransformationAsTemporaryTableSuccess
    | MaterializeTransformationAsTemporaryTableProgress
    | MaterializeTransformationAsTemporaryTableFailure;

export interface MaxAggregation {
    sourceColumnName?: string;
}

export const MAX_FILE_SIZE_EXCEEDED = 'MaxFileSizeExceeded';

export interface MaxFileSizeExceeded {
    _kind: typeof MAX_FILE_SIZE_EXCEEDED;
    maxFileSize: number;
    message: string;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export type Measurement =
    | AnyAggregation
    | ApproxCountDistinctAggregation
    | AvgAggregation
    | CountAggregation
    | CountDistinctAggregation
    | KurtosisAggregation
    | MaxAggregation
    | MedianAggregation
    | MinAggregation
    | ModeAggregation
    | SkewAggregation
    | StddevAggregation
    | StddevPopAggregation
    | SumAggregation
    | VarPopAggregation
    | VarSampAggregation;

export interface MedianAggregation {
    sourceColumnName?: string;
}

export interface MedianDurationMillis {
    currentValue?: Number;
    dataType: JobMetricsDataType.MILLISECONDS;
    name: JobMetricsName.MedianDurationMillis;
    previousValue?: Number;
    type: JobMetricsType.AGGREGATE;
}

export type Message =
    | AccessControlDtos
    | Accessible
    | AlreadyDeployedFromAnotherTransformation
    | AlreadyUsedAsProjectInput
    | AmbiguousColumnReference
    | AnalyzeSchemaUsageInitiated
    | AnotherInstanceIsResponsible
    | ApiKeyCreated
    | ApiKeyEntries
    | ApiKeyOperationNotPermitted
    | ArbitrarySqlResult
    | AssetDeletionNotPermitted
    | AssetModificationNotPermitted
    | AssetNotDeletable
    | Authenticated
    | CancelAccepted
    | Canceled
    | CannotCastValueOfType
    | ChatGptError
    | ChatGptInfo
    | ChatGptNotConfigured
    | ChatGptResponse
    | CircularReference
    | ClientConnectionError
    | ColumnMetadata
    | CompilationFailedWithDatasetError
    | CompilationFailedWithTransformationErrors
    | ConfigProperties
    | ConnectionDetails
    | ConnectionNotConfigured
    | DapMigrationCreated
    | DapMigrationUpdated
    | DapMigrations
    | DataDrivenTriggerCandidates
    | DataLoadingFailed
    | DataSampleSql
    | DatabaseNotExistsOrNotAuthorized
    | DatameerChannelCreated
    | DatameerChannelNotFound
    | DatameerChannelUsages
    | DatameerChannels
    | DbtModelInfo
    | DeleteUnusedDatasetsFromProjectRequested
    | DeploymentDetails
    | DeploymentHistoryEntries
    | DeploymentOverview
    | DerivedAssetMustNotBeModified
    | DiscoveryFailure
    | DistinctValues
    | DownstreamReferences
    | DuplicateColumnNames
    | EmailAddressAlreadyTaken
    | EmailDomainNotAuthorized
    | EntityNotFoundOrNotAccessible
    | EventIdNotFound
    | EventSubscriptionCreated
    | EventSubscriptionNotFound
    | EventSubscriptions
    | ExecutionHistory
    | Exists
    | Exploration
    | ExplorationCreated
    | ExplorationDeleted
    | ExplorationDownloadInitiated
    | ExplorationEmailRequested
    | ExplorationNotFound
    | ExplorationPromoted
    | ExplorationUpdated
    | Explorations
    | FailureDuringSubmission
    | FetchDapMigrationFilesStarted
    | FileFormatChanged
    | FileFormatNotSupported
    | GetEventSubscriptionResponse
    | GithubAuthenticationError
    | GithubAuthenticationInfo
    | GoogleAuthenticationFailed
    | GoogleCredentialsInfo
    | GoogleCredentialsNotConfigured
    | GoogleSheetAlreadyInUse
    | GoogleSheetDoesNotExist
    | GoogleSheetsShare
    | GoogleSheetsSyncRequested
    | GoogleSpreadsheetNotAccessible
    | GoogleSpreadsheetNotFound
    | GoogleSpreadsheetNotWritable
    | GroupCreated
    | GroupDeleted
    | GroupDescriptionUpdated
    | GroupNameUpdated
    | GroupNotFound
    | ImportOfDatasetsStarted
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | InvalidSlackChannelName
    | InvalidSqlSyntax
    | JobActivityMetrics
    | JobDefinitionCreated
    | JobDefinitionDetailedEntry
    | JobDefinitionUpdated
    | JobDefinitions
    | JobEnvironmentCreated
    | JobEnvironmentUpdated
    | JobEnvironments
    | JobOperationCounts
    | JobRunAuditResult
    | JobRunCancelRequested
    | JobRunCheckFailed
    | JobRunCompleted
    | JobRunEntry
    | JobRunFilterOptions
    | JobRunNotCancelable
    | JobRunNotFound
    | JobRunResult
    | JobRunSkipped
    | JobRunTimeline
    | JobRunTriggered
    | JobRuns
    | JobRunsTriggered
    | JobTriggerCandidates
    | LoadSnowflakeTableResult
    | MaxFileSizeExceeded
    | MissingColumn
    | MissingTable
    | ModelDeployed
    | ModelJobsValidationResult
    | ModelRedeployed
    | ModelTagCheckCandidates
    | ModelTagOutputColumns
    | ModelTagSourceTables
    | ModelTags
    | ModelUpdated
    | Models
    | NameAlreadyExists
    | NameCannotBeBlank
    | NoActiveWarehouseSelected
    | NoDbtModelFound
    | NoDeploymentHistory
    | NoSampleFilesFoundByPattern
    | NoWritableSchemaFound
    | NodeAlreadyInGroup
    | NotAccessible
    | NotAuthenticated
    | NotAuthenticatedToSnowflake
    | NotAuthorizedToSlack
    | NotExists
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | NotInSlackChannel
    | NotPermitted
    | ObjectAlreadyExists
    | OperationNotPermitted
    | OutputColumns
    | OutputColumnsWithInvisibleControlCharacters
    | OutputColumnsWithLineSeparator
    | PreparedDownloadInitiated
    | ProjectDependencies
    | ProjectDeployments
    | ProjectInputs
    | ProjectReferences
    | ProjectSynced
    | ProjectsContainingTableDeployments
    | RequirementNotMet
    | RestoreTableDeploymentTriggered
    | ResultRecords
    | RolesAndWarehouses
    | SchemaNotExistsOrNotAuthorized
    | SearchResultsDto
    | SecretNotFound
    | ShareCreated
    | ShareNotFound
    | ShareResumed
    | ShareUpdated
    | SharedOutputs
    | SlackApiCallFailed
    | SlackAuthorizationUrl
    | SlackChannelCreated
    | SlackChannelNotFound
    | SlackChannels
    | SlackConfiguration
    | SlackNotConfigured
    | SnowflakeAccountNotExistsOrNotAccessible
    | SnowflakeAuthorizationUrl
    | SnowflakeConfigurationSet
    | SnowflakeConnection
    | SnowflakeConnectionDetails
    | SnowflakeFileFormats
    | SnowflakeInternalServerError
    | SnowflakeMultiUserConfiguration
    | SnowflakeNotConfigured
    | SnowflakeOAuth2Connection
    | SnowflakeObjectNotFoundOrNotAccessible
    | SnowflakeOutputAlreadyExists
    | SnowflakeQueries
    | SnowflakeQueryCanceled
    | SnowflakeQueryStatus
    | SnowflakeQuerySubmitted
    | SnowflakeResult
    | SnowflakeServiceAccountCreated
    | SnowflakeServiceAccountUpdated
    | SnowflakeServiceAccountUserDeleted
    | SnowflakeServiceAccountUserNotExists
    | SnowflakeServiceAccountUsers
    | SnowflakeSql
    | SnowflakeStageFileInfo
    | SnowflakeStages
    | SnowflakeStoredProcedures
    | SnowflakeTableAccessValidated
    | SnowflakeTableColumns
    | SnowflakeTableDeleted
    | SpaceCopied
    | SpaceCreatedWithView
    | StageDetails
    | StageFiles
    | Succeeded
    | TableAlreadyExists
    | TableDetails
    | TableDetailsForDataBrowser
    | TableImportedToNewProject
    | TableImportedToProject
    | TableMaterializationInitiated
    | TargetTableColumns
    | TargetTableNotExists
    | TargetViewOrTableNotAllowedToOverwrite
    | TemporaryTableMaterializationInitiated
    | Timeout
    | TokenExpired
    | TooManyEnabledUsers
    | TransformationEmailRequested
    | TransformationEmailSchedule
    | TransformationNodeCreated
    | TransformationNodeUpdated
    | TransformationNotOperational
    | UncategorizedSqlError
    | UnexpectedFailure
    | UnexpectedRejection
    | UnknownFunction
    | UnknownFunctions
    | UnknownRequest
    | UnloadJobRunResult
    | UserAlreadyEnabled
    | UserCreated
    | UserDeleted
    | UserDiffersFromToken
    | UserEnabled
    | UserNotFound
    | UsernameAlreadyTaken
    | UsernameInvalid
    | ValueNotRecognized
    | ViewAlreadyExists
    | ViewDeleted
    | ViewDeployed
    | ViewDeploymentRestored
    | ViewOutputColumnsSet
    | ViewPublishingInitiated
    | ViewWithSameNameAlreadyExists
    | WarehouseList
    | WarehouseSuspended
    | WorkerQueueFull;

export interface MessageResponsePayload {
    message: Message;
}

export interface Meta {
    context?: object;
    requestId: string;
    sessionId?: string;
}

export interface MinAggregation {
    sourceColumnName?: string;
}

export const MISSING_COLUMN = 'MissingColumn';

export interface MissingColumn {
    _kind: typeof MISSING_COLUMN;
    column: string;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export interface MissingSourceColumn {
    columnName: string;
    expression: string;
    message: string;
}

export const MISSING_TABLE = 'MissingTable';

export interface MissingTable {
    _kind: typeof MISSING_TABLE;
    availableTables: Array<string>;
    errorPosition: ErrorPosition;
    index?: number;
    origins: Array<DatasetUid>;
    qualification?: TableQualification;
    sql: string;
    table: string;
}

export interface ModeAggregation {
    sourceColumnName?: string;
}

export interface ModelDeletionConcern {
    jobs: Array<ModelJob>;
}

export const MODEL_DEPLOYED = 'ModelDeployed';

export interface ModelDeployed {
    _kind: typeof MODEL_DEPLOYED;
    jobDefId: number;
    jobDefUid: string;
    viewRef: ViewRef;
}

export interface ModelEntry {
    id: number;
    jobs: Array<ValidatedJobDefEntry>;
    latestTag?: LatestTag;
    name: string;
    projectId: number;
    projectName?: string;
    projectSlug?: string;
    slug: string;
}

export interface ModelJob {
    envName: string;
    jobDefId: number;
    jobDefName: string;
    jobDefUid: string;
}

export interface ModelJobValidationEntry {
    currentTagId: number;
    currentTagName: string;
    definitionId: number;
    definitionName: string;
    environmentName: string;
    isUpToDate: boolean;
    isValid: boolean;
    lastModifiedAt: Instant;
    messages: Array<JobValidationMessage>;
    status: JobValidationStatus;
    trigger?: JobTrigger;
}

export const MODEL_JOBS_VALIDATION_RESULT = 'ModelJobsValidationResult';

export interface ModelJobsValidationResult {
    _kind: typeof MODEL_JOBS_VALIDATION_RESULT;
    entries: Array<ModelJobValidationEntry>;
}

export const MODEL_REDEPLOYED = 'ModelRedeployed';

export interface ModelRedeployed {
    _kind: typeof MODEL_REDEPLOYED;
    jobDefId: number;
    jobDefUid: string;
    viewRef: ViewRef;
}

export interface ModelRef {
    id: number;
    isDeleted: boolean;
    name: string;
}

export interface ModelReference {
    fqn: SnowflakeTableFqn;
    modelName?: string;
}

export interface ModelTagCheckCandidate {
    inputTable: string;
    lastUpdatedAt: Instant;
}

export const MODEL_TAG_CHECK_CANDIDATES = 'ModelTagCheckCandidates';

export interface ModelTagCheckCandidates {
    _kind: typeof MODEL_TAG_CHECK_CANDIDATES;
    candidates: Array<ModelTagCheckCandidate>;
}

export interface ModelTagEntry {
    createdAt: Instant;
    createdBy: string;
    jobDefRefs: Array<JobDefinitionRef>;
    message?: string;
    modelId: number;
    modelName?: string;
    projectId: number;
    projectName?: string;
    projectVersion: number;
    tagId: number;
    tagName: string;
    version: number;
}

export const MODEL_TAG_OUTPUT_COLUMNS = 'ModelTagOutputColumns';

export interface ModelTagOutputColumns {
    _kind: typeof MODEL_TAG_OUTPUT_COLUMNS;
    entries: Array<SqlColumn>;
}

export const MODEL_TAG_SOURCE_TABLES = 'ModelTagSourceTables';

export interface ModelTagSourceTables {
    _kind: typeof MODEL_TAG_SOURCE_TABLES;
    entries: Array<SnowflakeTableFqn>;
}

export const MODEL_TAGS = 'ModelTags';

export interface ModelTags {
    _kind: typeof MODEL_TAGS;
    entries: Array<ModelTagEntry>;
}

export const MODEL_UPDATED = 'ModelUpdated';

export interface ModelUpdated {
    _kind: typeof MODEL_UPDATED;
    viewRef: ViewRef;
}

export const MODELS = 'Models';

export interface Models {
    _kind: typeof MODELS;
    entries: Array<ModelEntry>;
}

export interface ModificationIsNotSupported {
    message: string;
}

export interface ModifiedTableEntity {
    fqn: SnowflakeTableFqn;
    modificationType: TableModificationType;
}

export interface MostJobRunsRanking {
    dataType: JobMetricsDataType.NUMBER;
    entries: Array<JobRanking>;
    name: JobMetricsName.MostJobRunsRanking;
    type: JobMetricsType.TOP_N;
}

export interface MostRecordsProducedRanking {
    dataType: JobMetricsDataType.NUMBER;
    entries: Array<JobRanking>;
    name: JobMetricsName.MostRecordsProducedRanking;
    type: JobMetricsType.TOP_N;
}

export const NAME_ALREADY_EXISTS = 'NameAlreadyExists';

export interface NameAlreadyExists {
    _kind: typeof NAME_ALREADY_EXISTS;
    id: number;
}

export const NAME_CANNOT_BE_BLANK = 'NameCannotBeBlank';

export interface NameCannotBeBlank {
    _kind: typeof NAME_CANNOT_BE_BLANK;
}

export const NO_ACTIVE_WAREHOUSE_SELECTED = 'NoActiveWarehouseSelected';

export interface NoActiveWarehouseSelected {
    _kind: typeof NO_ACTIVE_WAREHOUSE_SELECTED;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const NO_DBT_MODEL_FOUND = 'NoDbtModelFound';

export interface NoDbtModelFound {
    _kind: typeof NO_DBT_MODEL_FOUND;
    database: string;
    schema: string;
    table: string;
}

export const NO_DEPLOYMENT_HISTORY = 'NoDeploymentHistory';

export interface NoDeploymentHistory {
    _kind: typeof NO_DEPLOYMENT_HISTORY;
}

export interface NoRowsProduced {
    _kind: 'NoRowsProduced';
}

export const NO_SAMPLE_FILES_FOUND_BY_PATTERN = 'NoSampleFilesFoundByPattern';

export interface NoSampleFilesFoundByPattern {
    _kind: typeof NO_SAMPLE_FILES_FOUND_BY_PATTERN;
    pattern: string;
}

export const NO_USAGES_FOUND = 'NoUsagesFound';

export interface NoUsagesFound {
    _kind: typeof NO_USAGES_FOUND;
}

export const NO_WRITABLE_SCHEMA_FOUND = 'NoWritableSchemaFound';

export interface NoWritableSchemaFound {
    _kind: typeof NO_WRITABLE_SCHEMA_FOUND;
}

export const NODE_ALREADY_IN_GROUP = 'NodeAlreadyInGroup';

export interface NodeAlreadyInGroup {
    _kind: typeof NODE_ALREADY_IN_GROUP;
}

export const NOT_ACCESSIBLE = 'NotAccessible';

export interface NotAccessible {
    _kind: typeof NOT_ACCESSIBLE;
    accessible: boolean;
    database: string;
    failures: Array<object>;
    schema: string;
    table: string;
}

export const NOT_AUTHENTICATED = 'NotAuthenticated';

export interface NotAuthenticated {
    _kind: typeof NOT_AUTHENTICATED;
}

export const NOT_AUTHENTICATED_TO_SNOWFLAKE = 'NotAuthenticatedToSnowflake';

export interface NotAuthenticatedToSnowflake {
    _kind: typeof NOT_AUTHENTICATED_TO_SNOWFLAKE;
    jdbcUrl: string;
    type: SnowflakeConfigurationType;
}

export const NOT_AUTHORIZED_TO_SLACK = 'NotAuthorizedToSlack';

export interface NotAuthorizedToSlack {
    _kind: typeof NOT_AUTHORIZED_TO_SLACK;
}

export const NOT_EXISTS = 'NotExists';

export interface NotExists {
    _kind: typeof NOT_EXISTS;
}

export const NOT_EXISTS_OR_NOT_AUTHORIZED = 'NotExistsOrNotAuthorized';

export interface NotExistsOrNotAuthorized {
    _kind: typeof NOT_EXISTS_OR_NOT_AUTHORIZED;
    identifier: SnowflakeTableFqn;
    index?: number;
    message: string;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const NOT_EXISTS_OR_OPERATION_CANNOT_BE_PERFORMED = 'NotExistsOrOperationCannotBePerformed';

export interface NotExistsOrOperationCannotBePerformed {
    _kind: typeof NOT_EXISTS_OR_OPERATION_CANNOT_BE_PERFORMED;
    message: string;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const NOT_IN_SLACK_CHANNEL = 'NotInSlackChannel';

export interface NotInSlackChannel {
    _kind: typeof NOT_IN_SLACK_CHANNEL;
    channel: string;
}

export const NOT_PERMITTED = 'NotPermitted';

export interface NotPermitted {
    _kind: typeof NOT_PERMITTED;
    operation: Operation;
}

export const OBJECT_ALREADY_EXISTS = 'ObjectAlreadyExists';

export interface ObjectAlreadyExists {
    _kind: typeof OBJECT_ALREADY_EXISTS;
    message: string;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export type Operation =
    | AuditJobRun
    | Authenticate
    | CallSnowflakeStoredProcedure
    | CancelJobRun
    | CancelRequest
    | CancelSnowflakeQuery
    | CollectDbtModels
    | CollectRoiTableMetrics
    | CollectSnowflakeEvents
    | CollectWarehouseUsage
    | CompleteJobRun
    | CopySpace
    | CreateApiKey
    | CreateDapMigration
    | CreateDatameerChannel
    | CreateEventSubscription
    | CreateExploration
    | CreateGroup
    | CreateSlackChannel
    | CreateSpaceWithView
    | CreateTransformationNode
    | CreateUser
    | DeleteDatameerChannel
    | DeleteEventSubscription
    | DeleteExploration
    | DeleteGithubAuthentication
    | DeleteGroup
    | DeleteJobDefinition
    | DeleteJobEnvironment
    | DeleteShare
    | DeleteSnowflakeServiceAccountUser
    | DeleteSnowflakeTable
    | DeleteSnowflakeUserCredentials
    | DeleteTemporaryTable
    | DeleteUnusedDatasetsFromProject
    | DeleteUser
    | DeleteView
    | DeployModel
    | DeployTransformationAsSnowflakeTable
    | DeployTransformationAsSnowflakeView
    | EnableUser
    | ExecuteArbitrarySql
    | ExecuteQueryByEnvironment
    | ExecuteQueryByServiceAccount
    | FetchSnowflakeTableColumns
    | GetAccessControls
    | GetChatGptInfo
    | GetColumnMetadata
    | GetDataDrivenTriggerCandidates
    | GetDatameerChannelUsages
    | GetDbtModelInfo
    | GetDeploymentHistory
    | GetDeploymentOverview
    | GetDistinctColumnValues
    | GetDownstreamReferences
    | GetEventSubscription
    | GetExecutionHistory
    | GetExploration
    | GetGithubAuthenticationInfo
    | GetGoogleCredentialsInfo
    | GetGoogleSheetsShare
    | GetJobActivityMetrics
    | GetJobDefinition
    | GetJobOperationCounts
    | GetJobRun
    | GetJobRunFilterOptions
    | GetJobRunTimeline
    | GetLastJobRun
    | GetLoadJobTargetTableColumns
    | GetModelTagCheckCandidates
    | GetModelTagOutputColumns
    | GetModelTagSourceTables
    | GetOutputColumns
    | GetOverrideConfig
    | GetProjectDependencies
    | GetProjectDeployments
    | GetProjectInputs
    | GetProjectReferences
    | GetProjectsContainingTableDeployments
    | GetSlackAuthorizationUrl
    | GetSlackConfiguration
    | GetSnowflakeAuthorizationUrl
    | GetSnowflakeConfiguration
    | GetSnowflakeConnectionDetails
    | GetSnowflakeQueryHistory
    | GetSnowflakeQueryStatus
    | GetSnowflakeResult
    | GetSnowflakeSqlByProjectSql
    | GetSnowflakeSqlByTransformation
    | GetSnowflakeSqlByTransformationId
    | GetSnowflakeSqlForOutputMapping
    | GetSnowflakeStageDetails
    | GetSnowflakeStageFileInfo
    | GetSnowflakeTableAccess
    | GetSnowflakeTableConnectionDetails
    | GetTableDetails
    | GetTableDetailsForDataBrowser
    | GetTransformationEmailSchedule
    | Heartbeat
    | ImportDatasets
    | ImportTableToNewProject
    | ImportTableToProject
    | InitiateAnalyzeSchemaUsage
    | InitiateExplorationDownload
    | InitiatePrepareDownload
    | ListApiKeys
    | ListDapMigrations
    | ListDatameerChannels
    | ListEventSubscriptions
    | ListExplorations
    | ListJobDefinitions
    | ListJobEnvironments
    | ListJobRuns
    | ListModelTags
    | ListModels
    | ListRolesAndWarehouses
    | ListSharedOutputs
    | ListSlackChannels
    | ListSnowflakeFileFormats
    | ListSnowflakeQueries
    | ListSnowflakeServiceAccountUsers
    | ListSnowflakeStageFiles
    | ListSnowflakeStages
    | ListSnowflakeStoredProcedures
    | ListSnowflakeWarehouses
    | LoadSnowflakeTable
    | LogAction
    | MaterializeTable
    | MaterializeTransformationAsTemporaryTable
    | PauseJobTrigger
    | PauseShare
    | PauseSnowflakeOutput
    | PromoteExploration
    | PromptChatGpt
    | RedeployModel
    | RequestExplorationEmail
    | RequestGoogleSheetsSync
    | RequestLicense
    | RequestTransformationEmail
    | RestoreTableDeployment
    | RestoreViewDeployment
    | ResumeJobTrigger
    | ResumeSnowflakeOutput
    | RevokeApiKey
    | RevokeSnowflakeToken
    | RunFullMaterialization
    | RunIncrementalMaterialization
    | SampleProjectExists
    | SearchProjects
    | SetExplorationEmailSchedule
    | SetGithubAuthentication
    | SetGoogleSheetsShare
    | SetJobDefinition
    | SetJobEnvironment
    | SetModelSource
    | SetSnowflakeMultiUserConfiguration
    | SetSnowflakeOAuth2ConnectionAdvanced
    | SetSnowflakeOAuth2ConnectionDiscovery
    | SetSnowflakeOutput
    | SetSnowflakeRole
    | SetSnowflakeServiceAccountConfiguration
    | SetSnowflakeServiceAccountUser
    | SetSnowflakeUserCredentials
    | SetSnowflakeWarehouse
    | SetTransformationEmailSchedule
    | SetViewOutputColumns
    | StartFetchDapMigrationFiles
    | SubmitSnowflakeQuery
    | SyncProject
    | TriggerJobRun
    | TriggerJobRuns
    | UninstallSlack
    | UnloadSnowflakeTable
    | UpdateChatGptConfiguration
    | UpdateDapMigration
    | UpdateEventSubscriptionDeliveryTarget
    | UpdateExploration
    | UpdateGroupDescription
    | UpdateGroupName
    | UpdateModelJobs
    | UpdateProjectEnv
    | UpdateTransformationNode
    | ValidateModelJobs
    | ValidateSnowflakeAccess;

export const OPERATION_NOT_PERMITTED = 'OperationNotPermitted';

export interface OperationNotPermitted {
    _kind: typeof OPERATION_NOT_PERMITTED;
}

export type OperationUid = string;

export enum Operator {
    EQUALS = 'EQUALS',
    NOT_EQUALS = 'NOT_EQUALS',
    NOT_EQUALS_OR_EMPTY = 'NOT_EQUALS_OR_EMPTY',
    LESS_THAN = 'LESS_THAN',
    LESS_THAN_EQUALS = 'LESS_THAN_EQUALS',
    GREATER_THAN = 'GREATER_THAN',
    GREATER_THAN_EQUALS = 'GREATER_THAN_EQUALS',
    CONTAINS = 'CONTAINS',
    NOT_CONTAINS = 'NOT_CONTAINS',
    STARTS_WITH = 'STARTS_WITH',
    NOT_STARTS_WITH = 'NOT_STARTS_WITH',
    ENDS_WITH = 'ENDS_WITH',
    NOT_ENDS_WITH = 'NOT_ENDS_WITH',
    IN = 'IN',
    IS_EMPTY = 'IS_EMPTY',
    IS_NOT_EMPTY = 'IS_NOT_EMPTY',
    NOT_IN = 'NOT_IN',
    NULL = 'NULL',
    NOT_NULL = 'NOT_NULL'
}

export const OUTPUT_COLUMNS = 'OutputColumns';

export interface OutputColumns {
    _kind: typeof OUTPUT_COLUMNS;
    inputColumns: Array<Column>;
    mappings: Array<MappedOutputColumn>;
}

export const OUTPUT_COLUMNS_WITH_INVISIBLE_CONTROL_CHARACTERS =
    'OutputColumnsWithInvisibleControlCharacters';

export interface OutputColumnsWithInvisibleControlCharacters {
    _kind: typeof OUTPUT_COLUMNS_WITH_INVISIBLE_CONTROL_CHARACTERS;
    columns: Array<string>;
}

export const OUTPUT_COLUMNS_WITH_LINE_SEPARATOR = 'OutputColumnsWithLineSeparator';

export interface OutputColumnsWithLineSeparator {
    _kind: typeof OUTPUT_COLUMNS_WITH_LINE_SEPARATOR;
    columns: Array<string>;
}

export interface OwnerOrCollaboratorshipRequired {
    message: string;
}

export interface OwnershipRequired {
    message: string;
}

export const PAUSE_JOB_TRIGGER = 'PauseJobTrigger';

export interface PauseJobTrigger {
    _kind: typeof PAUSE_JOB_TRIGGER;
    uid: string;
}

export type PauseJobTriggerSuccess = Succeeded;

export type PauseJobTriggerFailure = UnexpectedFailure;

export type PauseJobTriggerMessage = PauseJobTriggerSuccess | PauseJobTriggerFailure;

export const PAUSE_SHARE = 'PauseShare';

export interface PauseShare {
    _kind: typeof PAUSE_SHARE;
    id: number;
}

export type PauseShareSuccess = Succeeded;

export type PauseShareFailure = UnexpectedFailure;

export type PauseShareMessage = PauseShareSuccess | PauseShareFailure;

export const PAUSE_SNOWFLAKE_OUTPUT = 'PauseSnowflakeOutput';

export interface PauseSnowflakeOutput {
    _kind: typeof PAUSE_SNOWFLAKE_OUTPUT;
    database: string;
    schema: string;
    table: string;
}

export type PauseSnowflakeOutputSuccess = Succeeded;

export type PauseSnowflakeOutputFailure = UnexpectedFailure;

export type PauseSnowflakeOutputMessage = PauseSnowflakeOutputSuccess | PauseSnowflakeOutputFailure;
export type Period = string;

export interface Pivot {
    measurements: Array<Measurement>;
    pivotColumns: Array<PivotColumn>;
    rowColumns: Array<string>;
}

export interface PivotColumn {
    columnName: string;
    filterValues: Array<string>;
}

export interface PivotError {
    cause: string;
    message: string;
}

export const PREPARED_DOWNLOAD_INITIATED = 'PreparedDownloadInitiated';

export interface PreparedDownloadInitiated {
    _kind: typeof PREPARED_DOWNLOAD_INITIATED;
    viewUuid: ViewUuid;
}

export interface Project {
    aliases: Array<ColumnAlias>;
    columns: Array<string>;
}

export const PROJECT_DEPENDENCIES = 'ProjectDependencies';

export interface ProjectDependencies {
    _kind: typeof PROJECT_DEPENDENCIES;
    tablesByUid: Record<string, Array<SnowflakeTableFqn>>;
}

export interface ProjectDeploymentEntry {
    database: string;
    deployedAt: Instant;
    lastRun: Instant;
    projectDatasetName?: string;
    projectDatasetSlug?: string;
    projectSlug: string;
    role: string | null;
    schema: string;
    spaceViewUuid: string;
    table: string;
    tableType: TableType;
    trigger: SnowflakeOutputTriggerResponse;
    version: number;
    warehouse: string | null;
}

export const PROJECT_DEPLOYMENTS = 'ProjectDeployments';

export interface ProjectDeployments {
    _kind: typeof PROJECT_DEPLOYMENTS;
    entries: Array<ProjectDeploymentEntry>;
}

export interface ProjectEnvironment {
    database?: string;
    id: number;
    name: string;
    role: string;
    schema?: string;
    uid: string;
    warehouse: string;
}

export interface ProjectInput {
    columnCount: number;
    database: string;
    projectCount: number;
    schema: string;
    table: string;
    viewName: string;
    viewSlug: string;
}

export const PROJECT_INPUTS = 'ProjectInputs';

export interface ProjectInputs {
    _kind: typeof PROJECT_INPUTS;
    inputs: Array<ProjectInput>;
}

export interface ProjectRef {
    id: number;
    name: string;
    slug: string;
}

export interface ProjectReference {
    isAccessibleByUser?: Boolean;
    projectName: string;
    projectOwner: string;
    projectSlug: string;
    spaceUid: string;
}

export const PROJECT_REFERENCES = 'ProjectReferences';

export interface ProjectReferences {
    _kind: typeof PROJECT_REFERENCES;
    projectReferences: Array<ProjectReference>;
}

export const PROJECT_SYNCED = 'ProjectSynced';

export interface ProjectSynced {
    _kind: typeof PROJECT_SYNCED;
    spaceUid: string;
}

export interface ProjectTableRef {
    database: string;
    schema: string;
    spaceName: string;
    spaceSlug: string;
    spaceUid: string;
    table: string;
    viewName: string;
    viewSlug: string;
    viewType: ViewType;
    viewUid: string;
}

export interface Projection {
    inputs: Columns;
    mappings: Array<MappedOutputColumn>;
    sourceViews: Array<string>;
}

export const PROJECTS_CONTAINING_TABLE_DEPLOYMENTS = 'ProjectsContainingTableDeployments';

export interface ProjectsContainingTableDeployments {
    _kind: typeof PROJECTS_CONTAINING_TABLE_DEPLOYMENTS;
    entries: Record<string, string>;
}

export const PROMOTE_EXPLORATION = 'PromoteExploration';

export interface PromoteExploration {
    _kind: typeof PROMOTE_EXPLORATION;
    id: string;
    transformationName?: string;
}

export type PromoteExplorationSuccess = ExplorationPromoted;

export type PromoteExplorationFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | DuplicateColumnNames
    | OutputColumnsWithInvisibleControlCharacters
    | OutputColumnsWithLineSeparator
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | CompilationFailedWithTransformationErrors
    | ExplorationNotFound
    | UnexpectedFailure;

export type PromoteExplorationMessage = PromoteExplorationSuccess | PromoteExplorationFailure;

export const PROMPT_CHAT_GPT = 'PromptChatGpt';

export interface PromptChatGpt {
    _kind: typeof PROMPT_CHAT_GPT;
    requestJson: object;
    timeout?: Duration;
}

export type PromptChatGptSuccess = ChatGptResponse;

export type PromptChatGptFailure =
    SecretNotFound | ChatGptNotConfigured | Timeout | UnexpectedFailure;

export type PromptChatGptMessage = PromptChatGptSuccess | PromptChatGptFailure;

export interface QueryBuilder {
    model: object;
    sourceViews: Array<string>;
    sql: string;
}

export interface RecordsProducedSum {
    currentValue?: Number;
    dataType: JobMetricsDataType.NUMBER;
    name: JobMetricsName.RecordsProducedSum;
    previousValue?: Number;
    type: JobMetricsType.AGGREGATE;
}

export const REDEPLOY_MODEL = 'RedeployModel';

export interface RedeployModel {
    _kind: typeof REDEPLOY_MODEL;
    modelId: number;
    tagMessage: string;
    tagName: string;
}

export type RedeployModelSuccess = ModelRedeployed;

export type RedeployModelFailure = UnexpectedFailure;

export type RedeployModelMessage = RedeployModelSuccess | RedeployModelFailure;

export type RefDto = SpaceRefDto | ViewRefDto;

export interface ReferencedColumInError {
    columnName: string;
    expression: string;
    message: string;
}

export interface Reject {
    failures: Array<object>;
    request?: Request;
}

export interface Replace {
    column: string;
    currentValue: string;
    newValue: string;
    suggestedColumnName?: string;
}

export interface Request {
    meta: Meta;
    name: string;
    payload: RequestPayload;
}

export const REQUEST_EXPLORATION_EMAIL = 'RequestExplorationEmail';

export interface RequestExplorationEmail {
    _kind: typeof REQUEST_EXPLORATION_EMAIL;
    explorationId: string;
    message: string;
    recipients: Array<string>;
    subject: string;
}

export type RequestExplorationEmailSuccess = ExplorationEmailRequested;

export type RequestExplorationEmailProgress = ExplorationEmailRequested;

export type RequestExplorationEmailFailure =
    ExplorationNotFound | EntityNotFoundOrNotAccessible | UnexpectedFailure;

export type RequestExplorationEmailMessage =
    | RequestExplorationEmailSuccess
    | RequestExplorationEmailProgress
    | RequestExplorationEmailFailure;

export const REQUEST_GOOGLE_SHEETS_SYNC = 'RequestGoogleSheetsSync';

export interface RequestGoogleSheetsSync {
    _kind: typeof REQUEST_GOOGLE_SHEETS_SYNC;
    id: number;
}

export type RequestGoogleSheetsSyncSuccess = GoogleSheetWritten;

export type RequestGoogleSheetsSyncProgress = GoogleSheetsSyncRequested;

export type RequestGoogleSheetsSyncFailure = ShareNotFound | UnexpectedFailure;

export type RequestGoogleSheetsSyncMessage =
    | RequestGoogleSheetsSyncSuccess
    | RequestGoogleSheetsSyncProgress
    | RequestGoogleSheetsSyncFailure;

export type RequestId = string;

export const REQUEST_LICENSE = 'RequestLicense';

export interface RequestLicense {
    _kind: typeof REQUEST_LICENSE;
    company: string;
    email: string;
    firstName: string;
    lastName: string;
    message?: string;
    phoneNumber?: string;
    teamSize?: number;
    type: string;
}

export type RequestLicenseSuccess = Succeeded;

export type RequestLicenseFailure = UnexpectedFailure;

export type RequestLicenseMessage = RequestLicenseSuccess | RequestLicenseFailure;

export interface RequestPayload {
    simpleName: string;
    toOperation: Operation;
}

export const REQUEST_TRANSFORMATION_EMAIL = 'RequestTransformationEmail';

export interface RequestTransformationEmail {
    _kind: typeof REQUEST_TRANSFORMATION_EMAIL;
    message: string;
    recipients: Array<string>;
    subject: string;
    transformationId: ViewUuid;
}

export type RequestTransformationEmailSuccess = TransformationEmailRequested;

export type RequestTransformationEmailProgress = TransformationEmailRequested;

export type RequestTransformationEmailFailure = EntityNotFoundOrNotAccessible | UnexpectedFailure;

export type RequestTransformationEmailMessage =
    | RequestTransformationEmailSuccess
    | RequestTransformationEmailProgress
    | RequestTransformationEmailFailure;

export const REQUIREMENT_NOT_MET = 'RequirementNotMet';

export interface RequirementNotMet {
    _kind: typeof REQUIREMENT_NOT_MET;
    requirementNotMet: string;
}

export interface Response {
    meta: Meta;
    name: string;
    payload: ResponsePayload;
    progressedPreviously: boolean;
    responseName: string;
    status: ResponseType;
}

export type ResponsePayload = FailureResponsePayload | MessageResponsePayload | Reject;

export enum ResponseType {
    Failed = 'Failed',
    Progress = 'Progress',
    Rejected = 'Rejected',
    Succeeded = 'Succeeded'
}

export const RESTORE_TABLE_DEPLOYMENT = 'RestoreTableDeployment';

export interface RestoreTableDeployment {
    _kind: typeof RESTORE_TABLE_DEPLOYMENT;
    database: string;
    message?: string;
    role?: string;
    schema: string;
    table: string;
    version: number;
    warehouse?: string;
}

export type RestoreTableDeploymentSuccess = TableMaterializationCompleted;

export type RestoreTableDeploymentProgress = RestoreTableDeploymentTriggered;

export type RestoreTableDeploymentFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | FailureDuringSubmission
    | SecretNotFound
    | TargetViewOrTableNotAllowedToOverwrite
    | ViewWithSameNameAlreadyExists
    | UnexpectedFailure;

export type RestoreTableDeploymentMessage =
    RestoreTableDeploymentSuccess | RestoreTableDeploymentProgress | RestoreTableDeploymentFailure;

export const RESTORE_TABLE_DEPLOYMENT_TRIGGERED = 'RestoreTableDeploymentTriggered';

export interface RestoreTableDeploymentTriggered {
    _kind: typeof RESTORE_TABLE_DEPLOYMENT_TRIGGERED;
    database: string;
    schema: string;
    table: string;
    version: number;
}

export const RESTORE_VIEW_DEPLOYMENT = 'RestoreViewDeployment';

export interface RestoreViewDeployment {
    _kind: typeof RESTORE_VIEW_DEPLOYMENT;
    database: string;
    message?: string;
    schema: string;
    table: string;
    version: number;
}

export type RestoreViewDeploymentSuccess = ViewDeploymentRestored;

export type RestoreViewDeploymentFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type RestoreViewDeploymentMessage =
    RestoreViewDeploymentSuccess | RestoreViewDeploymentFailure;

export const RESULT_RECORDS = 'ResultRecords';

export interface ResultRecords {
    _kind: typeof RESULT_RECORDS;
    value: Array<any>;
}

export const RESUME_JOB_TRIGGER = 'ResumeJobTrigger';

export interface ResumeJobTrigger {
    _kind: typeof RESUME_JOB_TRIGGER;
    uid: string;
}

export type ResumeJobTriggerSuccess = Succeeded;

export type ResumeJobTriggerFailure = UnexpectedFailure;

export type ResumeJobTriggerMessage = ResumeJobTriggerSuccess | ResumeJobTriggerFailure;

export const RESUME_SNOWFLAKE_OUTPUT = 'ResumeSnowflakeOutput';

export interface ResumeSnowflakeOutput {
    _kind: typeof RESUME_SNOWFLAKE_OUTPUT;
    database: string;
    schema: string;
    table: string;
}

export type ResumeSnowflakeOutputSuccess = Succeeded;

export type ResumeSnowflakeOutputFailure = UnexpectedFailure;

export type ResumeSnowflakeOutputMessage =
    ResumeSnowflakeOutputSuccess | ResumeSnowflakeOutputFailure;

export const REVOKE_API_KEY = 'RevokeApiKey';

export interface RevokeApiKey {
    _kind: typeof REVOKE_API_KEY;
    id: string;
}

export type RevokeApiKeySuccess = Succeeded;

export type RevokeApiKeyFailure = ApiKeyOperationNotPermitted | UnexpectedFailure;

export type RevokeApiKeyMessage = RevokeApiKeySuccess | RevokeApiKeyFailure;

export const REVOKE_SNOWFLAKE_TOKEN = 'RevokeSnowflakeToken';

export interface RevokeSnowflakeToken {
    _kind: typeof REVOKE_SNOWFLAKE_TOKEN;
}

export type RevokeSnowflakeTokenSuccess = Succeeded;

export type RevokeSnowflakeTokenFailure = UnexpectedFailure;

export type RevokeSnowflakeTokenMessage = RevokeSnowflakeTokenSuccess | RevokeSnowflakeTokenFailure;

export const ROLES_AND_WAREHOUSES = 'RolesAndWarehouses';

export interface RolesAndWarehouses {
    _kind: typeof ROLES_AND_WAREHOUSES;
    currentRole: string;
    currentWarehouse?: string;
    roles: Array<string>;
    warehouses: Array<string>;
}

export const RUN_FULL_MATERIALIZATION = 'RunFullMaterialization';

export interface RunFullMaterialization {
    _kind: typeof RUN_FULL_MATERIALIZATION;
    sourceTableMappings: Array<SourceTableMapping>;
    tagId: number;
    targetTable: SnowflakeTableFqn;
    targetType: TargetType;
}

export type RunFullMaterializationSuccess = JobRunResult;

export type RunFullMaterializationFailure = UnexpectedFailure;

export type RunFullMaterializationMessage =
    RunFullMaterializationSuccess | RunFullMaterializationFailure;

export const RUN_INCREMENTAL_MATERIALIZATION = 'RunIncrementalMaterialization';

export interface RunIncrementalMaterialization {
    _kind: typeof RUN_INCREMENTAL_MATERIALIZATION;
    columns: Array<string>;
    sourceTableMappings: Array<SourceTableMapping>;
    tagId: number;
    targetTable: SnowflakeTableFqn;
}

export type RunIncrementalMaterializationSuccess = JobRunResult;

export type RunIncrementalMaterializationFailure = UnexpectedFailure;

export type RunIncrementalMaterializationMessage =
    RunIncrementalMaterializationSuccess | RunIncrementalMaterializationFailure;

export const SAMPLE_PROJECT_EXISTS = 'SampleProjectExists';

export interface SampleProjectExists {
    _kind: typeof SAMPLE_PROJECT_EXISTS;
}

export type SampleProjectExistsSuccess = Exists | NotExists;

export type SampleProjectExistsFailure = UnexpectedFailure;

export type SampleProjectExistsMessage = SampleProjectExistsSuccess | SampleProjectExistsFailure;

export interface ScheduleDrivenJobTrigger {
    enabled: boolean;
    schedule: Scheduling;
    triggerType: JobTriggerType.SCHEDULED;
}

export interface ScheduleDrivenTriggerRequest {
    enabled: boolean;
    schedule: Scheduling;
    triggerType: ExecutionTriggerType.SCHEDULE_DRIVEN;
}

export interface ScheduleDrivenTriggerResponse {
    enabled: boolean;
    schedule: Scheduling;
    triggerType: ExecutionTriggerType.SCHEDULE_DRIVEN;
}

export interface ScheduledJobRunTrigger {
    schedule: Scheduling;
    triggerType: JobRunTriggerType.SCHEDULED;
}

export interface Scheduling {
    cronPattern?: string;
    daysInMonth: Array<number>;
    lastModified: Instant;
    repeatEveryUnit: SchedulingUnit;
    repeatOn: Array<DayOfWeek>;
    startOffsetTime?: LocalTime;
    timeZone: string;
}

export enum SchedulingUnit {
    ONCE = 'ONCE',
    DAY = 'DAY',
    WEEK = 'WEEK',
    MONTH = 'MONTH',
    CUSTOM_CRON = 'CUSTOM_CRON'
}

export interface SchemaDto {
    columns: Array<ColumnDescriptorDto>;
}

export const SCHEMA_NOT_EXISTS_OR_NOT_AUTHORIZED = 'SchemaNotExistsOrNotAuthorized';

export interface SchemaNotExistsOrNotAuthorized {
    _kind: typeof SCHEMA_NOT_EXISTS_OR_NOT_AUTHORIZED;
    database: string;
    index?: number;
    message: string;
    originalMessage: string;
    origins: Array<DatasetUid>;
    schema: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const SEARCH_PROJECTS = 'SearchProjects';

export interface SearchProjects {
    _kind: typeof SEARCH_PROJECTS;
    filters: Record<string, Array<string>>;
    numberOfResults: number;
    term: string;
}

export type SearchProjectsSuccess = SearchResultsDto;

export type SearchProjectsFailure = UnexpectedFailure;

export type SearchProjectsMessage = SearchProjectsSuccess | SearchProjectsFailure;

export type SearchResultDto = SpaceSearchResultDto;

export const SEARCH_RESULTS_DTO = 'SearchResultsDto';

export interface SearchResultsDto {
    _kind: typeof SEARCH_RESULTS_DTO;
    facets: Array<Facets>;
    results: Array<SearchResultDto>;
    term: string;
    totalResults: number;
}

export const SECRET_NOT_FOUND = 'SecretNotFound';

export interface SecretNotFound {
    _kind: typeof SECRET_NOT_FOUND;
    path: string;
}

export interface SelectOptionAppearance {
    color: number;
    shape: number;
}

export interface SelectionDefinitionPayload {
    defaultValue?: string;
    options: Array<SelectionOption>;
}

export interface SelectionOption {
    appearance?: SelectOptionAppearance;
    name: string;
}

export interface SelectionPayload {
    value: string;
}

export interface SelfReference {
    columnName: string;
    expression: string;
    message: string;
}

export interface SequentialJobRunCheck {}

export type SequentialJobRunCheckNotMet = object;

export const SET_EXPLORATION_EMAIL_SCHEDULE = 'SetExplorationEmailSchedule';

export interface SetExplorationEmailSchedule {
    _kind: typeof SET_EXPLORATION_EMAIL_SCHEDULE;
    explorationId: string;
    message: string;
    recipients: Array<string>;
    schedule: Scheduling;
    subject: string;
}

export type SetExplorationEmailScheduleSuccess = ShareCreated;

export type SetExplorationEmailScheduleFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | DuplicateColumnNames
    | OutputColumnsWithInvisibleControlCharacters
    | OutputColumnsWithLineSeparator
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | CompilationFailedWithTransformationErrors
    | ExplorationNotFound
    | UnexpectedFailure;

export type SetExplorationEmailScheduleMessage =
    SetExplorationEmailScheduleSuccess | SetExplorationEmailScheduleFailure;

export const SET_GITHUB_AUTHENTICATION = 'SetGithubAuthentication';

export interface SetGithubAuthentication {
    _kind: typeof SET_GITHUB_AUTHENTICATION;
    auth: GithubAuth;
}

export type SetGithubAuthenticationSuccess = Succeeded;

export type SetGithubAuthenticationFailure = GithubAuthenticationError | UnexpectedFailure;

export type SetGithubAuthenticationMessage =
    SetGithubAuthenticationSuccess | SetGithubAuthenticationFailure;

export const SET_GOOGLE_SHEETS_SHARE = 'SetGoogleSheetsShare';

export interface SetGoogleSheetsShare {
    _kind: typeof SET_GOOGLE_SHEETS_SHARE;
    schedule: Scheduling;
    sheetId: number;
    spreadsheetId: string;
    transformationId: string;
}

export type SetGoogleSheetsShareSuccess = ShareCreated | ShareUpdated | ShareResumed;

export type SetGoogleSheetsShareFailure =
    | EntityNotFoundOrNotAccessible
    | SecretNotFound
    | GoogleAuthenticationFailed
    | GoogleCredentialsNotConfigured
    | GoogleSheetDoesNotExist
    | GoogleSpreadsheetNotAccessible
    | GoogleSpreadsheetNotFound
    | GoogleSpreadsheetNotWritable
    | GoogleSheetAlreadyInUse
    | UnexpectedFailure;

export type SetGoogleSheetsShareMessage = SetGoogleSheetsShareSuccess | SetGoogleSheetsShareFailure;

export const SET_JOB_DEFINITION = 'SetJobDefinition';

export interface SetJobDefinition {
    _kind: typeof SET_JOB_DEFINITION;
    checks: Array<JobCheck>;
    creationDate?: Instant;
    description?: string;
    environmentUid: string;
    name: string;
    operation: Operation;
    projectId?: number;
    schedule?: Scheduling;
    eventDrivenTriggers?: Array<EventDrivenTrigger>;
    uid?: string;
}

export type SetJobDefinitionSuccess = JobDefinitionCreated | JobDefinitionUpdated;

export type SetJobDefinitionFailure = UnexpectedFailure;

export type SetJobDefinitionMessage = SetJobDefinitionSuccess | SetJobDefinitionFailure;

export const SET_JOB_ENVIRONMENT = 'SetJobEnvironment';

export interface SetJobEnvironment {
    _kind: typeof SET_JOB_ENVIRONMENT;
    credentials?: Record<string, string>;
    database?: string;
    description?: string;
    name: string;
    role: string;
    schema?: string;
    slackChannelId?: string;
    snowflakeServiceAccountUid?: string;
    uid?: string;
    warehouse: string;
}

export type SetJobEnvironmentSuccess = JobEnvironmentCreated | JobEnvironmentUpdated;

export type SetJobEnvironmentFailure =
    | EventIdNotFound
    | NameAlreadyExists
    | NameCannotBeBlank
    | SecretNotFound
    | SnowflakeServiceAccountUserNotExists
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | EventSubscriptionNotFound
    | UnexpectedFailure;

export type SetJobEnvironmentMessage = SetJobEnvironmentSuccess | SetJobEnvironmentFailure;

export interface SetModelSource {
    modelId: number;
    newSourceUid: string;
    projUid: string;
}

export type SetModelSourceSuccess = ModelUpdated;

export type SetModelSourceFailure = UnexpectedFailure;

export type SetModelMessage = SetModelSourceSuccess | SetModelSourceFailure;

export const SET_SNOWFLAKE_MULTI_USER_CONFIGURATION = 'SetSnowflakeMultiUserConfiguration';

export interface SetSnowflakeMultiUserConfiguration {
    _kind: typeof SET_SNOWFLAKE_MULTI_USER_CONFIGURATION;
    account: string;
}

export type SetSnowflakeMultiUserConfigurationSuccess = SnowflakeConfigurationSet;

export type SetSnowflakeMultiUserConfigurationFailure =
    OperationNotPermitted | SnowflakeAccountNotExistsOrNotAccessible | UnexpectedFailure;

export type SetSnowflakeMultiUserConfigurationMessage =
    SetSnowflakeMultiUserConfigurationSuccess | SetSnowflakeMultiUserConfigurationFailure;

export const SET_SNOWFLAKE_O_AUTH2_CONNECTION_ADVANCED = 'SetSnowflakeOAuth2ConnectionAdvanced';

export interface SetSnowflakeOAuth2ConnectionAdvanced {
    _kind: typeof SET_SNOWFLAKE_O_AUTH2_CONNECTION_ADVANCED;
    account: string;
    authorizationPath: string;
    clientId: string;
    clientSecret: Record<string, string>;
    extra: Record<string, string>;
    site: string;
    tokenPath: string;
}

export type SetSnowflakeOAuth2ConnectionAdvancedSuccess = SnowflakeConfigurationSet;

export type SetSnowflakeOAuth2ConnectionAdvancedFailure = OperationNotPermitted | UnexpectedFailure;

export type SetSnowflakeOAuth2ConnectionAdvancedMessage =
    SetSnowflakeOAuth2ConnectionAdvancedSuccess | SetSnowflakeOAuth2ConnectionAdvancedFailure;

export const SET_SNOWFLAKE_O_AUTH2_CONNECTION_DISCOVERY = 'SetSnowflakeOAuth2ConnectionDiscovery';

export interface SetSnowflakeOAuth2ConnectionDiscovery {
    _kind: typeof SET_SNOWFLAKE_O_AUTH2_CONNECTION_DISCOVERY;
    account: string;
    clientId: string;
    clientSecret: Record<string, string>;
    site: string;
}

export type SetSnowflakeOAuth2ConnectionDiscoverySuccess = SnowflakeConfigurationSet;

export type SetSnowflakeOAuth2ConnectionDiscoveryFailure =
    OperationNotPermitted | UnexpectedFailure;

export type SetSnowflakeOAuth2ConnectionDiscoveryMessage =
    SetSnowflakeOAuth2ConnectionDiscoverySuccess | SetSnowflakeOAuth2ConnectionDiscoveryFailure;

export const SET_SNOWFLAKE_OUTPUT = 'SetSnowflakeOutput';

export interface SetSnowflakeOutput {
    _kind: typeof SET_SNOWFLAKE_OUTPUT;
    database: string;
    deployedNodeId: string;
    role?: string;
    schema: string;
    table: string;
    transformationId: string;
    trigger: SnowflakeOutputTriggerRequest;
    warehouse?: string;
}

export type SetSnowflakeOutputSuccess = Succeeded;

export type SetSnowflakeOutputFailure =
    | EventIdNotFound
    | NameAlreadyExists
    | NameCannotBeBlank
    | SnowflakeOutputAlreadyExists
    | UnexpectedFailure;

export type SetSnowflakeOutputMessage = SetSnowflakeOutputSuccess | SetSnowflakeOutputFailure;

export const SET_SNOWFLAKE_ROLE = 'SetSnowflakeRole';

export interface SetSnowflakeRole {
    _kind: typeof SET_SNOWFLAKE_ROLE;
    role: string;
}

export type SetSnowflakeRoleSuccess = Succeeded;

export type SetSnowflakeRoleFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type SetSnowflakeRoleMessage = SetSnowflakeRoleSuccess | SetSnowflakeRoleFailure;

export const SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION =
    'SetSnowflakeServiceAccountConfiguration';

export interface SetSnowflakeServiceAccountConfiguration {
    _kind: typeof SET_SNOWFLAKE_SERVICE_ACCOUNT_CONFIGURATION;
    account: string;
    credentials: Record<string, string>;
    warehouse?: DatameerWarehouse;
}

export type SetSnowflakeServiceAccountConfigurationSuccess = SnowflakeConfigurationSet;

export type SetSnowflakeServiceAccountConfigurationFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | OperationNotPermitted
    | UnexpectedFailure;

export type SetSnowflakeServiceAccountConfigurationMessage =
    SetSnowflakeServiceAccountConfigurationSuccess | SetSnowflakeServiceAccountConfigurationFailure;

export const SET_SNOWFLAKE_SERVICE_ACCOUNT_USER = 'SetSnowflakeServiceAccountUser';

export interface SetSnowflakeServiceAccountUser {
    _kind: typeof SET_SNOWFLAKE_SERVICE_ACCOUNT_USER;
    credentials: Record<string, string>;
    description?: string;
    name: string;
    uid?: string;
}

export type SetSnowflakeServiceAccountUserSuccess =
    SnowflakeServiceAccountCreated | SnowflakeServiceAccountUpdated;

export type SetSnowflakeServiceAccountUserFailure =
    | Canceled
    | Timeout
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type SetSnowflakeServiceAccountUserMessage =
    SetSnowflakeServiceAccountUserSuccess | SetSnowflakeServiceAccountUserFailure;

export const SET_SNOWFLAKE_USER_CREDENTIALS = 'SetSnowflakeUserCredentials';

export interface SetSnowflakeUserCredentials {
    _kind: typeof SET_SNOWFLAKE_USER_CREDENTIALS;
    credentials: Record<string, string>;
}

export type SetSnowflakeUserCredentialsSuccess = Succeeded;

export type SetSnowflakeUserCredentialsFailure =
    | SnowflakeNotConfigured
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | OperationNotPermitted
    | UnexpectedFailure;

export type SetSnowflakeUserCredentialsMessage =
    SetSnowflakeUserCredentialsSuccess | SetSnowflakeUserCredentialsFailure;

export const SET_SNOWFLAKE_WAREHOUSE = 'SetSnowflakeWarehouse';

export interface SetSnowflakeWarehouse {
    _kind: typeof SET_SNOWFLAKE_WAREHOUSE;
    warehouse: string;
}

export type SetSnowflakeWarehouseSuccess = Succeeded;

export type SetSnowflakeWarehouseFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type SetSnowflakeWarehouseMessage =
    SetSnowflakeWarehouseSuccess | SetSnowflakeWarehouseFailure;

export const SET_TRANSFORMATION_EMAIL_SCHEDULE = 'SetTransformationEmailSchedule';

export interface SetTransformationEmailSchedule {
    _kind: typeof SET_TRANSFORMATION_EMAIL_SCHEDULE;
    message: string;
    recipients: Array<string>;
    schedule: Scheduling;
    subject: string;
    transformationId: string;
}

export type SetTransformationEmailScheduleSuccess = ShareCreated | ShareUpdated | ShareResumed;

export type SetTransformationEmailScheduleFailure =
    EntityNotFoundOrNotAccessible | UnexpectedFailure;

export type SetTransformationEmailScheduleMessage =
    SetTransformationEmailScheduleSuccess | SetTransformationEmailScheduleFailure;

export const SET_VIEW_OUTPUT_COLUMNS = 'SetViewOutputColumns';

export interface SetViewOutputColumns {
    _kind: typeof SET_VIEW_OUTPUT_COLUMNS;
    mappings: Array<MappedOutputColumn>;
    uuid: string;
}

export type SetViewOutputColumnsSuccess = ViewOutputColumnsSet;

export type SetViewOutputColumnsFailure =
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | Canceled
    | Timeout
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | DuplicateColumnNames
    | OutputColumnsWithInvisibleControlCharacters
    | OutputColumnsWithLineSeparator
    | CircularReference
    | UnexpectedFailure;

export type SetViewOutputColumnsMessage = SetViewOutputColumnsSuccess | SetViewOutputColumnsFailure;

export const SHARE_CREATED = 'ShareCreated';

export interface ShareCreated {
    _kind: typeof SHARE_CREATED;
    id: number;
    name: string;
    slug: string;
}

export type ShareId = number;

export type ShareName = string;

export const SHARE_NOT_FOUND = 'ShareNotFound';

export interface ShareNotFound {
    _kind: typeof SHARE_NOT_FOUND;
}

export const SHARE_RESUMED = 'ShareResumed';

export interface ShareResumed {
    _kind: typeof SHARE_RESUMED;
    id: number;
}

export type ShareSlug = string;

export type ShareTarget = EmailTarget | GoogleSheetsTarget;

export enum ShareType {
    EMAIL = 'EMAIL',
    GOOGLE_SHEETS = 'GOOGLE_SHEETS',
    SNOWFLAKE = 'SNOWFLAKE'
}

export const SHARE_UPDATED = 'ShareUpdated';

export interface ShareUpdated {
    _kind: typeof SHARE_UPDATED;
    id: number;
}

export interface SharedOutput {
    id: number;
    lastRunAt?: Instant;
    lastRunError?: object;
    lastRunStatus?: LastExecutionStatus;
    name: string;
    slug: string;
    target: ShareTarget;
    transformationId: string;
    trigger: SnowflakeOutputTriggerResponse;
    type: ShareType;
}

export const SHARED_OUTPUTS = 'SharedOutputs';

export interface SharedOutputs {
    _kind: typeof SHARED_OUTPUTS;
    entries: Array<SharedOutput>;
}

export interface ShortTextDefinitionPayload {
    defaultValue?: string;
}

export interface ShortTextPayload {
    value: string;
}

export interface SkewAggregation {
    sourceColumnName?: string;
}

export const SLACK_API_CALL_FAILED = 'SlackApiCallFailed';

export interface SlackApiCallFailed {
    _kind: typeof SLACK_API_CALL_FAILED;
    reason: string;
}

export const SLACK_AUTHORIZATION_URL = 'SlackAuthorizationUrl';

export interface SlackAuthorizationUrl {
    _kind: typeof SLACK_AUTHORIZATION_URL;
    url: string;
}

export interface SlackChannel {
    id?: string;
    name?: string;
}

export const SLACK_CHANNEL_CREATED = 'SlackChannelCreated';

export interface SlackChannelCreated {
    _kind: typeof SLACK_CHANNEL_CREATED;
    id: string;
}

export const SLACK_CHANNEL_NOT_FOUND = 'SlackChannelNotFound';

export interface SlackChannelNotFound {
    _kind: typeof SLACK_CHANNEL_NOT_FOUND;
    channel: string;
}

export const SLACK_CHANNELS = 'SlackChannels';

export interface SlackChannels {
    _kind: typeof SLACK_CHANNELS;
    channels: Record<string, string>;
}

export const SLACK_CONFIGURATION = 'SlackConfiguration';

export interface SlackConfiguration {
    _kind: typeof SLACK_CONFIGURATION;
    appId: string;
    authorizationDate: Instant;
    authorizedBy: string;
    botName: string;
    workspace: string;
}

export const SLACK_NOT_CONFIGURED = 'SlackNotConfigured';

export interface SlackNotConfigured {
    _kind: typeof SLACK_NOT_CONFIGURED;
}

export const SNOWFLAKE_ACCOUNT_NOT_EXISTS_OR_NOT_ACCESSIBLE =
    'SnowflakeAccountNotExistsOrNotAccessible';

export interface SnowflakeAccountNotExistsOrNotAccessible {
    _kind: typeof SNOWFLAKE_ACCOUNT_NOT_EXISTS_OR_NOT_ACCESSIBLE;
    account: string;
}

export const SNOWFLAKE_AUTHORIZATION_URL = 'SnowflakeAuthorizationUrl';

export interface SnowflakeAuthorizationUrl {
    _kind: typeof SNOWFLAKE_AUTHORIZATION_URL;
    authorizationUrl: string;
}

export enum SnowflakeBinaryFormat {
    HEX = 'HEX',
    BASE64 = 'BASE64',
    UTF8 = 'UTF8'
}

export interface SnowflakeColumnFqn extends SnowflakeColumnQualifiedName {
    column: string;
    database: string;
    schema: string;
    table: string;
}

export type SnowflakeColumnQualifiedName = {
    database: string;
    schema: string;
    table: string;
    column: string;
};

export const SNOWFLAKE_CONFIGURATION_SET = 'SnowflakeConfigurationSet';

export interface SnowflakeConfigurationSet {
    _kind: typeof SNOWFLAKE_CONFIGURATION_SET;
    previousConfigurationToSandboxAccount: boolean;
}

export enum SnowflakeConfigurationType {
    EXTERNAL_OAUTH = 'EXTERNAL_OAUTH',
    MULTI_USER_USERNAME_PASSWORD = 'MULTI_USER_USERNAME_PASSWORD',
    SERVICE_ACCOUNT = 'SERVICE_ACCOUNT'
}

export const SNOWFLAKE_CONNECTION = 'SnowflakeConnection';

export interface SnowflakeConnection {
    _kind: typeof SNOWFLAKE_CONNECTION;
    account: string;
    isSandboxAccount: boolean;
    username: string;
}

export const SNOWFLAKE_CONNECTION_DETAILS = 'SnowflakeConnectionDetails';

export interface SnowflakeConnectionDetails {
    _kind: typeof SNOWFLAKE_CONNECTION_DETAILS;
    account: string;
    role: string;
    type: SnowflakeConfigurationType;
    user: string;
    warehouse?: string;
}

export enum SnowflakeConnectionState {
    CONNECTED = 'CONNECTED',
    DISCONNECTED = 'DISCONNECTED'
}

export interface SnowflakeConnectionStatus {
    account?: string;
    message?: string;
    name: string;
    reason?: GetSnowflakeConnectionDetailsFailure;
    role?: string;
    state: SnowflakeConnectionState;
    tokenExpiringSoon: boolean;
    type?: SnowflakeConfigurationType;
    user?: string;
    warehouse?: string;
    warehouseStatus?: WarehouseStatus;
}

export type SnowflakeCopyOptions = Record<string, string>;

export type SnowflakeCopyOptions_ = object;

export interface SnowflakeDataDrivenTarget {
    database: string;
    role?: string;
    schema: string;
    table: string;
    warehouse?: string;
}

export interface SnowflakeFileFormat {
    fqn: SnowflakeFileFormatFqn;
    type: string;
}

export enum SnowflakeFileFormatCompression {
    AUTO = 'AUTO',
    GZIP = 'GZIP',
    BZ2 = 'BZ2',
    BROTLI = 'BROTLI',
    ZSTD = 'ZSTD',
    DEFLATE = 'DEFLATE',
    RAW_DEFLATE = 'RAW_DEFLATE',
    NONE = 'NONE'
}

export interface SnowflakeFileFormatFqn {
    database: string;
    fileFormat: string;
    schema: string;
}

export const SNOWFLAKE_FILE_FORMATS = 'SnowflakeFileFormats';

export interface SnowflakeFileFormats {
    _kind: typeof SNOWFLAKE_FILE_FORMATS;
    fileFormats: Array<SnowflakeFileFormat>;
}

export const SNOWFLAKE_INTERNAL_SERVER_ERROR = 'SnowflakeInternalServerError';

export interface SnowflakeInternalServerError {
    _kind: typeof SNOWFLAKE_INTERNAL_SERVER_ERROR;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const SNOWFLAKE_MULTI_USER_CONFIGURATION = 'SnowflakeMultiUserConfiguration';

export interface SnowflakeMultiUserConfiguration {
    _kind: typeof SNOWFLAKE_MULTI_USER_CONFIGURATION;
    account: string;
}

export const SNOWFLAKE_NOT_CONFIGURED = 'SnowflakeNotConfigured';

export interface SnowflakeNotConfigured {
    _kind: typeof SNOWFLAKE_NOT_CONFIGURED;
}

export const SNOWFLAKE_O_AUTH2_CONNECTION = 'SnowflakeOAuth2Connection';

export interface SnowflakeOAuth2Connection {
    _kind: typeof SNOWFLAKE_O_AUTH2_CONNECTION;
    account: string;
    authorizationPath?: string;
    clientId: string;
    extra: Record<string, string>;
    oidcDiscovery: boolean;
    site: string;
    tokenPath?: string;
}

export enum SnowflakeOAuthAuthenticationErrorCode {
    CLIENT_SECRET_ERROR = 'CLIENT_SECRET_ERROR',
    HTTP_METHOD_ERROR = 'HTTP_METHOD_ERROR',
    INVALID_ISSUER = 'INVALID_ISSUER',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export const SNOWFLAKE_OBJECT_NOT_FOUND_OR_NOT_ACCESSIBLE =
    'SnowflakeObjectNotFoundOrNotAccessible';

export interface SnowflakeObjectNotFoundOrNotAccessible {
    _kind: typeof SNOWFLAKE_OBJECT_NOT_FOUND_OR_NOT_ACCESSIBLE;
    database: string;
    schema: string;
    table: string;
}

export const SNOWFLAKE_OUTPUT_ALREADY_EXISTS = 'SnowflakeOutputAlreadyExists';

export interface SnowflakeOutputAlreadyExists {
    _kind: typeof SNOWFLAKE_OUTPUT_ALREADY_EXISTS;
    transformationId: string;
}

export type SnowflakeOutputTriggerRequest =
    | ApiTriggerRequest
    | DataDrivenTriggerRequest
    | ManualTriggerRequest
    | ScheduleDrivenTriggerRequest;

export type SnowflakeOutputTriggerResponse =
    | ApiTriggerResponse
    | DataDrivenTriggerResponse
    | ManualTriggerResponse
    | ScheduleDrivenTriggerResponse;

export const SNOWFLAKE_QUERIES = 'SnowflakeQueries';

export interface SnowflakeQueries {
    _kind: typeof SNOWFLAKE_QUERIES;
    queries: Array<SnowflakeQuery>;
}

export interface SnowflakeQuery {
    _context: object;
    completedAt?: Instant;
    isAnError: boolean;
    isComplete: boolean;
    isStale: boolean;
    isStillRunning: boolean;
    queryId: string;
    sessionId: string;
    state?: string;
    stats: object;
    status: string;
    submittedAt: Instant;
    totalDuration?: Duration;
    warehouseName?: string;
    warehouseSize?: string;
}

export const SNOWFLAKE_QUERY_CANCELED = 'SnowflakeQueryCanceled';

export interface SnowflakeQueryCanceled {
    _kind: typeof SNOWFLAKE_QUERY_CANCELED;
    message: string;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const SNOWFLAKE_QUERY_STATUS = 'SnowflakeQueryStatus';

export interface SnowflakeQueryStatus {
    _kind: typeof SNOWFLAKE_QUERY_STATUS;
    isAnError: boolean;
    isComplete: boolean;
    isQueued: boolean;
    isStale: boolean;
    isStillRunning: boolean;
    queryStatus: string;
}

export const SNOWFLAKE_QUERY_SUBMITTED = 'SnowflakeQuerySubmitted';

export interface SnowflakeQuerySubmitted {
    _kind: typeof SNOWFLAKE_QUERY_SUBMITTED;
    queryId: string;
    sessionId: string;
}

export const SNOWFLAKE_RESULT = 'SnowflakeResult';

export interface SnowflakeResult {
    _kind: typeof SNOWFLAKE_RESULT;
    columns: Array<SnowflakeResultColumn>;
    records: Array<Array<any>>;
}

export interface SnowflakeResultColumn {
    name: string;
    type: string;
    typeFamily: TypeFamily;
}

export interface SnowflakeSchemaFqn {
    database: string;
    schema: string;
}

export const SNOWFLAKE_SERVICE_ACCOUNT_CREATED = 'SnowflakeServiceAccountCreated';

export interface SnowflakeServiceAccountCreated {
    _kind: typeof SNOWFLAKE_SERVICE_ACCOUNT_CREATED;
    uid: string;
}

export const SNOWFLAKE_SERVICE_ACCOUNT_UPDATED = 'SnowflakeServiceAccountUpdated';

export interface SnowflakeServiceAccountUpdated {
    _kind: typeof SNOWFLAKE_SERVICE_ACCOUNT_UPDATED;
    uid: string;
    username: string;
}

export const SNOWFLAKE_SERVICE_ACCOUNT_USER_DELETED = 'SnowflakeServiceAccountUserDeleted';

export interface SnowflakeServiceAccountUserDeleted {
    _kind: typeof SNOWFLAKE_SERVICE_ACCOUNT_USER_DELETED;
}

export interface SnowflakeServiceAccountUserEntry {
    createdAt: Instant;
    createdBy: string;
    description: string;
    lastUsedAt?: Instant;
    modifiedAt: Instant;
    name: string;
    uid: string;
    username: string;
}

export const SNOWFLAKE_SERVICE_ACCOUNT_USER_NOT_EXISTS = 'SnowflakeServiceAccountUserNotExists';

export interface SnowflakeServiceAccountUserNotExists {
    _kind: typeof SNOWFLAKE_SERVICE_ACCOUNT_USER_NOT_EXISTS;
}

export const SNOWFLAKE_SERVICE_ACCOUNT_USERS = 'SnowflakeServiceAccountUsers';

export interface SnowflakeServiceAccountUsers {
    _kind: typeof SNOWFLAKE_SERVICE_ACCOUNT_USERS;
    entries: Array<SnowflakeServiceAccountUserEntry>;
}

export const SNOWFLAKE_SQL = 'SnowflakeSql';

export interface SnowflakeSql {
    _kind: typeof SNOWFLAKE_SQL;
    inputTables: Array<SnowflakeTableFqn>;
    sampledCachingTables: Record<string, SnowflakeTableFqn>;
    sampledInputTables: Array<SnowflakeTableFqn>;
    sql: string;
}

export interface SnowflakeStageFileColumn {
    isNullable: boolean;
    name: string;
    samples: Array<string>;
    typeFamily: TypeFamily;
}

export type SnowflakeStageFileFormat =
    | SnowflakeStageFileFormatAvro
    | SnowflakeStageFileFormatCsv
    | SnowflakeStageFileFormatJson
    | SnowflakeStageFileFormatOrc
    | SnowflakeStageFileFormatParquet
    | SnowflakeStageFileFormatUnknown
    | SnowflakeStageFileFormatXml;

export interface SnowflakeStageFileFormatAvro {
    formatName?: string;
    type: 'AVRO';
}

export interface SnowflakeStageFileFormatCsv {
    binaryFormat: SnowflakeBinaryFormat;
    compression: SnowflakeFileFormatCompression;
    dateFormat?: string;
    emptyFieldAsNull: boolean;
    encoding: string;
    errorOnColumnCountMissmatch: boolean;
    escapeCharacter?: string;
    escapeUnenclosedField?: string;
    fieldDelimiter?: string;
    fieldUnenclosedBy?: string;
    fileExtension: string;
    formatName?: string;
    nullIf: Array<string>;
    parseHeader: boolean;
    recordDelimiter?: string;
    replaceInvalidCharacters: boolean;
    skipBlankLines: boolean;
    skipByteOrderMark: boolean;
    skipHeader: number;
    timeFormat?: string;
    timestampFormat?: string;
    trimSpace: boolean;
    type: 'CSV';
}

export type SnowflakeStageFileFormatCsv_ = object;

export interface SnowflakeStageFileFormatJson {
    formatName?: string;
    type: 'JSON';
}

export interface SnowflakeStageFileFormatOrc {
    formatName?: string;
    type: 'ORC';
}

export interface SnowflakeStageFileFormatParquet {
    formatName?: string;
    type: 'PARQUET';
}

export interface SnowflakeStageFileFormatUnknown {
    formatName?: string;
    type: string;
}

export interface SnowflakeStageFileFormatXml {
    formatName?: string;
    type: 'XML';
}

export const SNOWFLAKE_STAGE_FILE_INFO = 'SnowflakeStageFileInfo';

export interface SnowflakeStageFileInfo {
    _kind: typeof SNOWFLAKE_STAGE_FILE_INFO;
    columns: Array<SnowflakeStageFileColumn>;
    fileFormatProperties: FileFormatProperties;
    parsedFile: string;
    rawData?: string;
}

export interface SnowflakeStageFqn {
    database: string;
    schema: string;
    stage: string;
}

export const SNOWFLAKE_STAGES = 'SnowflakeStages';

export interface SnowflakeStages {
    _kind: typeof SNOWFLAKE_STAGES;
    value: Array<StageDescripton>;
}

export type SnowflakeStoredProcedure = SupportedStoredProcedure | UnsupportedStoredProcedure;

export enum SnowflakeStoredProcedureArgType {
    BOOLEAN = 'BOOLEAN',
    DATE = 'DATE',
    DATE_TIME = 'DATE_TIME',
    FLOAT = 'FLOAT',
    LONG = 'LONG',
    SQL_FRAGMENT = 'SQL_FRAGMENT',
    TIME = 'TIME',
    VARCHAR = 'VARCHAR'
}

export interface SnowflakeStoredProcedureArgument {
    name: string;
    type: SnowflakeStoredProcedureArgType;
    value: string;
}

export interface SnowflakeStoredProcedureFqn {
    database: string;
    schema: string;
    storedProcedure: string;
}

export interface SnowflakeStoredProcedureParameter {
    name: string;
    type: SnowflakeStoredProcedureSqlType;
}

export enum SnowflakeStoredProcedureSqlType {
    ARRAY = 'ARRAY',
    BOOLEAN = 'BOOLEAN',
    DATE = 'DATE',
    FLOAT = 'FLOAT',
    NUMBER = 'NUMBER',
    OBJECT = 'OBJECT',
    TIME = 'TIME',
    TIMESTAMP_LTZ = 'TIMESTAMP_LTZ',
    TIMESTAMP_NTZ = 'TIMESTAMP_NTZ',
    TIMESTAMP_TZ = 'TIMESTAMP_TZ',
    VARCHAR = 'VARCHAR',
    VARIANT = 'VARIANT',
    UNSUPPORTED = 'UNSUPPORTED'
}

export const SNOWFLAKE_STORED_PROCEDURES = 'SnowflakeStoredProcedures';

export interface SnowflakeStoredProcedures {
    _kind: typeof SNOWFLAKE_STORED_PROCEDURES;
    value: Array<SnowflakeStoredProcedure>;
}

export const SNOWFLAKE_TABLE_ACCESS_VALIDATED = 'SnowflakeTableAccessValidated';

export interface SnowflakeTableAccessValidated {
    _kind: typeof SNOWFLAKE_TABLE_ACCESS_VALIDATED;
    tables: Array<GetSnowflakeTableAccessSuccess>;
}

export const SNOWFLAKE_TABLE_ALREADY_IMPORTED = 'SnowflakeTableAlreadyImported';

export interface SnowflakeTableAlreadyImported {
    _kind: typeof SNOWFLAKE_TABLE_ALREADY_IMPORTED;
    database: string;
    schema: string;
    table: string;
    viewRef: ViewRef;
}

export const SNOWFLAKE_TABLE_COLUMNS = 'SnowflakeTableColumns';

export interface SnowflakeTableColumns {
    _kind: typeof SNOWFLAKE_TABLE_COLUMNS;
    columns: Array<Column>;
}

export const SNOWFLAKE_TABLE_DELETED = 'SnowflakeTableDeleted';

export interface SnowflakeTableDeleted {
    _kind: typeof SNOWFLAKE_TABLE_DELETED;
}

export interface SnowflakeTableFqn {
    database: string;
    schema: string;
    table: string;
}

export enum SortingOrder {
    ASC = 'ASC',
    DESC = 'DESC'
}

export interface SourceTableMapping {
    sourceTable: SnowflakeTableFqn;
    targetTable: SnowflakeTableFqn;
}

export const SPACE_COPIED = 'SpaceCopied';

export interface SpaceCopied {
    _kind: typeof SPACE_COPIED;
    collaborators: Array<string>;
    createdAt: Instant;
    customAttributes: Array<AssetPropertyDto>;
    description: string;
    entityId: EntityId;
    groups: Record<string, string>;
    name: string;
    owner: string;
    slug: string;
    spaceName: string;
    spaceRef: SpaceRef;
    subscriberConfig: SubscriberConfig;
    uid: string;
}

export const SPACE_CREATED_WITH_VIEW = 'SpaceCreatedWithView';

export interface SpaceCreatedWithView {
    _kind: typeof SPACE_CREATED_WITH_VIEW;
    collaborators: Array<string>;
    createdAt: Instant;
    customAttributes: Array<AssetPropertyDto>;
    description: string;
    entityId: EntityId;
    importedView?: string;
    name: string;
    owner: string;
    slug: string;
    spaceName: string;
    spaceRef: SpaceRef;
    subscriberConfig: SubscriberConfig;
    uid: string;
}

export interface SpaceRef {
    name: string;
    slug: string;
    uid: string;
}

export interface SpaceRefDto {
    entityId: EntityId;
    exists: boolean;
    isDeletable: boolean;
    isOwnerModifiable: boolean;
    isReadable: boolean;
    name: string;
    slug?: string;
    uid: string;
}

export interface SpaceSearchResultDto {
    accessControl: AccessControlDto;
    assetId: EntityId;
    createdAt: Instant;
    description: string;
    entityId: EntityId;
    modifiedAt: Instant;
    name: string;
    numberOfSpaceCollaborators: number;
    numberOfViews: number;
    owner: string;
    prominentCustomAttributes: Array<AssetPropertyDto>;
    referencedIn: Array<EntityId>;
    spaceSlug: string;
    spaceUid: string;
    tags: Array<string>;
}

export type SpaceUid = string;

export type SpaceViewColumn = string;

export type SpaceViewUuid = string;

export interface Split {
    column: string;
    delimiter: string;
    limit: number;
    suggestedColumnName?: string;
}

export interface SqlColumn {
    name: string;
    type: string;
}

export interface StageDescripton {
    cloud?: string;
    comment: string;
    createdOn: Instant;
    owner: string;
    region?: string;
    stage: SnowflakeStageFqn;
    storageIntegration?: string;
    type: string;
    url: string;
}

export const STAGE_DETAILS = 'StageDetails';

export interface StageDetails {
    _kind: typeof STAGE_DETAILS;
    copyOptions: Record<string, string>;
    directoryAutoRefresh: boolean;
    directoryEnable: boolean;
    fileFormat: SnowflakeStageFileFormat;
    storageIntegration: string;
    url: string;
}

export interface StageFileCopyIntoResult {
    errorsFound: number;
    file: string;
    firstError?: string;
    rowsLoaded: number;
    rowsParsed: number;
    status: string;
}

export const STAGE_FILES = 'StageFiles';

export interface StageFiles {
    _kind: typeof STAGE_FILES;
    files: Array<string>;
}

export const START_FETCH_DAP_MIGRATION_FILES = 'StartFetchDapMigrationFiles';

export interface StartFetchDapMigrationFiles {
    _kind: typeof START_FETCH_DAP_MIGRATION_FILES;
    uid: string;
}

export type StartFetchDapMigrationFilesSuccess = FetchDapMigrationFilesStarted;

export type StartFetchDapMigrationFilesProgress = DapMigrationFileContents;

export type StartFetchDapMigrationFilesFailure = UnexpectedFailure;

export type StartFetchDapMigrationFilesMessage =
    | StartFetchDapMigrationFilesSuccess
    | StartFetchDapMigrationFilesProgress
    | StartFetchDapMigrationFilesFailure;

export interface StddevAggregation {
    sourceColumnName?: string;
}

export interface StddevPopAggregation {
    sourceColumnName?: string;
}

export interface StoredProcedureEntity {
    fqn: SnowflakeStoredProcedureFqn;
}

export const SUBMIT_SNOWFLAKE_QUERY = 'SubmitSnowflakeQuery';

export interface SubmitSnowflakeQuery {
    _kind: typeof SUBMIT_SNOWFLAKE_QUERY;
    sql: string;
}

export type SubmitSnowflakeQuerySuccess = SnowflakeQuerySubmitted;

export type SubmitSnowflakeQueryFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | UnexpectedFailure;

export type SubmitSnowflakeQueryMessage = SubmitSnowflakeQuerySuccess | SubmitSnowflakeQueryFailure;

export interface SubscriberConfig {
    informActiveUser: boolean;
    informEntitySubscribers: Array<EntityId>;
    informUsers: Array<string>;
    informUsersCapableOf: Array<string>;
}

export interface SubstringExtract {
    column: string;
    length?: number;
    startIndex: number;
    suggestedColumnName?: string;
}

export const SUCCEEDED = 'Succeeded';

export interface Succeeded {
    _kind: typeof SUCCEEDED;
}

export interface SumAggregation {
    sourceColumnName?: string;
}

export interface SupportedStoredProcedure {
    argumentSignature: string;
    comment?: string;
    fqn: SnowflakeStoredProcedureFqn;
    isSupported: boolean;
    owner: string;
    parameters: Array<SnowflakeStoredProcedureParameter>;
    returnType: string;
}

export const SYNC_PROJECT = 'SyncProject';

export interface SyncProject {
    _kind: typeof SYNC_PROJECT;
    spaceId: string;
    outOfSyncTables?: SnowflakeTableFqn[];
}

export type SyncProjectSuccess = ProjectSynced;

export type SyncProjectFailure = UnexpectedFailure;

export type SyncProjectMessage = SyncProjectSuccess | SyncProjectFailure;

export const TABLE_ALREADY_EXISTS = 'TableAlreadyExists';

export interface TableAlreadyExists {
    _kind: typeof TABLE_ALREADY_EXISTS;
    database: string;
    owner: TableOwner;
    schema: string;
    table: string;
    type: TableType;
}

export const TABLE_DETAILS = 'TableDetails';

export interface TableDetails {
    _kind: typeof TABLE_DETAILS;
    createdInProject?: string;
    database: string;
    description: string;
    properties: Array<TableProperty>;
    schema: string;
    table: string;
    tags: Array<string>;
}

export const TABLE_DETAILS_FOR_DATA_BROWSER = 'TableDetailsForDataBrowser';

export interface TableDetailsForDataBrowser {
    _kind: typeof TABLE_DETAILS_FOR_DATA_BROWSER;
    details: Array<DataBrowserTableDetails>;
}

export const TABLE_IMPORTED_TO_NEW_PROJECT = 'TableImportedToNewProject';

export interface TableImportedToNewProject {
    _kind: typeof TABLE_IMPORTED_TO_NEW_PROJECT;
    collaborators: Array<string>;
    createdAt: Instant;
    customAttributes: Array<AssetPropertyDto>;
    database: string;
    description: string;
    entityId: EntityId;
    importedAt: Instant;
    name: string;
    newExplorationIds: Array<string>;
    owner: string;
    schema: string;
    slug: string;
    spaceName: string;
    spaceRef: SpaceRef;
    subscriberConfig: SubscriberConfig;
    table: string;
    uid: string;
    viewRef: ViewRef;
}

export const TABLE_IMPORTED_TO_PROJECT = 'TableImportedToProject';

export interface TableImportedToProject {
    _kind: typeof TABLE_IMPORTED_TO_PROJECT;
    database: string;
    importedAt: Instant;
    newExplorationIds: Array<string>;
    schema: string;
    subscriberConfig: SubscriberConfig;
    table: string;
    viewRef: ViewRef;
}

export const TABLE_MATERIALIZATION_COMPLETED = 'TableMaterializationCompleted';

export interface TableMaterializationCompleted {
    _kind: typeof TABLE_MATERIALIZATION_COMPLETED;
    byteCount?: number;
    columns: Array<Column>;
    dataDrivenTargets: Array<SnowflakeDataDrivenTarget>;
    database: string;
    outputNodeUrl: string;
    owner: string;
    projectUid: string;
    rowCount?: number;
    schema: string;
    table: string;
}

export const TABLE_MATERIALIZATION_INITIATED = 'TableMaterializationInitiated';

export interface TableMaterializationInitiated {
    _kind: typeof TABLE_MATERIALIZATION_INITIATED;
    database: string;
    queryId: string;
    schema: string;
    sessionId: string;
    table: string;
}

export enum TableModificationType {
    ALTER = 'ALTER',
    COPY_INTO = 'COPY_INTO',
    CREATE = 'CREATE',
    DELETE = 'DELETE',
    DROP = 'DROP',
    GRANT = 'GRANT',
    INSERT = 'INSERT',
    MERGE = 'MERGE',
    REVOKE = 'REVOKE',
    SELECT = 'SELECT',
    TRUNCATE = 'TRUNCATE',
    UNDROP = 'UNDROP',
    UPDATE = 'UPDATE'
}

export enum TableOwner {
    APP = 'APP',
    USER = 'USER'
}

export interface TableProperty {
    name: string;
    payload: AssetPropertyDefinitionPayload;
    prominent: boolean;
    uid: string;
    value: string;
}

export interface TableQualification {
    database: string;
    schema: string;
}

export enum TableType {
    EVENT_TABLE = 'EVENT_TABLE',
    EXTERNAL_TABLE = 'EXTERNAL_TABLE',
    TABLE = 'TABLE',
    TEMPORARY_TABLE = 'TEMPORARY_TABLE',
    VIEW = 'VIEW'
}

export const TARGET_TABLE_COLUMNS = 'TargetTableColumns';

export interface TargetTableColumns {
    _kind: typeof TARGET_TABLE_COLUMNS;
    mappings: Array<LoadJobTargetColumnMapping>;
}

export const TARGET_TABLE_NOT_EXISTS = 'TargetTableNotExists';

export interface TargetTableNotExists {
    _kind: typeof TARGET_TABLE_NOT_EXISTS;
    targetTableFqn: SnowflakeTableFqn;
}

export enum TargetType {
    TABLE = 'TABLE',
    VIEW = 'VIEW'
}

export const TARGET_VIEW_OR_TABLE_NOT_ALLOWED_TO_OVERWRITE =
    'TargetViewOrTableNotAllowedToOverwrite';

export interface TargetViewOrTableNotAllowedToOverwrite {
    _kind: typeof TARGET_VIEW_OR_TABLE_NOT_ALLOWED_TO_OVERWRITE;
}

export const TEMPORARY_TABLE_MATERIALIZATION_COMPLETED = 'TemporaryTableMaterializationCompleted';

export interface TemporaryTableMaterializationCompleted {
    _kind: typeof TEMPORARY_TABLE_MATERIALIZATION_COMPLETED;
    byteCount?: number;
    database: string;
    rowCount?: number;
    schema: string;
    table: string;
}

export const TEMPORARY_TABLE_MATERIALIZATION_INITIATED = 'TemporaryTableMaterializationInitiated';

export interface TemporaryTableMaterializationInitiated {
    _kind: typeof TEMPORARY_TABLE_MATERIALIZATION_INITIATED;
    database: string;
    schema: string;
    sessionId: string;
    table: string;
}

export const TIMEOUT = 'Timeout';

export interface Timeout {
    _kind: typeof TIMEOUT;
    message: string;
    serviceName?: string;
    stackTraceAsString: string;
    timeoutAt?: Duration;
}

export const TOKEN_EXPIRED = 'TokenExpired';

export interface TokenExpired {
    _kind: typeof TOKEN_EXPIRED;
}

export const TOO_MANY_ENABLED_USERS = 'TooManyEnabledUsers';

export interface TooManyEnabledUsers {
    _kind: typeof TOO_MANY_ENABLED_USERS;
    maximum: number;
}

export type Transformation =
    Aggregation | Blend | Filter | Formula | Pivot | Project | Replace | Split | SubstringExtract;

export type TransformationColumn = string;

export const TRANSFORMATION_EMAIL_REQUESTED = 'TransformationEmailRequested';

export interface TransformationEmailRequested {
    _kind: typeof TRANSFORMATION_EMAIL_REQUESTED;
    message: string;
    recipients: Array<string>;
    requestedBy: string;
    shareId?: number;
    subject: string;
    transformationId: ViewUuid;
}

export const TRANSFORMATION_EMAIL_SCHEDULE = 'TransformationEmailSchedule';

export interface TransformationEmailSchedule {
    _kind: typeof TRANSFORMATION_EMAIL_SCHEDULE;
    id: number;
    message: string;
    recipients: Array<string>;
    schedule: Scheduling;
    subject: string;
}

export type TransformationError =
    | AggregationError
    | AmbiguousOutputColumns
    | DuplicateColumnName
    | FormulaCircularReference
    | IllegalAlias
    | InvalidExpression
    | MissingSourceColumn
    | PivotError
    | ReferencedColumInError
    | SelfReference
    | UnknownColumnNames
    | UnknownColumnNamesWithSource;

export const TRANSFORMATION_NODE_CREATED = 'TransformationNodeCreated';

export interface TransformationNodeCreated {
    _kind: typeof TRANSFORMATION_NODE_CREATED;
    performedBy: string;
    viewRef: ViewRef;
}

export const TRANSFORMATION_NODE_UPDATED = 'TransformationNodeUpdated';

export interface TransformationNodeUpdated {
    _kind: typeof TRANSFORMATION_NODE_UPDATED;
    performedBy: string;
    viewRef: ViewRef;
}

export const TRANSFORMATION_NOT_OPERATIONAL = 'TransformationNotOperational';

export interface TransformationNotOperational {
    _kind: typeof TRANSFORMATION_NOT_OPERATIONAL;
    error: DatasetError;
}

export type TransformationPayload = Combine | GenericTransform | Join | Projection | QueryBuilder;

export type TransformationUuid = string;

export interface TrialPeriod {
    firstProductUsage: Instant;
    name: string;
    period: Period;
}

export interface TriggerCandidate {
    database: string;
    deployedNode?: ViewRef;
    inputNode: string;
    lastRunAt?: Instant;
    role?: string;
    schema: string;
    table: string;
    tableType?: TableType;
    trigger?: SnowflakeOutputTriggerResponse;
    triggerSourceDeployedNode?: ViewRef;
    warehouse?: string;
}

export const TRIGGER_JOB_RUN = 'TriggerJobRun';

export interface TriggerJobRun {
    _kind: typeof TRIGGER_JOB_RUN;
    definitionUid: string;
    skipChecks: boolean;
}

export type TriggerJobRunSuccess = JobRunTriggered;

export type TriggerJobRunFailure =
    | JobRunCheckFailed
    | JobRunSkipped
    | Canceled
    | Timeout
    | EventIdNotFound
    | NameAlreadyExists
    | NameCannotBeBlank
    | UnexpectedFailure;

export type TriggerJobRunMessage = TriggerJobRunSuccess | TriggerJobRunFailure;

export const TRIGGER_JOB_RUNS = 'TriggerJobRuns';

export interface TriggerJobRuns {
    _kind: typeof TRIGGER_JOB_RUNS;
    requests: Array<JobRunRequest>;
}

export type TriggerJobRunsSuccess = JobRunsTriggered;

export type TriggerJobRunsFailure = UnexpectedFailure;

export type TriggerJobRunsMessage = TriggerJobRunsSuccess | TriggerJobRunsFailure;

export interface TypeChange {
    typeAfter: TypeFamily;
    typeBefore: TypeFamily;
}

export enum TypeFamily {
    ARRAY = 'ARRAY',
    BOOLEAN = 'BOOLEAN',
    DATE = 'DATE',
    DOUBLE = 'DOUBLE',
    GEOGRAPHY = 'GEOGRAPHY',
    INTEGER = 'INTEGER',
    OBJECT = 'OBJECT',
    STRING = 'STRING',
    TIME = 'TIME',
    TIMESTAMP = 'TIMESTAMP',
    UNSUPPORTED = 'UNSUPPORTED',
    VARIANT = 'VARIANT'
}

export const UNCATEGORIZED_SQL_ERROR = 'UncategorizedSqlError';

export interface UncategorizedSqlError {
    _kind: typeof UNCATEGORIZED_SQL_ERROR;
    errorPosition?: ErrorPosition;
    index?: number;
    message: string;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const UNEXPECTED_FAILURE = 'UnexpectedFailure';

export interface UnexpectedFailure {
    _kind: typeof UNEXPECTED_FAILURE;
    message: string;
    serviceName?: string;
    stackTraceAsString: string;
}

export interface UnexpectedRejection {
    message: string;
    serviceName?: string;
    stackTraceAsString: string;
}

export const UNINSTALL_SLACK = 'UninstallSlack';

export interface UninstallSlack {
    _kind: typeof UNINSTALL_SLACK;
}

export type UninstallSlackSuccess = Succeeded;

export type UninstallSlackFailure =
    SecretNotFound | NotAuthorizedToSlack | SlackApiCallFailed | UnexpectedFailure;

export type UninstallSlackMessage = UninstallSlackSuccess | UninstallSlackFailure;

export interface UnknownColumnNames {
    columnNames: Array<string>;
    message: string;
}

export interface UnknownColumnNamesWithSource {
    columnNames: Array<string>;
    message: string;
    viewUid: string;
}

export const UNKNOWN_FUNCTION = 'UnknownFunction';

export interface UnknownFunction {
    _kind: typeof UNKNOWN_FUNCTION;
    function: string;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export const UNKNOWN_FUNCTIONS = 'UnknownFunctions';

export interface UnknownFunctions {
    _kind: typeof UNKNOWN_FUNCTIONS;
    functionNames: string;
    index?: number;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export interface UnknownRequest {}

export const UNLOAD_JOB_RUN_RESULT = 'UnloadJobRunResult';

export interface UnloadJobRunResult {
    _kind: typeof UNLOAD_JOB_RUN_RESULT;
    executionMetadata: Record<string, unknown>;
    warnings: Array<JobRunWarning>;
}

export const UNLOAD_SNOWFLAKE_TABLE = 'UnloadSnowflakeTable';

export interface UnloadSnowflakeTable {
    _kind: typeof UNLOAD_SNOWFLAKE_TABLE;
    deployedNodeUuid: string;
    deployedTableFqn: SnowflakeTableFqn;
    deployedVersion: number;
    fileFormat?: SnowflakeFileFormatFqn;
    filePrefix?: string;
    partitionBy?: string;
    pathSuffix?: string;
    properties?: string;
    stage: SnowflakeStageFqn;
}

export type UnloadSnowflakeTableSuccess = UnloadJobRunResult;

export type UnloadSnowflakeTableFailure =
    NoDeploymentHistory | Canceled | Timeout | UnexpectedFailure;

export type UnloadSnowflakeTableMessage = UnloadSnowflakeTableSuccess | UnloadSnowflakeTableFailure;

export interface UnsupportedStoredProcedure {
    argumentSignature: string;
    comment?: string;
    fqn: SnowflakeStoredProcedureFqn;
    isSupported: boolean;
    owner: string;
    returnType: string;
    unsupportedToken: string;
}

export const UPDATE_CHAT_GPT_CONFIGURATION = 'UpdateChatGptConfiguration';

export interface UpdateChatGptConfiguration {
    _kind: typeof UPDATE_CHAT_GPT_CONFIGURATION;
    credentials?: Record<string, string>;
    model: string;
}

export type UpdateChatGptConfigurationSuccess = Succeeded;

export type UpdateChatGptConfigurationFailure =
    SecretNotFound | ChatGptError | Timeout | UnexpectedFailure;

export type UpdateChatGptConfigurationMessage =
    UpdateChatGptConfigurationSuccess | UpdateChatGptConfigurationFailure;

export const UPDATE_DAP_MIGRATION = 'UpdateDapMigration';

export interface UpdateDapMigration {
    _kind: typeof UPDATE_DAP_MIGRATION;
    description?: string;
    feMetadata?: object;
    name?: string;
    uid: string;
}

export type UpdateDapMigrationSuccess = DapMigrationUpdated;

export type UpdateDapMigrationFailure = UnexpectedFailure;

export type UpdateDapMigrationMessage = UpdateDapMigrationSuccess | UpdateDapMigrationFailure;

export const UPDATE_EVENT_SUBSCRIPTION_DELIVERY_TARGET = 'UpdateEventSubscriptionDeliveryTarget';

export interface UpdateEventSubscriptionDeliveryTarget {
    _kind: typeof UPDATE_EVENT_SUBSCRIPTION_DELIVERY_TARGET;
    name: string;
    newDeliveryTarget: EventDeliveryTarget;
}

export type UpdateEventSubscriptionDeliveryTargetSuccess = Succeeded;

export type UpdateEventSubscriptionDeliveryTargetFailure =
    EventSubscriptionNotFound | UnexpectedFailure;

export type UpdateEventSubscriptionDeliveryTargetMessage =
    UpdateEventSubscriptionDeliveryTargetSuccess | UpdateEventSubscriptionDeliveryTargetFailure;

export const UPDATE_EXPLORATION = 'UpdateExploration';

export interface UpdateExploration {
    _kind: typeof UPDATE_EXPLORATION;
    id: string;
    model: object;
    sql: string;
}

export type UpdateExplorationSuccess = ExplorationUpdated;

export type UpdateExplorationFailure = ExplorationNotFound | UnexpectedFailure;

export type UpdateExplorationMessage = UpdateExplorationSuccess | UpdateExplorationFailure;

export const UPDATE_GROUP_DESCRIPTION = 'UpdateGroupDescription';

export interface UpdateGroupDescription {
    _kind: typeof UPDATE_GROUP_DESCRIPTION;
    description: string;
    groupUid: string;
    spaceUid: string;
}

export type UpdateGroupDescriptionSuccess = GroupDescriptionUpdated;

export type UpdateGroupDescriptionFailure = GroupNotFound | UnexpectedFailure;

export type UpdateGroupDescriptionMessage =
    UpdateGroupDescriptionSuccess | UpdateGroupDescriptionFailure;

export const UPDATE_GROUP_NAME = 'UpdateGroupName';

export interface UpdateGroupName {
    _kind: typeof UPDATE_GROUP_NAME;
    groupUid: string;
    name: string;
    spaceUid: string;
}

export type UpdateGroupNameSuccess = GroupNameUpdated;

export type UpdateGroupNameFailure = GroupNotFound | UnexpectedFailure;

export type UpdateGroupNameMessage = UpdateGroupNameSuccess | UpdateGroupNameFailure;

export const UPDATE_MODEL_JOBS = 'UpdateModelJobs';

export interface UpdateModelJobs {
    _kind: typeof UPDATE_MODEL_JOBS;
    definitionIds: Array<number>;
    newTagId: number;
}

export type UpdateModelJobsSuccess = Succeeded;

export type UpdateModelJobsFailure = UnexpectedFailure;

export type UpdateModelJobsMessage = UpdateModelJobsSuccess | UpdateModelJobsFailure;

export const UPDATE_PROJECT_ENV = 'UpdateProjectEnv';

export interface UpdateProjectEnv {
    _kind: typeof UPDATE_PROJECT_ENV;
    envUid: string;
    spaceUid: string;
}

export type UpdateProjectEnvSuccess = Succeeded;

export type UpdateProjectEnvFailure = UnexpectedFailure;

export type UpdateProjectEnvMessage = UpdateProjectEnvSuccess | UpdateProjectEnvFailure;

export const UPDATE_TRANSFORMATION_NODE = 'UpdateTransformationNode';

export interface UpdateTransformationNode {
    _kind: typeof UPDATE_TRANSFORMATION_NODE;
    payload: TransformationPayload;
    spaceUid: string;
    viewUid: string;
}

export type UpdateTransformationNodeSuccess = TransformationNodeUpdated;

export type UpdateTransformationNodeFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | CompilationFailedWithDatasetError
    | DuplicateColumnNames
    | OutputColumnsWithInvisibleControlCharacters
    | OutputColumnsWithLineSeparator
    | CircularReference
    | AssetModificationNotPermitted
    | EntityNotFoundOrNotAccessible
    | CompilationFailedWithTransformationErrors
    | UnexpectedFailure;

export type UpdateTransformationNodeMessage =
    UpdateTransformationNodeSuccess | UpdateTransformationNodeFailure;

export const USAGES_TABLE_CREATED = 'UsagesTableCreated';

export interface UsagesTableCreated {
    _kind: typeof USAGES_TABLE_CREATED;
    importedView?: string;
    resultTable: SnowflakeTableFqn;
}

export interface UsedTableEntity {
    fqn: SnowflakeTableFqn;
}

export const USER_ALREADY_ENABLED = 'UserAlreadyEnabled';

export interface UserAlreadyEnabled {
    _kind: typeof USER_ALREADY_ENABLED;
    username: string;
}

export const USER_CREATED = 'UserCreated';

export interface UserCreated {
    _kind: typeof USER_CREATED;
    createdAt: Instant;
    email: string;
    enabled: boolean;
    firstName: string;
    group: string;
    lastName: string;
    username: string;
}

export const USER_DELETED = 'UserDeleted';

export interface UserDeleted {
    _kind: typeof USER_DELETED;
    username: string;
}

export const USER_DIFFERS_FROM_TOKEN = 'UserDiffersFromToken';

export interface UserDiffersFromToken {
    _kind: typeof USER_DIFFERS_FROM_TOKEN;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
}

export interface UserDto {
    capabilities: Array<string>;
    email?: string;
    name: string;
    permittedOperations: Array<string>;
    sessionId?: string;
    username: string;
}

export const USER_ENABLED = 'UserEnabled';

export interface UserEnabled {
    _kind: typeof USER_ENABLED;
    username: string;
}

export const USER_NOT_FOUND = 'UserNotFound';

export interface UserNotFound {
    _kind: typeof USER_NOT_FOUND;
    username: string;
}

export const USERNAME_ALREADY_TAKEN = 'UsernameAlreadyTaken';

export interface UsernameAlreadyTaken {
    _kind: typeof USERNAME_ALREADY_TAKEN;
    username: string;
}

export const USERNAME_INVALID = 'UsernameInvalid';

export interface UsernameInvalid {
    _kind: typeof USERNAME_INVALID;
    username: string;
}

export const VALIDATE_MODEL_JOBS = 'ValidateModelJobs';

export interface ValidateModelJobs {
    _kind: typeof VALIDATE_MODEL_JOBS;
    definitionIds: Array<number>;
    targetTagId: number;
}

export type ValidateModelJobsSuccess = ModelJobsValidationResult;

export type ValidateModelJobsFailure = UnexpectedFailure;

export type ValidateModelJobsMessage = ValidateModelJobsSuccess | ValidateModelJobsFailure;

export const VALIDATE_SNOWFLAKE_ACCESS = 'ValidateSnowflakeAccess';

export interface ValidateSnowflakeAccess {
    _kind: typeof VALIDATE_SNOWFLAKE_ACCESS;
    objects: Array<SnowflakeTableFqn>;
}

export type ValidateSnowflakeAccessSuccess = SnowflakeTableAccessValidated;

export type ValidateSnowflakeAccessFailure =
    | AmbiguousColumnReference
    | CannotCastValueOfType
    | ClientConnectionError
    | DatabaseNotExistsOrNotAuthorized
    | DataLoadingFailed
    | InsufficientPrivileges
    | InsufficientTaskPrivileges
    | InvalidAccessToken
    | InvalidAccountName
    | InvalidIdentifier
    | InvalidParameterTypeForFunction
    | MaxFileSizeExceeded
    | MissingColumn
    | NoActiveWarehouseSelected
    | NotExistsOrNotAuthorized
    | NotExistsOrOperationCannotBePerformed
    | ObjectAlreadyExists
    | SchemaNotExistsOrNotAuthorized
    | SnowflakeInternalServerError
    | SnowflakeQueryCanceled
    | Timeout
    | UncategorizedSqlError
    | UnknownFunction
    | UnknownFunctions
    | UserDiffersFromToken
    | ValueNotRecognized
    | WarehouseSuspended
    | NotAuthenticatedToSnowflake
    | SnowflakeNotConfigured
    | NoDeploymentHistory
    | CompilationFailedWithDatasetError
    | EntityNotFoundOrNotAccessible
    | UnexpectedFailure;

export type ValidateSnowflakeAccessMessage =
    ValidateSnowflakeAccessSuccess | ValidateSnowflakeAccessFailure;

export interface ValidatedJobDefEntry {
    id: number;
    name: string;
    status: JobValidationStatus;
}

export const VALUE_NOT_RECOGNIZED = 'ValueNotRecognized';

export interface ValueNotRecognized {
    _kind: typeof VALUE_NOT_RECOGNIZED;
    column?: string;
    index?: number;
    line?: number;
    message: string;
    notRecognizedValue: string;
    originalMessage: string;
    origins: Array<DatasetUid>;
    sql: string;
    sqlState?: string;
    targetType: string;
    vendorCode?: number;
}

export interface VarPopAggregation {
    sourceColumnName?: string;
}

export interface VarSampAggregation {
    sourceColumnName?: string;
}

export const VIEW_ALREADY_EXISTS = 'ViewAlreadyExists';

export interface ViewAlreadyExists {
    _kind: typeof VIEW_ALREADY_EXISTS;
    viewName: string;
}

export const VIEW_DELETED = 'ViewDeleted';

export interface ViewDeleted {
    _kind: typeof VIEW_DELETED;
    deletedAt: Instant;
    deletedFromGroup?: string;
    deletedGroup?: string;
    tableFqn?: SnowflakeTableFqn;
    viewRef: ViewRef;
}

export const VIEW_DEPLOYED = 'ViewDeployed';

export interface ViewDeployed {
    _kind: typeof VIEW_DEPLOYED;
    database: string;
    schema: string;
    table: string;
    version: number;
    viewRef: ViewRef;
}

export const VIEW_DEPLOYMENT_RESTORED = 'ViewDeploymentRestored';

export interface ViewDeploymentRestored {
    _kind: typeof VIEW_DEPLOYMENT_RESTORED;
    database: string;
    schema: string;
    table: string;
    version: number;
}

export const VIEW_OUTPUT_COLUMNS_SET = 'ViewOutputColumnsSet';

export interface ViewOutputColumnsSet {
    _kind: typeof VIEW_OUTPUT_COLUMNS_SET;
}

export const VIEW_PUBLISHING_INITIATED = 'ViewPublishingInitiated';

export interface ViewPublishingInitiated {
    _kind: typeof VIEW_PUBLISHING_INITIATED;
    columns: Columns;
    database: string;
    importToProject: string;
    schema: string;
    table: string;
    version: number;
}

export interface ViewRef {
    entityId: EntityId;
    spaceName: string;
    spaceSlug: string;
    spaceUid: string;
    viewName: string;
    viewSlug: string;
    viewType: ViewType;
    viewUid: string;
}

export interface ViewDTO {
    entityId: EntityId;
    tableFqn: SnowflakeTableFqn;
}

export interface ViewRefDto {
    entityId: EntityId;
    exists: boolean;
    isReadable: boolean;
    name: string;
    slug?: string;
    spaceIsReadable: boolean;
    spaceName: string;
    spaceSlug?: string;
    spaceUid: string;
    uid: string;
    viewType?: ViewType;
}

export enum ViewType {
    DATA_SOURCE_REFERENCE = 'DATA_SOURCE_REFERENCE',
    MODEL = 'MODEL',
    SQL_VIEW = 'SQL_VIEW',
    TRANSFORMATION_NODE = 'TRANSFORMATION_NODE',
    TRANSFORMATION_STACK = 'TRANSFORMATION_STACK'
}

export type ViewUid = string;

export type ViewUuid = SnowflakeTableFqn | SpaceViewUuid | TransformationUuid;

export const VIEW_WITH_SAME_NAME_ALREADY_EXISTS = 'ViewWithSameNameAlreadyExists';

export interface ViewWithSameNameAlreadyExists {
    _kind: typeof VIEW_WITH_SAME_NAME_ALREADY_EXISTS;
}

export const WAREHOUSE_LIST = 'WarehouseList';

export interface WarehouseList {
    _kind: typeof WAREHOUSE_LIST;
    defaultWarehouse?: string;
    warehouses: Record<string, WarehouseProperties>;
}

export interface WarehouseProperties {
    isDefault: boolean;
    isXSmall: boolean;
    queued: number;
    running: number;
    size: string;
    state: string;
}

export interface WarehouseEntry {
    name: string;
    queued: number;
    running: number;
    size: string;
    state: string;
}

export interface WarehouseStatus extends WarehouseEntry {
    fetchedAt: Instant;
}

export const WAREHOUSE_SUSPENDED = 'WarehouseSuspended';

export interface WarehouseSuspended {
    _kind: typeof WAREHOUSE_SUSPENDED;
    originalMessage: string;
    sql: string;
    sqlState?: string;
    vendorCode?: number;
    warehouse: string;
}

export interface Webhook {
    url: string;
}

export interface WorkerQueueFull {
    message: string;
    serviceName?: string;
    stackTraceAsString: string;
}

export type GroupUid = string;

export enum TransformationType {
    AGGREGATION = 'AGGREGATION',
    EXTRACT = 'EXTRACT',
    EXTRACT_JSON = 'EXTRACT_JSON',
    FILTER = 'FILTER',
    FORMULA = 'FORMULA',
    LEGACY_JOIN = 'JOIN',
    LEGACY_PROJECTION = 'PROJECTION',
    MULTI_JOIN = 'MULTI_JOIN',
    PIVOT = 'PIVOT',
    QUERY = 'QUERY',
    REPLACE = 'REPLACE',
    SPLIT = 'SPLIT',
    SQL = 'SQL',
    UNION = 'UNION'
}

export interface CreateTransformation {
    groupUid?: GroupUid;
    insertAfter: ViewUid;
    insertBefore: ViewUid[];
    payload: object;
    spaceUid: SpaceUid;
    suggestedName?: string;
    transformationType: TransformationType;
}
